import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileText, GripVertical, Image, Info, Loader2, Merge, MoreVertical, Pause, Play, RotateCcw, TriangleAlert, Undo2 } from 'lucide-react';

import { MathTextBlock } from '../../components/MathTextBlock';
import {
  factoryService,
  isFactoryBatchOwnerAccepted,
  type FactoryAsset,
  type FactoryBatch,
  type FactoryLayoutUnit,
  type FactorySemanticSplitPage,
  type FactorySemanticSplitsResult,
  type FactoryStructuralUnit,
  type FactoryTtqAdmissionResult,
  type FactoryWarehouseTtqa,
  type FactoryWarehouseTtqaParticle,
  type FactoryIpttqBatchResult,
  type FactoryIpttqRecord,
  type FactoryInducedKpDraft,
  type ProductBankStockStatusResponse,
  type ProductBankStockRunState,
  type SimilarityReAdmitReport,
} from '../../services/factoryService';
import { ItemPreviewImage } from '../components/ItemPreviewImage';
import { AuthImage, type AuthImageLoadedMeta } from '../components/AuthImage';
import { PDFViewer, type PdfContentItem, type PdfSplitLine } from '../components/PDFViewer';
import { stripImageDescriptions } from '../utils/stripImageDescriptions';
import {
  isConfirmedPrintedFigureQa,
  structuralUnitExtractQa,
  structuralUnitExtractRouteLabel,
  structuralUnitExtractTitle,
  structuralUnitExtractTokensLabel,
  structuralUnitLooksExtractFailed,
  qaFigureMarkLabel,
} from '../utils/extractQaPreview';
import {
  structuralUnitEnrichLabel,
  structuralUnitEnrichQaPreview,
  formatUnitFcError,
} from '../utils/enrichPreview';
import {
  buildCoachOverlayPreview,
  buildLearnerOverlayPreview,
  overlayJobStatusLabelZh,
} from '../utils/overlayPreview';
import {
  CoachOverlayEnrichBody,
  LearnerOverlayEnrichBody,
} from '../components/PageOverlayEnrichBodies';
import {
  FigureCropPreviewDialog,
  type FigureCropPreview,
} from '../components/FigureCropPreviewDialog';
import { FigureExtractPhasePanel } from '../components/FigureExtractPhasePanel';
import { structuralUnitPreviewLines } from '../utils/structuralPreview';
import { truncateMathAwarePreview } from '../utils/truncateMathAwarePreview';
import {
  formatInductionRatioLabel,
  inductionTargetByMembers,
  inductionReferenceSampleSize,
  readInductionBankMemberRatio,
} from '../inductionBankPolicy';
import {
  admissionParticleListLabel,
  resolveAdmissionParticleQa,
} from '../utils/resolveAdmissionParticleQa';
import { buildPapDetail } from '../../features/ttqib/papDisplay';
import type { PrivateActivityPackage } from '../../services/ttqService';

type ResultPhase = 'semantic_split' | 'layout' | 'structure' | 'extract' | 'figure_extract' | 'ttq' | 'ipttq';
type SelectionSource = 'list' | 'bbox' | 'pdf' | null;
type PdfLayoutMode = 'fit-width' | 'fit-height';
type SourceViewMode = 'image' | 'pdf';

function formatImageBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10 * 1024 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function lookupExtractQaForParticle(
  particleId: string | null | undefined,
  units: FactoryStructuralUnit[],
) {
  const id = String(particleId || '');
  const colon = id.lastIndexOf(':');
  if (colon <= 0) return null;
  const unitId = id.slice(0, colon);
  const qi = Number(id.slice(colon + 1));
  if (!Number.isFinite(qi)) return null;
  const unit = units.find((u) => u.id === unitId);
  if (!unit) return null;
  return structuralUnitExtractQa(unit)[qi] || null;
}

const ALL_PHASES: Array<{ id: ResultPhase; label: string; wired?: boolean }> = [
  { id: 'layout', label: '版面拆分', wired: true },
  { id: 'semantic_split', label: '语义切分', wired: true },
  { id: 'structure', label: '结构聚合', wired: true },
  { id: 'extract', label: '颗粒提取', wired: true },
  { id: 'figure_extract', label: '插图提取', wired: true },
  { id: 'ttq', label: '品题推荐', wired: true },
  { id: 'ipttq', label: '品题浓缩', wired: true },
];

const PHASE_HINT: Record<ResultPhase, string> = {
  layout: '版面拆分：对照文档块与原文；滚到分册边缘可换册 · 方向键切换工序',
  semantic_split:
    '语义切分：标出跨页接缝；未开启时本步无数据 · 方向键切换工序',
  structure: '结构聚合：把文档块收成可提取单元；开了切分会按接缝合并 · 方向键切换工序',
  extract: '颗粒提取：一题一卡看问答；点开可看能力与考点标注 · 方向键切换工序',
  figure_extract: '插图提取：给需要配图的题再做图；可多家模型对照 · 方向键切换工序',
  ttq: '品题推荐：把相近题收成组；相似漏网会再判一次；成品见顶栏「成品智能体」· 方向键切换工序',
  ipttq: '品题浓缩：为每组提炼练法，并按约十分之一抽原题+能力摘要生成诱导题 · 方向键切换工序',
};

/** Extract list window size (scroll to load more). */
const EXTRACT_PAGE_SIZE = 12;

function phasesForBatch(enableSemanticSplit: boolean | undefined): Array<{
  id: ResultPhase;
  label: string;
  wired?: boolean;
}> {
  if (enableSemanticSplit) return ALL_PHASES;
  // 单页直通：与任务详情可见工序一致
  return [
    { id: 'layout', label: '页图准备', wired: true },
    { id: 'extract', label: '颗粒提取', wired: true },
    { id: 'figure_extract', label: '插图提取', wired: true },
    { id: 'ttq', label: '品题推荐', wired: true },
    { id: 'ipttq', label: '品题浓缩', wired: true },
  ];
}

function parseResultPhase(raw: string | null): ResultPhase {
  if (
    raw === 'semantic_split'
    || raw === 'structure'
    || raw === 'extract'
    || raw === 'figure_extract'
    || raw === 'ttq'
    || raw === 'ipttq'
    || raw === 'layout'
  ) {
    return raw;
  }
  return 'layout';
}

/** Parse worker extras like ·等FC30s·超时2·跳过12 so skip/timeout aren't hidden. */
function ipttqStockWasteBits(rest: string): string {
  const bits: string[] = [];
  const wait = /·等FC(\d+s)/.exec(rest);
  const to = /·超时(\d+)/.exec(rest);
  const sk = /·跳过(\d+)/.exec(rest);
  if (wait) bits.push(`等 FC ${wait[1]}`);
  if (to && Number(to[1]) > 0) bits.push(`超时 ${to[1]}`);
  if (sk && Number(sk[1]) > 0) bits.push(`跳过 ${sk[1]}`);
  return bits.length ? ` · ${bits.join(' · ')}` : '';
}

/** Humanize job.progressLabel like `ipttq_condense:1/19:g2:bank3/5` for pane meta. */
function formatIpttqProgressLabel(raw: string): string {
  const key = String(raw || '').trim();
  if (!key) return '';
  if (key === 'ipttq_start' || key === 'ipttq_condense_start' || key === 'ipttq_continue_start') {
    return key === 'ipttq_continue_start' ? '继续启动中…' : '启动中…';
  }
  if (key === 'queued' || key === 'running') return '排队/领取中…';
  if (key === 'ipttq_done') return '已完成';
  if (key.startsWith('ipttq_product_stock:')) {
    const rest = key.slice('ipttq_product_stock:'.length);
    const agg = /^(\d+)\/(\d+)(?::bank(\d+)\/(\d+)(?:·进行中(\d+))?(?:·活跃(\d+)组)?(?::g(\d+))?)?/.exec(rest);
    if (agg && agg[3] != null) {
      const head = `已完成 ${agg[1]}/${agg[2]} 组`;
      const bank = `诱导已回 ${agg[3]}/${agg[4]}`;
      const inflight = agg[5] ? ` · FC 在途 ${agg[5]}` : '';
      const active = agg[6] ? ` · 活跃 ${agg[6]} 组` : '';
      return `生成诱导题库 · ${head} · ${bank}${inflight}${active}${ipttqStockWasteBits(rest)}`;
    }
    const m = /^(\d+)\/(\d+)(?::g(\d+))?(?::(skip|done|bank(\d+)\/(\d+)(?:·进行中(\d+))?))?/.exec(rest);
    if (m) {
      const head = `已完成 ${m[1]}/${m[2]} 组`;
      if (m[4] === 'skip' && m[3]) return `生成诱导题库 · ${head} · 第 ${m[3]} 组已满`;
      if (m[4] === 'done' && m[3]) return `生成诱导题库 · ${head} · 第 ${m[3]} 组完成`;
      if (m[5] != null && m[3]) {
        const inflight = m[7] ? ` · FC 在途 ${m[7]}` : '';
        return `生成诱导题库 · ${head} · 第 ${m[3]} 组已回 ${m[5]}/${m[6]}${inflight}`;
      }
      if (m[3]) return `生成诱导题库 · ${head} · 正在处理第 ${m[3]} 组`;
      return `生成诱导题库 · ${head}`;
    }
    return `生成诱导题库 · ${rest}`;
  }
  if (key === 'ipttq_product_stock') return '生成诱导题库…';
  const m = /^ipttq_condense:(\d+)\/(\d+)(?::g(\d+))?(?::(skill|done|bank(\d+)\/(\d+)))?/.exec(key);
  if (m) {
    const done = m[1];
    const total = m[2];
    const g = m[3];
    const phase = m[4];
    const bankDone = m[5];
    const bankTarget = m[6];
    const head = `已完成 ${done}/${total} 组`;
    if (phase === 'skill' && g) return `${head} · 第 ${g} 组写 skill…`;
    if (phase === 'done' && g) return `${head} · 第 ${g} 组刚完成`;
    if (bankDone != null && g) return `${head} · 第 ${g} 组诱导 ${bankDone}/${bankTarget}`;
    if (bankDone != null) return `${head} · 诱导 ${bankDone}/${bankTarget}`;
    return head;
  }
  if (key.startsWith('ipttq_condense:')) return `进行中 ${key.slice('ipttq_condense:'.length)}`;
  if (/^[a-z][a-z0-9_]*$/.test(key)) return '进行中…';
  return key;
}

function formatElapsedMs(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return '0s';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return rem > 0 ? `${min}m ${rem}s` : `${min}m`;
}

function formatLocalTimeFromIso(iso: string | undefined): string {
  const raw = String(iso || '').trim();
  if (!raw) return '';
  const ms = Date.parse(raw);
  if (!Number.isFinite(ms)) return '';
  return new Date(ms).toLocaleTimeString('zh-CN', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function readBankDraftGenerateMs(
  draft: FactoryInducedKpDraft,
  sessionStartedAt?: string,
): number | null {
  const direct = Number(draft.metadata?.generateMs);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const createdAt = String(draft.metadata?.createdAt || '').trim();
  const startedAt = String(draft.metadata?.generateStartedAt || sessionStartedAt || '').trim();
  const endMs = createdAt ? Date.parse(createdAt) : NaN;
  const startMs = startedAt ? Date.parse(startedAt) : NaN;
  if (Number.isFinite(endMs) && Number.isFinite(startMs) && endMs >= startMs) {
    return endMs - startMs;
  }
  return null;
}

function sortBankDraftsByCreatedAt(drafts: FactoryInducedKpDraft[]): FactoryInducedKpDraft[] {
  return [...drafts].sort((a, b) => {
    const aMs = Date.parse(String(a.metadata?.createdAt || '')) || 0;
    const bMs = Date.parse(String(b.metadata?.createdAt || '')) || 0;
    if (aMs !== bMs) return aMs - bMs;
    return String(a.id).localeCompare(String(b.id));
  });
}

function bankDraftsThisSession(
  drafts: FactoryInducedKpDraft[],
  sessionStartedAt?: string,
): FactoryInducedKpDraft[] {
  const t0 = sessionStartedAt ? Date.parse(sessionStartedAt) : NaN;
  if (!Number.isFinite(t0)) return sortBankDraftsByCreatedAt(drafts);
  return sortBankDraftsByCreatedAt(
    drafts.filter((d) => {
      const ca = String(d.metadata?.createdAt || '').trim();
      const ms = ca ? Date.parse(ca) : NaN;
      return Number.isFinite(ms) && ms >= t0 - 3000;
    }),
  );
}

function formatBankDraftArrivalLine(
  draft: FactoryInducedKpDraft,
  lookup: MemberReferenceLookup,
  sessionStartedAt?: string,
): string {
  const at = formatLocalTimeFromIso(draft.metadata?.createdAt);
  const genMs = readBankDraftGenerateMs(draft, sessionStartedAt);
  const title = String(draft.title || draft.metadata?.displayTitle || '诱导题').replace(/\s+/g, ' ').trim();
  const refs = formatDraftReferenceLabels(draft, lookup);
  const bits = [
    at ? `回写 ${at}` : '',
    genMs != null ? `FC ${formatElapsedMs(genMs)}` : '',
    refs ? `参考 ${refs}` : '',
  ].filter(Boolean);
  return bits.length
    ? `${bits.join(' · ')} · ${title.slice(0, 28)}${title.length > 28 ? '…' : ''}`
    : title;
}

function formatTokenCompact(n: number): string {
  const v = Math.max(0, Number(n) || 0);
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1000) return `${(v / 1000).toFixed(1)}k`;
  return String(Math.round(v));
}

function bankStockPhaseLabel(phase: string | undefined): string {
  if (phase === 'plan') return '准备抽样';
  if (phase === 'corpus') return '准备抽样';
  if (phase === 'bank') return '生成诱导题';
  if (phase === 'skip') return '已满，跳过';
  if (phase === 'done') return '完成';
  return '准备中';
}

function resolveIpttqRowTarget(row: FactoryIpttqRecord, fallback = 5): number {
  const t = Number(row.factoryMeta?.targetBank);
  return Number.isFinite(t) && t > 0 ? t : fallback;
}

function mergeBankStockIntoIpttqRow(
  row: FactoryIpttqRecord,
  status: Pick<ProductBankStockStatusResponse, 'bankCount' | 'product' | 'resolvedBankTarget'>,
): FactoryIpttqRecord {
  const drafts = (status.product.bankDrafts || []) as FactoryInducedKpDraft[];
  const target = status.resolvedBankTarget || resolveIpttqRowTarget(row);
  const bankCount = status.bankCount ?? drafts.length;
  return {
    ...row,
    bankParticleDrafts: drafts,
    effectiveBankCount: bankCount,
    factoryMeta: {
      ...row.factoryMeta,
      targetBank: target,
      bankOk: bankCount >= target,
    },
  };
}

function bankTargetFromProductMeta(meta: Record<string, unknown> | undefined): number | undefined {
  const native = Number(meta?.nativeInductionCount ?? meta?.bankTargetMemberCount);
  if (Number.isFinite(native) && native > 0) {
    return inductionTargetByMembers(native, readInductionBankMemberRatio(meta));
  }
  return undefined;
}

function mapBankStockRunState(
  productId: string,
  status: Pick<ProductBankStockStatusResponse, 'bankCount' | 'product' | 'resolvedBankTarget' | 'run' | 'elapsedMs'>,
  rowTarget?: number,
): {
  productId: string;
  status: ProductBankStockRunState['status'] | 'idle';
  bankDone: number;
  bankTarget: number;
  inFlight: number;
  timeouts: number;
  skips: number;
  usageTotal: number;
  elapsedMs: number;
  added: number;
  error: string | null;
  phase: string;
  startedAt: string;
} {
  const run = status.run;
  const bankDone = Math.max(run?.bankDone ?? 0, status.bankCount);
  const bankTarget = run?.bankTarget
    || status.resolvedBankTarget
    || rowTarget
    || bankTargetFromProductMeta(status.product.meta)
    || bankDone
    || 0;
  return {
    productId,
    status: run?.status || 'idle',
    bankDone,
    bankTarget,
    inFlight: run?.inFlight || 0,
    timeouts: run?.timeouts || 0,
    skips: run?.skips || 0,
    usageTotal: Number(run?.usage?.total_tokens) || 0,
    elapsedMs: status.elapsedMs || 0,
    added: run?.added || 0,
    error: run?.error || (run?.skippedLocked ? '成品智能体正被其它补库占用，请稍后再试' : null),
    phase: run?.phase || '',
    startedAt: run?.startedAt || '',
  };
}

type MemberReferenceLookup = {
  byIndex: Map<number, string>;
  byParticleId: Map<string, string>;
};

function clipReferenceTitle(text: string, max = 28): string {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  if (!t) return '';
  return t.length > max ? `${t.slice(0, Math.max(0, max - 1))}…` : t;
}

function buildMemberReferenceLookup(
  row: FactoryIpttqRecord,
  warehouseTtqas: FactoryWarehouseTtqa[] | undefined,
): MemberReferenceLookup {
  const byIndex = new Map<number, string>();
  const byParticleId = new Map<string, string>();
  const ttqa = (warehouseTtqas || []).find((w) => w.id === row.ttqaId);
  const particles = ttqa?.particles || [];
  for (let i = 0; i < particles.length; i += 1) {
    const p = particles[i] as FactoryWarehouseTtqaParticle;
    const qa = resolveAdmissionParticleQa({
      particleTitle: p.particleTitle,
      particleQuestions: p.particleQuestions,
      particleAnswers: p.particleAnswers,
      pap: p.pap as Record<string, unknown> | null | undefined,
    });
    const label = clipReferenceTitle(
      admissionParticleListLabel(qa, false) || p.particleTitle || `原题${i + 1}`,
    );
    byIndex.set(i, label || `原题${i + 1}`);
    const pid = String(p.particleId || p.id || '').trim();
    if (pid) byParticleId.set(pid, label || `原题${i + 1}`);
  }
  return { byIndex, byParticleId };
}

/** Instant local merge so DnD UI updates before the API round-trip. */
function optimisticMergeTtqAdmission(
  admission: FactoryTtqAdmissionResult,
  sourceId: string,
  targetId: string,
): FactoryTtqAdmissionResult {
  const rows = [...(admission.warehouseTtqas || [])];
  const sourceIdx = rows.findIndex((r) => r.id === sourceId);
  const targetIdx = rows.findIndex((r) => r.id === targetId);
  if (sourceIdx < 0 || targetIdx < 0 || sourceIdx === targetIdx) return admission;

  const source = rows[sourceIdx]!;
  const target = { ...rows[targetIdx]!, particles: [...(rows[targetIdx]!.particles || [])] };
  const existing = new Set(target.particles.map((p) => p.particleId));
  for (const p of source.particles || []) {
    if (existing.has(p.particleId)) continue;
    target.particles.push({ ...p });
    existing.add(p.particleId);
  }
  const n = target.particles.length;
  const baseTitle = String(target.title || '').replace(/\s*·\s*\d+\s*题\s*$/, '').trim();
  target.title = baseTitle
    ? (n > 1 ? `${baseTitle} · ${n} 题` : baseTitle)
    : `品题组 · ${n} 题`;

  rows.splice(sourceIdx, 1);
  const nextTargetIdx = rows.findIndex((r) => r.id === targetId);
  if (nextTargetIdx >= 0) rows[nextTargetIdx] = target;

  return {
    ...admission,
    warehouseTtqas: rows,
    warehouseTtqaCount: rows.length,
    builtAt: new Date().toISOString(),
  };
}

function formatDraftReferenceLabels(
  draft: FactoryInducedKpDraft,
  lookup: MemberReferenceLookup,
): string {
  const titles = Array.isArray(draft.metadata?.sampledMemberTitles)
    ? draft.metadata!.sampledMemberTitles!.map((x) => clipReferenceTitle(String(x || ''))).filter(Boolean)
    : [];
  if (titles.length) return titles.join(' · ');
  const ids = Array.isArray(draft.metadata?.sampledParticleIds)
    ? draft.metadata!.sampledParticleIds!.map((x) => String(x || '').trim()).filter(Boolean)
    : [];
  const indices = Array.isArray(draft.metadata?.sampledMemberIndices)
    ? draft.metadata!.sampledMemberIndices!.map((x) => Number(x)).filter((x) => Number.isFinite(x))
    : [];
  const labels: string[] = [];
  if (ids.length) {
    for (const id of ids) {
      labels.push(lookup.byParticleId.get(id) || id.slice(0, 12));
    }
  } else {
    for (const idx of indices) {
      labels.push(lookup.byIndex.get(idx) || `序位${idx + 1}`);
    }
  }
  return labels.join(' · ');
}

function readInductionPoolMeta(
  meta: Record<string, unknown> | null | undefined,
  nativeMemberCount?: number,
  memberRatio?: number,
): { poolSize: number; referenceSampleSize: number } {
  const cp = meta?.corpusPlan;
  const cpObj = cp && typeof cp === 'object' && !Array.isArray(cp)
    ? cp as Record<string, unknown>
    : null;
  const poolSize = Math.max(
    0,
    Number(cpObj?.memberCount ?? cpObj?.nativeMemberCount ?? nativeMemberCount) || 0,
  );
  const ratio = memberRatio ?? readInductionBankMemberRatio(meta);
  // Prefer live formula so UI does not stick to an older corpusPlan.referenceSampleSize
  // (e.g. pre-cap runs that stored sample==target).
  const referenceSampleSize = inductionReferenceSampleSize(poolSize, ratio);
  return { poolSize, referenceSampleSize };
}

function needsTtqProductResolve(rows: FactoryWarehouseTtqa[] | undefined): boolean {
  if (!rows?.length) return false;
  return rows.some((r) => {
    const d = r.factoryMeta?.disposition;
    return !d || d === 'critical';
  });
}

const UNIT_RESULT_PHASES: ResultPhase[] = ['structure', 'extract', 'ttq'];

const HIDDEN = ['header', 'footer', 'page_number', 'page-number'];
const EDGE_PX = 4;
const EDGE_THRESHOLD = 90;

function formatPageRange(start: unknown, end: unknown) {
  if (start == null && end == null) return '';
  if (start != null && end != null && Number(start) !== Number(end)) {
    return `${Number(start) + 1}–${Number(end) + 1}`;
  }
  return `${Number(start ?? end) + 1}`;
}

function useEdgeWheelNavigate(
  ref: RefObject<HTMLElement | null>,
  onEdge: (direction: 'prev' | 'next') => void,
  deps: unknown[],
  options?: {
    allowEdge?: (direction: 'prev' | 'next') => boolean;
    /** When edge nav is suppressed (e.g. extract still has pages), still handle wheel intent */
    onSuppressedEdge?: (direction: 'prev' | 'next') => void;
  },
) {
  const onEdgeRef = useRef(onEdge);
  onEdgeRef.current = onEdge;
  const allowEdgeRef = useRef(options?.allowEdge);
  allowEdgeRef.current = options?.allowEdge;
  const onSuppressedEdgeRef = useRef(options?.onSuppressedEdge);
  onSuppressedEdgeRef.current = options?.onSuppressedEdge;
  const cooldownRef = useRef(false);
  const accumRef = useRef(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const atTop = () => el.scrollTop <= EDGE_PX;
    const atBottom = () => el.scrollTop + el.clientHeight >= el.scrollHeight - EDGE_PX;

    const fire = (direction: 'prev' | 'next') => {
      if (cooldownRef.current) return;
      cooldownRef.current = true;
      accumRef.current = 0;
      onEdgeRef.current(direction);
      window.setTimeout(() => {
        cooldownRef.current = false;
      }, 900);
    };

    const onWheel = (e: WheelEvent) => {
      if (cooldownRef.current) return;
      if (e.deltaY > 0 && atBottom()) {
        if (allowEdgeRef.current && !allowEdgeRef.current('next')) {
          accumRef.current = 0;
          onSuppressedEdgeRef.current?.('next');
          return;
        }
        accumRef.current += e.deltaY;
        if (accumRef.current >= EDGE_THRESHOLD) fire('next');
      } else if (e.deltaY < 0 && atTop()) {
        if (allowEdgeRef.current && !allowEdgeRef.current('prev')) {
          accumRef.current = 0;
          onSuppressedEdgeRef.current?.('prev');
          return;
        }
        accumRef.current += -e.deltaY;
        if (accumRef.current >= EDGE_THRESHOLD) fire('prev');
      } else {
        accumRef.current = 0;
      }
    };

    el.addEventListener('wheel', onWheel, { passive: true });
    return () => el.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps passed by caller
  }, deps);
}

export function FactoryTaskResultsPage() {
  const { batchId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const phase = parseResultPhase(searchParams.get('phase'));
  const warnExtract = searchParams.get('warn') === 'extract';
  const routeUnitId = String(searchParams.get('unit') || '').trim() || null;
  const routeQaParam = searchParams.get('qa');
  const routeQaIndex = (() => {
    if (routeQaParam == null || routeQaParam === '') return null;
    const n = Number(routeQaParam);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.floor(n);
  })();

  // Legacy ?phase=products → top-level 成品智能体 tab
  useEffect(() => {
    if (searchParams.get('phase') === 'products' && batchId) {
      navigate(`/factory/goods?batchId=${encodeURIComponent(batchId)}`, { replace: true });
    }
  }, [batchId, navigate, searchParams]);

  const [batch, setBatch] = useState<FactoryBatch | null>(null);
  const [assets, setAssets] = useState<FactoryAsset[]>([]);
  const [layoutUnits, setLayoutUnits] = useState<FactoryLayoutUnit[]>([]);
  const [structuralUnits, setStructuralUnits] = useState<FactoryStructuralUnit[]>([]);
  const [ttqAdmission, setTtqAdmission] = useState<FactoryTtqAdmissionResult | null>(null);
  const [ipttqBatch, setIpttqBatch] = useState<FactoryIpttqBatchResult | null>(null);
  const [ipttqDetail, setIpttqDetail] = useState<FactoryIpttqRecord | null>(null);
  const [ipttqProductMeta, setIpttqProductMeta] = useState<Record<string, unknown> | null>(null);
  const ipttqDialogRef = useRef<HTMLDivElement>(null);
  const ipttqBankLenRef = useRef(0);
  const [ipttqBankGen, setIpttqBankGen] = useState<ReturnType<typeof mapBankStockRunState> | null>(null);
  const [ipttqBankGenBusy, setIpttqBankGenBusy] = useState(false);
  const [ipttqBankPauseBusy, setIpttqBankPauseBusy] = useState(false);
  const [semanticSplits, setSemanticSplits] = useState<FactorySemanticSplitPage[]>([]);
  const [semanticSummary, setSemanticSummary] = useState<FactorySemanticSplitsResult['summary'] | null>(
    null,
  );
  const [activeUnit, setActiveUnit] = useState(0);
  const [contentItems, setContentItems] = useState<PdfContentItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<number | undefined>(undefined);
  const [selectionSource, setSelectionSource] = useState<SelectionSource>(null);
  const [pdfScrollRequestId, setPdfScrollRequestId] = useState(0);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [scrollAnchor, setScrollAnchor] = useState<'start' | 'end'>('start');
  const [layoutMode, setLayoutMode] = useState<PdfLayoutMode>('fit-width');
  /** Default image: avoid downloading whole booklet PDF over OSS. */
  const [sourceViewMode, setSourceViewMode] = useState<SourceViewMode>('image');
  const [pageImageMeta, setPageImageMeta] = useState<AuthImageLoadedMeta | null>(null);
  const [pageImageInfoOpen, setPageImageInfoOpen] = useState(false);
  const pageImageInfoRef = useRef<HTMLDivElement>(null);
  const [figureCropPreview, setFigureCropPreview] = useState<FigureCropPreview | null>(null);
  const [layoutMissing, setLayoutMissing] = useState(false);
  const [layoutError, setLayoutError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState('');
  const [reextractingUnitIds, setReextractingUnitIds] = useState<Set<string>>(new Set());
  const [reenricingUnitIds, setReenricingUnitIds] = useState<Set<string>>(new Set());
  const [flushProfilesBusy, setFlushProfilesBusy] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [unitMoreMenuId, setUnitMoreMenuId] = useState<string | null>(null);
  const [ignoreExtractBusyIds, setIgnoreExtractBusyIds] = useState<Set<string>>(new Set());
  const [reextractAllBusy, setReextractAllBusy] = useState(false);
  const [ttqRerunBusy, setTtqRerunBusy] = useState(false);
  const [ttqMergeOrphansBusy, setTtqMergeOrphansBusy] = useState(false);
  const [ttqManualMergeMode, setTtqManualMergeMode] = useState(false);
  const [ttqMergeQueueDepth, setTtqMergeQueueDepth] = useState(0);
  const [ttqManualMergeCanUndo, setTtqManualMergeCanUndo] = useState(false);
  const [ttqDragSourceId, setTtqDragSourceId] = useState<string | null>(null);
  const [ttqDropTargetId, setTtqDropTargetId] = useState<string | null>(null);
  const ttqDragSourceIdRef = useRef<string | null>(null);
  const ttqDidDragRef = useRef(false);
  const ttqMergeQueueRef = useRef<Array<{
    id: string;
    sourceId: string;
    targetId: string;
    admissionBefore: FactoryTtqAdmissionResult;
    undoAfterSync?: boolean;
  }>>([]);
  const ttqMergeInflightRef = useRef<{
    id: string;
    sourceId: string;
    targetId: string;
    admissionBefore: FactoryTtqAdmissionResult;
    undoAfterSync?: boolean;
  } | null>(null);
  const ttqMergeDrainRunningRef = useRef(false);
  const ttqAdmissionRef = useRef<FactoryTtqAdmissionResult | null>(null);
  const ttqServerUndoAvailableRef = useRef(false);
  const [ttqProductResolveBusy, setTtqProductResolveBusy] = useState(false);
  const [ttqJobProgress, setTtqJobProgress] = useState('');
  const [ttqJobProgressRaw, setTtqJobProgressRaw] = useState('');
  const [ttqReadmitReport, setTtqReadmitReport] = useState<SimilarityReAdmitReport | null>(null);
  const [ipttqRerunBusy, setIpttqRerunBusy] = useState(false);
  const [ipttqJobProgress, setIpttqJobProgress] = useState('');
  const [artifactPhaseLoading, setArtifactPhaseLoading] = useState(false);
  const [ttqGroupDrawerId, setTtqGroupDrawerId] = useState<string | null>(null);
  const [ttqGroupDrawerLoading, setTtqGroupDrawerLoading] = useState(false);
  const [ttqGroupDrawerRow, setTtqGroupDrawerRow] = useState<FactoryWarehouseTtqa | null>(null);
  const [ttqParticleDetail, setTtqParticleDetail] = useState<{
    groupTitle: string;
    particle: FactoryWarehouseTtqaParticle;
  } | null>(null);
  const itemRefs = useRef<Map<number, HTMLElement>>(new Map());
  const qaItemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const itemScrollRef = useRef<HTMLDivElement>(null);
  const extractLoadMoreRef = useRef<HTMLDivElement>(null);
  const toastTimerRef = useRef<number | null>(null);
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const ttqProductResolveAttemptedRef = useRef<string | null>(null);
  const enrichLoadingRef = useRef<Set<string>>(new Set());
  const [extractVisibleCount, setExtractVisibleCount] = useState(EXTRACT_PAGE_SIZE);
  const [enrichLoadingIds, setEnrichLoadingIds] = useState<Set<string>>(new Set());
  const [extractFailMenuOpen, setExtractFailMenuOpen] = useState(false);
  const extractFailMenuRef = useRef<HTMLDivElement>(null);
  const pendingFocusUnitRef = useRef<number | null>(null);

  const showToast = useCallback((message: string, durationMs = 2200) => {
    setToast(message);
    if (toastTimerRef.current != null) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => {
      setToast('');
      toastTimerRef.current = null;
    }, durationMs);
  }, []);

  const onReextractUnit = useCallback(
    async (unitId: string, opts?: { particlesOnly?: boolean }) => {
      if (!batchId || reextractAllBusy) return;
      if (batch && isFactoryBatchOwnerAccepted(batch)) {
        showToast('已验收，不可再提取。', 2200);
        return;
      }
      if (reextractingUnitIds.has(unitId)) return;

      setReextractingUnitIds((prev) => new Set(prev).add(unitId));
      try {
        const result = await factoryService.reextractStructuralUnit(batchId, unitId, opts);
        const structs = await factoryService.listStructuralUnits(batchId, { view: 'preview' });
        setStructuralUnits(structs);
        const refreshedBatch = await factoryService.getBatch(batchId).catch(() => null);
        if (refreshedBatch?.batch) setBatch(refreshedBatch.batch);
        showToast(
          result.message
            || (result.ok
              ? (opts?.particlesOnly
                ? `知识颗粒已重提（${result.qaCount} QA）`
                : `本页已重抽（${result.qaCount} QA）`)
              : `重提失败：${result.error || 'unknown'}`),
          result.ok ? 2200 : 4500,
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        showToast(msg.includes('失败') ? msg : `重提失败：${msg}`, 4500);
      } finally {
        setReextractingUnitIds((prev) => {
          const next = new Set(prev);
          next.delete(unitId);
          return next;
        });
      }
    },
    [batch, batchId, reextractingUnitIds, reextractAllBusy, showToast],
  );

  const onReenrichUnit = useCallback(
    async (unitId: string) => {
      if (!batchId || reextractAllBusy) return;
      if (batch && isFactoryBatchOwnerAccepted(batch)) {
        showToast('已验收，不可再改富化。', 2200);
        return;
      }
      if (reenricingUnitIds.has(unitId) || reextractingUnitIds.has(unitId)) return;

      setReenricingUnitIds((prev) => new Set(prev).add(unitId));
      try {
        const result = await factoryService.reenrichStructuralUnit(batchId, unitId);
        if (result.unit) {
          setStructuralUnits((prev) =>
            prev.map((u) =>
              (u.id === unitId
                ? {
                    ...u,
                    ...result.unit,
                    extractQa: u.extractQa?.length ? u.extractQa : result.unit?.extractQa,
                    view: 'full',
                  }
                : u)),
          );
        } else {
          const structs = await factoryService.listStructuralUnits(batchId, { view: 'preview' });
          setStructuralUnits(structs);
        }
        showToast(
          result.message
            || (result.ok
              ? `本页画像已刷新（RFE ${result.rfeBound}/${result.qaCount}）`
              : `本页画像刷新失败：${result.error || 'unknown'}`),
          result.ok ? 2200 : 4500,
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        showToast(msg.includes('画像') ? msg : `本页画像刷新失败：${msg}`, 4500);
      } finally {
        setReenricingUnitIds((prev) => {
          const next = new Set(prev);
          next.delete(unitId);
          return next;
        });
      }
    },
    [batch, batchId, reenricingUnitIds, reextractingUnitIds, reextractAllBusy, showToast],
  );

  const onToggleIgnoreExtract = useCallback(
    async (unitId: string, ignore: boolean) => {
      if (!batchId) return;
      if (batch && isFactoryBatchOwnerAccepted(batch)) {
        showToast('已验收，不可再改提取标记。', 2200);
        return;
      }
      if (ignoreExtractBusyIds.has(unitId)) return;
      setUnitMoreMenuId(null);
      setIgnoreExtractBusyIds((prev) => new Set(prev).add(unitId));
      try {
        const result = await factoryService.setStructuralUnitExtractIgnored(
          batchId,
          unitId,
          ignore,
        );
        const structs = await factoryService.listStructuralUnits(batchId, { view: 'preview' });
        setStructuralUnits(structs);
        if (result.batch) setBatch(result.batch);
        else {
          const refreshedBatch = await factoryService.getBatch(batchId).catch(() => null);
          if (refreshedBatch?.batch) setBatch(refreshedBatch.batch);
        }
        showToast(
          result.message
            || (ignore ? '已忽略本页提取' : '已取消忽略'),
          2200,
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        showToast(`忽略标记失败：${msg}`, 4500);
      } finally {
        setIgnoreExtractBusyIds((prev) => {
          const next = new Set(prev);
          next.delete(unitId);
          return next;
        });
      }
    },
    [batch, batchId, ignoreExtractBusyIds, showToast],
  );

  const onReextractAll = useCallback(async () => {
    if (!batchId || reextractAllBusy || reextractingUnitIds.size > 0) return;
    if (batch && isFactoryBatchOwnerAccepted(batch)) {
      showToast('已验收，不可再提取。', 2200);
      return;
    }
    if (!confirm('按当前学科策略重抽全部页？已提取的知识颗粒会被覆盖。')) return;
    setMoreMenuOpen(false);
    setReextractAllBusy(true);
    try {
      await factoryService.continueExtract(batchId, { forceRedo: true });
      showToast('已排队全部重提取；成功页会陆续刷新。', 3200);
      const structs = await factoryService.listStructuralUnits(batchId, { view: 'preview' });
      setStructuralUnits(structs);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(`全部重提取失败：${msg}`, 4500);
    } finally {
      setReextractAllBusy(false);
    }
  }, [batch, batchId, reextractAllBusy, reextractingUnitIds, showToast]);

  const onRerunTtqTitles = useCallback(async () => {
    if (!batchId || ttqRerunBusy || ttqMergeOrphansBusy || reextractAllBusy || ipttqRerunBusy) return;
    if (batch && isFactoryBatchOwnerAccepted(batch)) {
      showToast('已验收，不可再发起处理。', 2200);
      return;
    }
    setMoreMenuOpen(false);
    setTtqRerunBusy(true);
    setTtqReadmitReport(null);
    ttqProductResolveAttemptedRef.current = null;
    itemScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      const res = await factoryService.rerunTtqAdmission(batchId);
      showToast(res.message || '已排队重新跑品题推荐', 2800);
      for (let i = 0; i < 40; i += 1) {
        await new Promise((r) => setTimeout(r, 1500));
        const [admission, detail] = await Promise.all([
          factoryService.getTtqAdmission(batchId),
          factoryService.getBatch(batchId).catch(() => null),
        ]);
        setTtqAdmission(admission?.ttqAdmission || null);
        if (detail?.batch) setBatch(detail.batch);
        const reportRaw = detail?.batch?.meta?.ttqSimilarityReadmitReport;
        if (reportRaw && typeof reportRaw === 'object' && !Array.isArray(reportRaw)) {
          setTtqReadmitReport(reportRaw as SimilarityReAdmitReport);
        }
        const builtAt = admission?.builtAt || admission?.ttqAdmission?.builtAt;
        if (builtAt && Date.parse(builtAt) >= Date.now() - 120_000) {
          const sample = admission?.ttqAdmission?.warehouseTtqas?.[0]?.title || '';
          if (sample && !/^【\d+】/.test(sample) && !/=/.test(sample)) break;
        }
      }
      const ipttq = await factoryService.getIpttq(batchId).catch(() => null);
      setIpttqBatch(ipttq?.ipttq || null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), 4500);
    } finally {
      setTtqRerunBusy(false);
    }
  }, [batch, batchId, ttqRerunBusy, ttqMergeOrphansBusy, reextractAllBusy, ipttqRerunBusy, showToast]);

  const onMergeTtqOrphanGroups = useCallback(async () => {
    if (!batchId || ttqMergeOrphansBusy || ttqRerunBusy || reextractAllBusy || ipttqRerunBusy || ttqMergeQueueDepth > 0) return;
    if (batch && isFactoryBatchOwnerAccepted(batch)) {
      showToast('已验收，不可再发起处理。', 2200);
      return;
    }
    setMoreMenuOpen(false);
    setTtqManualMergeMode(false);
    setTtqMergeOrphansBusy(true);
    setTtqReadmitReport(null);
    // Pin orphan candidates to the top of the list viewport.
    itemScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    try {
      const res = await factoryService.mergeTtqOrphanGroups(batchId);
      if (res.ttqAdmission) setTtqAdmission(res.ttqAdmission);
      else {
        const admission = await factoryService.getTtqAdmission(batchId).catch(() => null);
        setTtqAdmission(admission?.ttqAdmission || null);
      }
      if (res.report) setTtqReadmitReport(res.report);
      showToast(
        res.message
          || (res.merged > 0
            ? `已融合 ${res.merged} 个孤立推荐组`
            : `未发现可融合的孤立组（审视 ${res.considered} 组）`),
        3200,
      );
      itemScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), 4500);
    } finally {
      setTtqMergeOrphansBusy(false);
    }
  }, [batch, batchId, ttqMergeOrphansBusy, ttqRerunBusy, reextractAllBusy, ipttqRerunBusy, ttqMergeQueueDepth, showToast]);

  const syncTtqMergeQueueDepth = useCallback(() => {
    setTtqMergeQueueDepth(
      ttqMergeQueueRef.current.length + (ttqMergeInflightRef.current ? 1 : 0),
    );
  }, []);

  const refreshTtqManualMergeCanUndo = useCallback(() => {
    const localPending = ttqMergeQueueRef.current.length > 0 || Boolean(ttqMergeInflightRef.current);
    const stack = batch?.meta?.ttqManualMergeUndoStack;
    const fromMeta = Array.isArray(stack) && stack.length > 0;
    setTtqManualMergeCanUndo(localPending || ttqServerUndoAvailableRef.current || fromMeta);
  }, [batch?.meta?.ttqManualMergeUndoStack]);

  const drainTtqMergeQueue = useCallback(async () => {
    if (ttqMergeDrainRunningRef.current || !batchId) return;
    ttqMergeDrainRunningRef.current = true;
    try {
      while (ttqMergeQueueRef.current.length > 0) {
        const next = ttqMergeQueueRef.current.shift()!;
        if (next.undoAfterSync) {
          syncTtqMergeQueueDepth();
          continue;
        }
        ttqMergeInflightRef.current = next;
        syncTtqMergeQueueDepth();
        try {
          const res = await factoryService.manualMergeTtqGroups(
            batchId,
            next.sourceId,
            next.targetId,
          );
          if (next.undoAfterSync) {
            try {
              const undoRes = await factoryService.undoManualMergeTtqGroups(batchId);
              ttqServerUndoAvailableRef.current = Boolean(undoRes.canUndo);
              refreshTtqManualMergeCanUndo();
            } catch (undoErr) {
              showToast(
                undoErr instanceof Error ? undoErr.message : String(undoErr),
                4500,
              );
            }
          } else {
            ttqServerUndoAvailableRef.current = res.canUndo !== false;
            if (ttqMergeQueueRef.current.length === 0) {
              // Only apply server snapshot when nothing newer is queued.
              if (res.ttqAdmission) {
                setTtqAdmission(res.ttqAdmission);
                ttqAdmissionRef.current = res.ttqAdmission;
              }
              setTtqManualMergeCanUndo(res.canUndo !== false);
              void factoryService.getIpttq(batchId).then((ipttq) => {
                if (ipttq?.ipttq) setIpttqBatch(ipttq.ipttq);
              }).catch(() => undefined);
            } else {
              setTtqManualMergeCanUndo(true);
            }
          }
        } catch (err) {
          // Drop remaining queued ops and roll back to before this failed merge.
          ttqMergeQueueRef.current = [];
          setTtqAdmission(next.admissionBefore);
          ttqAdmissionRef.current = next.admissionBefore;
          refreshTtqManualMergeCanUndo();
          showToast(err instanceof Error ? err.message : String(err), 4500);
        } finally {
          ttqMergeInflightRef.current = null;
          syncTtqMergeQueueDepth();
        }
      }
    } finally {
      ttqMergeDrainRunningRef.current = false;
      syncTtqMergeQueueDepth();
      if (ttqMergeQueueRef.current.length > 0) {
        void drainTtqMergeQueue();
      }
    }
  }, [batchId, showToast, syncTtqMergeQueueDepth, refreshTtqManualMergeCanUndo]);

  const toggleTtqManualMergeMode = useCallback(() => {
    if (batch && isFactoryBatchOwnerAccepted(batch)) {
      showToast('已验收，不可再合并。', 2200);
      return;
    }
    setMoreMenuOpen(false);
    setTtqManualMergeMode((prev) => {
      const next = !prev;
      if (next) {
        showToast('手动合并：拖动源卡放到目标卡上；点 Banner「退出」或按 Esc 结束。', 3600);
      }
      return next;
    });
    setTtqDragSourceId(null);
    setTtqDropTargetId(null);
    ttqDragSourceIdRef.current = null;
    ttqDidDragRef.current = false;
  }, [batch, showToast]);

  const exitTtqManualMergeMode = useCallback(() => {
    setTtqManualMergeMode(false);
    setTtqDragSourceId(null);
    setTtqDropTargetId(null);
    ttqDragSourceIdRef.current = null;
    ttqDidDragRef.current = false;
  }, []);

  const onManualMergeDrop = useCallback((sourceId: string, targetId: string) => {
    if (!batchId || !sourceId || !targetId || sourceId === targetId) return;
    if (ttqRerunBusy || ttqMergeOrphansBusy) return;
    if (batch && isFactoryBatchOwnerAccepted(batch)) {
      showToast('已验收，不可再合并。', 2200);
      return;
    }
    const scrollEl = itemScrollRef.current;
    const keepScrollTop = scrollEl?.scrollTop ?? 0;
    ttqDragSourceIdRef.current = null;
    setTtqDragSourceId(null);
    setTtqDropTargetId(null);

    let admissionBefore: FactoryTtqAdmissionResult | null = null;
    setTtqAdmission((prev) => {
      if (!prev) return prev;
      admissionBefore = prev;
      const next = optimisticMergeTtqAdmission(prev, sourceId, targetId);
      ttqAdmissionRef.current = next;
      return next;
    });
    if (!admissionBefore) return;

    ttqMergeQueueRef.current.push({
      id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      sourceId,
      targetId,
      admissionBefore,
    });
    setTtqManualMergeCanUndo(true);
    syncTtqMergeQueueDepth();
    window.requestAnimationFrame(() => {
      if (scrollEl) scrollEl.scrollTop = keepScrollTop;
    });
    void drainTtqMergeQueue();
  }, [
    batchId,
    batch,
    ttqRerunBusy,
    ttqMergeOrphansBusy,
    showToast,
    syncTtqMergeQueueDepth,
    drainTtqMergeQueue,
  ]);

  const onUndoManualMerge = useCallback(async () => {
    if (!batchId || !ttqManualMergeCanUndo) return;
    if (batch && isFactoryBatchOwnerAccepted(batch)) {
      showToast('已验收，不可再撤销。', 2200);
      return;
    }
    const scrollEl = itemScrollRef.current;
    const keepScrollTop = scrollEl?.scrollTop ?? 0;

    // 1) Cancel last not-yet-sent merge — instant local undo.
    if (ttqMergeQueueRef.current.length > 0) {
      const last = ttqMergeQueueRef.current.pop()!;
      setTtqAdmission(last.admissionBefore);
      ttqAdmissionRef.current = last.admissionBefore;
      syncTtqMergeQueueDepth();
      refreshTtqManualMergeCanUndo();
      window.requestAnimationFrame(() => {
        if (scrollEl) scrollEl.scrollTop = keepScrollTop;
      });
      showToast('已撤销（尚未同步到服务器）', 2200);
      return;
    }

    // 2) In-flight merge: restore UI now, reverse on server when request returns.
    if (ttqMergeInflightRef.current) {
      const inflight = ttqMergeInflightRef.current;
      inflight.undoAfterSync = true;
      setTtqAdmission(inflight.admissionBefore);
      ttqAdmissionRef.current = inflight.admissionBefore;
      refreshTtqManualMergeCanUndo();
      window.requestAnimationFrame(() => {
        if (scrollEl) scrollEl.scrollTop = keepScrollTop;
      });
      showToast('已撤销，后台将回滚同步', 2400);
      return;
    }

    // 3) Already synced — server undo.
    try {
      const res = await factoryService.undoManualMergeTtqGroups(batchId);
      if (res.ttqAdmission) {
        setTtqAdmission(res.ttqAdmission);
        ttqAdmissionRef.current = res.ttqAdmission;
      } else {
        const admission = await factoryService.getTtqAdmission(batchId).catch(() => null);
        if (admission?.ttqAdmission) {
          setTtqAdmission(admission.ttqAdmission);
          ttqAdmissionRef.current = admission.ttqAdmission;
        }
      }
      setTtqManualMergeCanUndo(Boolean(res.canUndo));
      ttqServerUndoAvailableRef.current = Boolean(res.canUndo);
      window.requestAnimationFrame(() => {
        if (scrollEl) scrollEl.scrollTop = keepScrollTop;
      });
      void factoryService.getIpttq(batchId).then((ipttq) => {
        if (ipttq?.ipttq) setIpttqBatch(ipttq.ipttq);
      }).catch(() => undefined);
      showToast(res.message || '已撤销上一步合并', 3200);
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), 4500);
    }
  }, [
    batchId,
    batch,
    ttqManualMergeCanUndo,
    showToast,
    syncTtqMergeQueueDepth,
    refreshTtqManualMergeCanUndo,
  ]);

  useEffect(() => {
    ttqAdmissionRef.current = ttqAdmission;
  }, [ttqAdmission]);

  useEffect(() => {
    if (ttqMergeQueueDepth > 0) return;
    const stack = batch?.meta?.ttqManualMergeUndoStack;
    if (Array.isArray(stack) && stack.length > 0) {
      ttqServerUndoAvailableRef.current = true;
      setTtqManualMergeCanUndo(true);
    }
  }, [batch?.meta?.ttqManualMergeUndoStack, ttqMergeQueueDepth]);

  useEffect(() => {
    if (!ttqManualMergeMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') exitTtqManualMergeMode();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ttqManualMergeMode, exitTtqManualMergeMode]);

  const openTtqGroupDrawer = useCallback(async (ttqaId: string) => {
    if (!batchId || !ttqaId) return;
    setTtqGroupDrawerId(ttqaId);
    setTtqGroupDrawerLoading(true);
    setTtqGroupDrawerRow(null);
    try {
      const res = await factoryService.getTtqAdmissionGroup(batchId, ttqaId);
      setTtqGroupDrawerRow(res.warehouseTtqa || null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), 4000);
      setTtqGroupDrawerId(null);
    } finally {
      setTtqGroupDrawerLoading(false);
    }
  }, [batchId, showToast]);

  const closeTtqGroupDrawer = useCallback(() => {
    setTtqGroupDrawerId(null);
    setTtqGroupDrawerRow(null);
    setTtqGroupDrawerLoading(false);
  }, []);

  const onRerunIpttq = useCallback(async (mode: 'full' | 'continue' = 'full') => {
    if (!batchId || ipttqRerunBusy || ttqRerunBusy || reextractAllBusy || ttqMergeOrphansBusy) return;
    if (batch && isFactoryBatchOwnerAccepted(batch)) {
      showToast('已验收，不可再发起处理。', 2200);
      return;
    }
    setMoreMenuOpen(false);
    setIpttqRerunBusy(true);
    setIpttqJobProgress(mode === 'continue' ? '继续排队中…' : '排队中…');
    try {
      const res = await factoryService.rerunIpttq(batchId, { mode });
      showToast(
        res.message
          || (mode === 'continue'
            ? '已排队继续品题浓缩（补缺口）'
            : '已排队重新跑品题浓缩（多组需数分钟）'),
        3600,
      );
      // Poll AppFactory only (never FC). Progress lives in pane meta — not toast spam.
      for (let i = 0; i < 180; i += 1) {
        await new Promise((r) => setTimeout(r, 5000));
        const [ipttq, detail] = await Promise.all([
          factoryService.getIpttq(batchId),
          factoryService.getBatch(batchId).catch(() => null),
        ]);
        setIpttqBatch(ipttq?.ipttq || null);
        const job = (detail?.jobs || []).find((j) => j.jobType === 'ipttq');
        const label = String(job?.progressLabel || '');
        if (label) setIpttqJobProgress(formatIpttqProgressLabel(label));
        if (job?.status === 'failed') {
          showToast(job.errorMessage || '品题浓缩失败', 4500);
          break;
        }
        const builtAt = ipttq?.builtAt || ipttq?.ipttq?.builtAt;
        const count = ipttq?.ipttq?.ipttqCount || 0;
        const partial = (ipttq?.ipttq?.warnings || []).some((w) => String(w).startsWith('partial:'));
        if (job?.status === 'succeeded' && builtAt) {
          showToast(
            count > 0 ? `品题浓缩完成 · ${count} 条` : '品题浓缩完成',
            3200,
          );
          break;
        }
        if (builtAt && count > 0 && !partial && job?.status !== 'running' && job?.status !== 'queued') {
          break;
        }
        if (ipttq?.summary?.skipped) break;
      }
      const finalIpttq = await factoryService.getIpttq(batchId).catch(() => null);
      setIpttqBatch(finalIpttq?.ipttq || null);
      if (!(finalIpttq?.ipttq?.records?.length)) {
        showToast('品题浓缩尚未产出记录，请稍后刷新或查看任务进度', 4500);
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : String(err), 4500);
    } finally {
      setIpttqRerunBusy(false);
      setIpttqJobProgress('');
    }
  }, [batch, batchId, ipttqRerunBusy, ttqRerunBusy, reextractAllBusy, ttqMergeOrphansBusy, showToast]);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current != null) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!moreMenuOpen) return;
    const onDoc = (e: MouseEvent) => {
      if (!moreMenuRef.current?.contains(e.target as Node)) setMoreMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreMenuOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [moreMenuOpen]);

  const setParticleRoute = useCallback(
    (unitId: string | null, qaIndex: number | null) => {
      const sp = new URLSearchParams(searchParams);
      if (unitId) sp.set('unit', unitId);
      else sp.delete('unit');
      if (qaIndex != null && Number.isFinite(qaIndex) && qaIndex >= 0) {
        sp.set('qa', String(Math.floor(qaIndex)));
      } else {
        sp.delete('qa');
      }
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setPhase = useCallback(
    (next: ResultPhase) => {
      const sp = new URLSearchParams(searchParams);
      sp.set('phase', next);
      const keepParticle = next === 'extract' || next === 'figure_extract';
      if (!keepParticle) {
        sp.delete('unit');
        sp.delete('qa');
        setSelectedItem(undefined);
        setSelectionSource(null);
      }
      setSearchParams(sp, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const loadShell = useCallback(async () => {
    const [detail, units, structs, token] = await Promise.all([
      factoryService.getBatch(batchId),
      factoryService.listLayoutUnits(batchId).catch(() => [] as FactoryLayoutUnit[]),
      factoryService.listStructuralUnits(batchId, { view: 'preview' }).catch(
        () => [] as FactoryStructuralUnit[],
      ),
      factoryService.getAccessToken(),
    ]);
    setBatch(detail.batch);
    const reportRaw = detail.batch?.meta?.ttqSimilarityReadmitReport;
    if (reportRaw && typeof reportRaw === 'object' && !Array.isArray(reportRaw)) {
      setTtqReadmitReport(reportRaw as SimilarityReAdmitReport);
    }
    setAssets(detail.assets || []);
    setLayoutUnits(units);
    setStructuralUnits(structs);
    setAuthToken(token || null);
    if (detail.artifactAccess === false) {
      throw new Error(detail.message || '无权查看加工产物');
    }
  }, [batchId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        await loadShell();
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadShell]);

  const loadPhaseArtifacts = useCallback(async () => {
    if (!batchId) return;
    setArtifactPhaseLoading(true);
    try {
      const [admission, ipttq] = await Promise.all([
        factoryService.getTtqAdmission(batchId).catch(() => null),
        factoryService.getIpttq(batchId).catch(() => null),
      ]);
      if (admission?.ttqAdmission) setTtqAdmission(admission.ttqAdmission);
      if (ipttq?.ipttq) setIpttqBatch(ipttq.ipttq);
    } finally {
      setArtifactPhaseLoading(false);
    }
  }, [batchId]);

  useEffect(() => {
    if (!batchId || loading) return;
    if (phase !== 'ttq' && phase !== 'ipttq') return;
    void loadPhaseArtifacts();
  }, [batchId, loading, phase, loadPhaseArtifacts]);

  const ttqMetaHint = useMemo(() => {
    const m = batch?.meta || {};
    const groups = Math.max(0, Number(m.ttqWarehouseCount) || 0);
    const particles = Math.max(0, Number(m.ttqParticleCount) || 0);
    if (groups <= 0 && particles <= 0) return '';
    return `任务摘要：${groups > 0 ? `${groups} 组成品智能体` : ''}${groups > 0 && particles > 0 ? ' · ' : ''}${particles > 0 ? `${particles} 颗粒已绑定` : ''}`;
  }, [batch?.meta]);

  const ipttqMetaHint = useMemo(() => {
    const m = batch?.meta || {};
    const actual = Math.max(0, Number(m.ipttqBankDraftCount) || 0);
    const target = Math.max(0, Number(m.ipttqBankTargetSum) || 0);
    const groups = Math.max(0, Number(m.ipttqCount) || Number(m.ttqWarehouseCount) || 0);
    if (actual <= 0 && target <= 0 && groups <= 0) return '';
    const bank = target > 0 ? `诱导题 ${actual}/${target}` : (actual > 0 ? `诱导题 ${actual}` : '');
    const head = groups > 0 ? `${groups} 组` : '';
    return ['任务摘要', head, bank].filter(Boolean).join(' · ');
  }, [batch?.meta]);

  const visiblePhases = useMemo(
    () => phasesForBatch(batch?.enableSemanticSplit),
    [batch?.enableSemanticSplit],
  );

  const warehouseTtqaCount = useMemo(() => {
    const fromAdmission = Number(ttqAdmission?.warehouseTtqaCount) || 0;
    const fromList = ttqAdmission?.warehouseTtqas?.length || 0;
    return Math.max(fromAdmission, fromList);
  }, [ttqAdmission]);

  const ttqNeedsProductResolve = useMemo(
    () => needsTtqProductResolve(ttqAdmission?.warehouseTtqas),
    [ttqAdmission],
  );

  const admissionScopedIpttqRecords = useMemo(() => {
    const records = ipttqBatch?.records || [];
    const warehouses = ttqAdmission?.warehouseTtqas || [];
    if (!records.length || !warehouses.length) return records;
    const allowed = new Set<string>();
    for (const w of warehouses) {
      const wid = String(w.id || '').trim();
      if (wid) allowed.add(wid);
      const pid = String(w.factoryMeta?.productId || '').trim();
      if (pid) allowed.add(pid);
    }
    if (!allowed.size) return records;
    const scoped = records.filter((r) => allowed.has(String(r.ttqaId || '').trim()));
    // If nothing matches (id scheme mismatch), keep original to avoid empty pane.
    return scoped.length ? scoped : records;
  }, [ipttqBatch?.records, ttqAdmission?.warehouseTtqas]);

  const condensedIpttqCount = useMemo(() => {
    const fromRecords = admissionScopedIpttqRecords.length;
    const fromWarehouse = Number(ttqAdmission?.warehouseTtqaCount)
      || (ttqAdmission?.warehouseTtqas?.length || 0);
    // After merge, prefer scoped list over stale ipttqCount (pre-merge product hydrate).
    if (fromRecords > 0) return fromRecords;
    if (fromWarehouse > 0) return fromWarehouse;
    return Number(ipttqBatch?.ipttqCount) || 0;
  }, [admissionScopedIpttqRecords, ipttqBatch?.ipttqCount, ttqAdmission]);

  const sortedIpttqRecords = useMemo(() => {
    const records = admissionScopedIpttqRecords;
    const defaultTarget = ipttqBatch?.targetBank || 5;
    return [...records].sort((a, b) => {
      const bankA = a.effectiveBankCount ?? a.bankParticleDrafts?.length ?? 0;
      const bankB = b.effectiveBankCount ?? b.bankParticleDrafts?.length ?? 0;
      const targetA = a.factoryMeta?.targetBank || defaultTarget;
      const targetB = b.factoryMeta?.targetBank || defaultTarget;
      const shortA = Math.max(0, targetA - bankA);
      const shortB = Math.max(0, targetB - bankB);
      const okA = a.factoryMeta?.bankOk === true && shortA === 0;
      const okB = b.factoryMeta?.bankOk === true && shortB === 0;
      if (okA !== okB) return okA ? 1 : -1;
      if (shortB !== shortA) return shortB - shortA;
      if (bankA !== bankB) return bankA - bankB;
      return String(a.skill?.mainTitle || '').localeCompare(String(b.skill?.mainTitle || ''), 'zh-CN');
    });
  }, [admissionScopedIpttqRecords, ipttqBatch?.targetBank]);

  const resolveIpttqProductId = useCallback((row: FactoryIpttqRecord): string => {
    const linkedTtqa = (ttqAdmission?.warehouseTtqas || []).find((w) => w.id === row.ttqaId);
    const extra = linkedTtqa as (FactoryWarehouseTtqa & { ipttqRecords?: Array<{ productId?: string }> }) | undefined;
    return String(
      linkedTtqa?.factoryMeta?.productId
      || row.ttqaId
      || extra?.ipttqRecords?.map((r) => r.productId).find(Boolean)
      || '',
    ).trim();
  }, [ttqAdmission?.warehouseTtqas]);

  const ipttqDetailProductId = useMemo(
    () => (ipttqDetail ? resolveIpttqProductId(ipttqDetail) : ''),
    [ipttqDetail, resolveIpttqProductId],
  );

  const syncIpttqBankStockStatus = useCallback(async (productId: string, rowTarget?: number) => {
    const status = await factoryService.getProductBankStockStatus(productId);
    setIpttqProductMeta(status.product.meta || null);
    setIpttqBankGen(mapBankStockRunState(productId, status, rowTarget));
    setIpttqDetail((prev) => (prev ? mergeBankStockIntoIpttqRow(prev, status) : prev));
    return status;
  }, []);

  const startIpttqBankGen = useCallback(async (productId: string) => {
    if (!productId || ipttqBankGenBusy) return;
    setIpttqBankGenBusy(true);
    try {
      await factoryService.startProductBankStock(productId);
      await syncIpttqBankStockStatus(
        productId,
        ipttqDetail ? resolveIpttqRowTarget(ipttqDetail) : undefined,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setIpttqBankGen({
        productId,
        status: 'failed',
        bankDone: ipttqDetail?.effectiveBankCount ?? ipttqDetail?.bankParticleDrafts?.length ?? 0,
        bankTarget: ipttqDetail?.factoryMeta?.targetBank || ipttqBatch?.targetBank || 5,
        inFlight: 0,
        timeouts: 0,
        skips: 0,
        usageTotal: 0,
        elapsedMs: 0,
        added: 0,
        error: message || '启动失败',
        phase: '',
        startedAt: '',
      });
    } finally {
      setIpttqBankGenBusy(false);
    }
  }, [
    ipttqBankGenBusy,
    ipttqBatch?.targetBank,
    ipttqDetail?.bankParticleDrafts?.length,
    ipttqDetail?.effectiveBankCount,
    ipttqDetail?.factoryMeta?.targetBank,
    ipttqDetail,
    syncIpttqBankStockStatus,
  ]);

  const pauseIpttqBankGen = useCallback(async (productId: string) => {
    if (!productId || ipttqBankPauseBusy) return;
    setIpttqBankPauseBusy(true);
    try {
      await factoryService.pauseProductBankStock(productId);
      await syncIpttqBankStockStatus(
        productId,
        ipttqDetail ? resolveIpttqRowTarget(ipttqDetail) : undefined,
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      showToast(message || '暂停失败', 3000);
    } finally {
      setIpttqBankPauseBusy(false);
    }
  }, [ipttqBankPauseBusy, ipttqDetail, showToast, syncIpttqBankStockStatus]);

  useEffect(() => {
    if (!ipttqDetail || !ipttqDetailProductId) {
      setIpttqBankGen(null);
      return;
    }
    let cancelled = false;
    void syncIpttqBankStockStatus(
      ipttqDetailProductId,
      resolveIpttqRowTarget(ipttqDetail),
    ).catch(() => {
      if (!cancelled) setIpttqBankGen(null);
    });
    return () => {
      cancelled = true;
    };
  }, [ipttqDetail?.id, ipttqDetailProductId, syncIpttqBankStockStatus]);

  useEffect(() => {
    ipttqBankLenRef.current = 0;
    setIpttqProductMeta(null);
  }, [ipttqDetail?.id]);

  useEffect(() => {
    const len = ipttqDetail?.bankParticleDrafts?.length ?? 0;
    const running = ipttqBankGen?.status === 'running';
    if (running && len > ipttqBankLenRef.current && ipttqDialogRef.current) {
      requestAnimationFrame(() => {
        ipttqDialogRef.current?.scrollTo({
          top: ipttqDialogRef.current.scrollHeight,
          behavior: 'smooth',
        });
      });
    }
    ipttqBankLenRef.current = len;
  }, [ipttqDetail?.bankParticleDrafts?.length, ipttqBankGen?.status]);

  useEffect(() => {
    if (!ipttqBankGen || ipttqBankGen.status !== 'running' || !ipttqBankGen.productId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const status = await syncIpttqBankStockStatus(
          ipttqBankGen.productId,
          ipttqDetail ? resolveIpttqRowTarget(ipttqDetail) : undefined,
        );
        if (cancelled) return;
        if (status.run?.status && status.run.status !== 'running') {
          void loadPhaseArtifacts();
        }
      } catch {
        /* keep polling */
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), 2000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [ipttqBankGen?.productId, ipttqBankGen?.status, syncIpttqBankStockStatus, loadPhaseArtifacts]);

  /** 品题推荐组数 > 已浓缩条数，或诱导库未达标 → 可「继续品题浓缩」 */
  const showContinueIpttq = phase === 'ipttq'
    && warehouseTtqaCount > 0
    && (
      warehouseTtqaCount > condensedIpttqCount
      || batch?.meta?.ipttqBankShortfall === true
      || (ipttqBatch?.records || []).some((r) => r.factoryMeta?.bankOk === false)
    );

  // Keep URL phase in the visible set (e.g. opened ?phase=structure on single-page).
  useEffect(() => {
    if (!batch) return;
    if (!visiblePhases.some((p) => p.id === phase)) {
      setPhase(visiblePhases[0]?.id || 'layout');
    }
  }, [batch, phase, visiblePhases, setPhase]);

  // Poll structural units while extract is in flight (preview only — not full FC blobs).
  const extractHasLive = useMemo(
    () => structuralUnits.some(
      (u) => u.extractStatus === 'running' || u.extractStatus === 'queued',
    ),
    [structuralUnits],
  );

  useEffect(() => {
    if (phase !== 'extract' || !batchId) return;
    let cancelled = false;
    const mergePreview = (structs: FactoryStructuralUnit[]) => {
      setStructuralUnits((prev) => {
        const rich = new Map(
          prev
            .filter((u) => u.enrichResult || u.extractResult)
            .map((u) => [u.id, u] as const),
        );
        if (!rich.size) return structs;
        return structs.map((u) => {
          const keep = rich.get(u.id);
          if (!keep) return u;
          return {
            ...u,
            enrichResult: keep.enrichResult,
            extractResult: keep.extractResult,
          };
        });
      });
    };
    const tick = async () => {
      try {
        const structs = await factoryService.listStructuralUnits(batchId, { view: 'preview' });
        if (!cancelled) mergePreview(structs);
      } catch {
        // ignore poll errors
      }
    };
    void tick();
    if (!extractHasLive) {
      return () => {
        cancelled = true;
      };
    }
    const timer = window.setInterval(() => {
      void tick();
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [phase, batchId, extractHasLive]);

  // Lazy-load semantic splits only when that phase is open.
  useEffect(() => {
    if (phase !== 'semantic_split' || !batchId) return;
    let cancelled = false;
    (async () => {
      try {
        const splits = await factoryService.listSemanticSplits(batchId);
        if (cancelled) return;
        setSemanticSplits(splits.pages || []);
        setSemanticSummary(splits.summary || null);
      } catch {
        if (!cancelled) {
          setSemanticSplits([]);
          setSemanticSummary(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, batchId]);

  // Auto-write 成品智能体仓 when 品题推荐已有但尚未 disposition（幂等；已绑定行会跳过）.
  useEffect(() => {
    if (phase !== 'ttq' || !batchId || !ttqNeedsProductResolve) return;
    if (ttqRerunBusy || ttqProductResolveBusy) return;
    if (ttqProductResolveAttemptedRef.current === batchId) return;
    ttqProductResolveAttemptedRef.current = batchId;
    let cancelled = false;
    (async () => {
      setTtqProductResolveBusy(true);
      try {
        const res = await factoryService.resolveTtqProducts(batchId);
        if (!cancelled && res.ttqAdmission) {
          setTtqAdmission(res.ttqAdmission);
        }
      } catch {
        if (!cancelled) ttqProductResolveAttemptedRef.current = null;
      } finally {
        if (!cancelled) setTtqProductResolveBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, batchId, ttqNeedsProductResolve, ttqRerunBusy, ttqProductResolveBusy]);

  // Poll AppFactory ttq job + admission while on 品题推荐 / 成品智能体.
  useEffect(() => {
    if (phase !== 'ttq' || !batchId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const [admission, detail] = await Promise.all([
          factoryService.getTtqAdmission(batchId),
          factoryService.getBatch(batchId).catch(() => null),
        ]);
        if (cancelled) return;
        if (admission?.ttqAdmission) {
          const next = admission.ttqAdmission;
          setTtqAdmission((prev) => {
            if (
              prev
              && prev.builtAt === next.builtAt
              && prev.warehouseTtqaCount === next.warehouseTtqaCount
              && (prev.warehouseTtqas || []).length === (next.warehouseTtqas || []).length
            ) {
              // Same stamp — keep referential equality to avoid topo remount / flicker.
              return prev;
            }
            // Don't let a stale poll undo an optimistic / just-completed manual merge.
            if (prev?.builtAt && next.builtAt) {
              const prevMs = Date.parse(prev.builtAt);
              const nextMs = Date.parse(next.builtAt);
              if (Number.isFinite(prevMs) && Number.isFinite(nextMs) && prevMs > nextMs) {
                return prev;
              }
            }
            if (
              ttqMergeQueueDepth > 0
              && prev
            ) {
              return prev;
            }
            return next;
          });
        }
        if (detail?.batch) {
          const nextBatch = detail.batch;
          setBatch((prev) => {
            if (
              prev
              && prev.updatedAt === nextBatch.updatedAt
              && prev.warehouseStatus === nextBatch.warehouseStatus
            ) {
              return prev;
            }
            return nextBatch;
          });
          const reportRaw = nextBatch.meta?.ttqSimilarityReadmitReport;
          if (reportRaw && typeof reportRaw === 'object' && !Array.isArray(reportRaw)) {
            setTtqReadmitReport(reportRaw as SimilarityReAdmitReport);
          }
        }
        const job = (detail?.jobs || []).find((j) => j.jobType === 'ttq');
        const status = String(job?.status || '');
        const label = String(job?.progressLabel || '').trim();
        if (status === 'running' || status === 'queued' || ttqRerunBusy) {
          setTtqJobProgressRaw(label);
          const sim = /^ttq_similarity_readmit:(\d+)\/(\d+)(?::([^·]+))?(?:·(.*))?/.exec(label);
          setTtqJobProgress(
            label.includes('product')
              ? '写入成品智能体仓…'
              : label.includes('title')
                ? '浓缩组标题…'
                : sim
                  ? `相似再入围 ${sim[1]}/${sim[2]}${sim[4] ? ` · ${sim[4]}` : '…'}`
                  : label.includes('cluster')
                    ? '入围聚类分组…'
                    : label
                      || (status === 'queued' ? '排队中…' : '品题推荐进行中…'),
          );
        } else if (!ttqRerunBusy) {
          setTtqJobProgress('');
          setTtqJobProgressRaw('');
        }
      } catch {
        // ignore
      }
    };
    void tick();
    // Idle products view: slow poll. In-flight / resolve: keep 4s.
    const ms = (ttqRerunBusy || ttqProductResolveBusy || ttqMergeQueueDepth > 0 || phase === 'ttq') ? 4000 : 12000;
    const timer = window.setInterval(() => {
      void tick();
    }, ms);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [phase, batchId, ttqRerunBusy, ttqProductResolveBusy, ttqMergeQueueDepth]);

  // Poll AppFactory ipttq meta + job label while on 品题浓缩 (never toast; never FC).
  useEffect(() => {
    if (phase !== 'ipttq' || !batchId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const [ipttq, detail] = await Promise.all([
          factoryService.getIpttq(batchId),
          factoryService.getBatch(batchId).catch(() => null),
        ]);
        if (cancelled) return;
        setIpttqBatch(ipttq?.ipttq || null);
        const job = (detail?.jobs || []).find((j) => j.jobType === 'ipttq');
        const status = String(job?.status || '');
        const label = String(job?.progressLabel || '');
        if (status === 'running' || status === 'queued' || ipttqRerunBusy) {
          setIpttqJobProgress(formatIpttqProgressLabel(label) || (status === 'queued' ? '排队中…' : '进行中…'));
        } else if (!ipttqRerunBusy) {
          setIpttqJobProgress('');
        }
      } catch {
        // ignore poll errors
      }
    };
    void tick();
    const timer = window.setInterval(() => {
      void tick();
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [phase, batchId, ipttqRerunBusy]);

  const currentLayoutUnit = layoutUnits[activeUnit] || null;
  const fallbackAsset = assets[0] || null;
  const pageStart = Number(currentLayoutUnit?.pageStart ?? 0);
  const pageEnd = Number(currentLayoutUnit?.pageEnd ?? pageStart);

  useEffect(() => {
    if (!currentLayoutUnit || (phase !== 'layout' && phase !== 'semantic_split')) {
      if ((phase === 'layout' || phase === 'semantic_split') && !currentLayoutUnit) {
        setContentItems([]);
        setLayoutMissing(true);
      }
      return;
    }
    let cancelled = false;
    setSelectedItem(undefined);
    setSelectionSource(null);
    setLayoutError('');
    (async () => {
      try {
        const res = await factoryService.getLayoutUnitContent(batchId, currentLayoutUnit.id);
        if (cancelled) return;
        setLayoutMissing(Boolean(res.missing));
        setContentItems(
          (res.contentItems || []).map((it, idx) => ({
            ...it,
            text: String(it.text || ''),
            type: String(it.type || 'text'),
            page_idx: Number(it.page_idx ?? 0),
            bbox: it.bbox as [number, number, number, number] | undefined,
            img_path: it.img_path ? String(it.img_path) : undefined,
            _originalIndex: Number(it._originalIndex ?? it._globalIndex ?? idx),
          })),
        );
      } catch (err) {
        if (!cancelled) {
          setContentItems([]);
          setLayoutMissing(true);
          setLayoutError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [batchId, currentLayoutUnit?.id, phase]);

  const structureForUnit = useMemo(() => {
    // 只保留当前任务 batch 的结构单元（防御串数据）
    const scoped = structuralUnits.filter((s) => {
      const bid = String(s.batchId || '').trim();
      return !bid || bid === batchId;
    });
    // 与颗粒提取同一分册范围，避免插图台混入其它分册页
    if (!currentLayoutUnit) return scoped;
    const linked = scoped.filter((s) => s.layoutUnitId === currentLayoutUnit.id);
    return linked.length ? linked : scoped;
  }, [structuralUnits, currentLayoutUnit, batchId]);

  useEffect(() => {
    setExtractVisibleCount(EXTRACT_PAGE_SIZE);
  }, [batchId, phase]);

  useEffect(() => {
    if (phase !== 'extract' || selectedItem == null) return;
    const need = Math.floor(selectedItem / 10000) + 1;
    if (need > extractVisibleCount) {
      setExtractVisibleCount(Math.ceil(need / EXTRACT_PAGE_SIZE) * EXTRACT_PAGE_SIZE);
    }
  }, [phase, selectedItem, extractVisibleCount]);

  const extractUnitsWindow = useMemo(() => {
    if (phase !== 'extract') return structureForUnit;
    return structureForUnit.slice(0, extractVisibleCount);
  }, [phase, structureForUnit, extractVisibleCount]);

  const extractFailedUnits = useMemo(() => {
    if (phase !== 'extract') return [] as Array<{
      suIdx: number;
      unitIndex: number;
      reason: string;
    }>;
    return structureForUnit
      .map((su, suIdx) => ({ su, suIdx }))
      .filter(({ su }) => structuralUnitLooksExtractFailed(su))
      .map(({ su, suIdx }) => ({
        suIdx,
        unitIndex: Number(su.unitIndex) || suIdx,
        reason: formatUnitFcError(su.extractError) || '提取失败（无详细原因）',
      }));
  }, [phase, structureForUnit]);

  /** Units whose enrich snapshot still lacks Runtime profiles (stale / profiles_empty). */
  const enrichProfileGapUnits = useMemo(() => {
    if (phase !== 'extract') {
      return [] as Array<{ suIdx: number; unitIndex: number; unitId?: string; reason: string }>;
    }
    return structureForUnit
      .map((su, suIdx) => ({ su, suIdx }))
      .filter(({ su }) => {
        if (structuralUnitLooksExtractFailed(su)) return false;
        const warnings = Array.isArray(su.enrichSummary?.contextWarnings)
          ? su.enrichSummary!.contextWarnings!
          : [];
        if (warnings.some((w) => {
          const s = String(w);
          return s.includes('runtime_admin_token_missing') || s.includes('profiles_empty');
        })) {
          return true;
        }
        if (su.enrichSummary?.profilesProvided === false) return true;
        const particles = su.enrichResult?.particles;
        if (Array.isArray(particles) && particles.length) {
          return particles.some((p) => {
            const rfe = p?.rfe && typeof p.rfe === 'object' ? p.rfe as Record<string, unknown> : null;
            const binding = rfe?.subjectValueBinding && typeof rfe.subjectValueBinding === 'object'
              ? rfe.subjectValueBinding as Record<string, unknown>
              : null;
            const reason = String(binding?.reason || rfe?.error || '');
            const status = String(binding?.status || rfe?.status || '');
            return reason.includes('profiles_empty')
              || (status === 'pending' && reason.includes('profile'));
          });
        }
        return false;
      })
      .map(({ su, suIdx }) => {
        const warnings = Array.isArray(su.enrichSummary?.contextWarnings)
          ? su.enrichSummary!.contextWarnings!
          : [];
        let reason = '缺 Runtime 学科画像';
        if (warnings.some((w) => String(w).includes('runtime_admin_token_missing'))) {
          reason = '缺 ADMIN token · 画像未加载';
        } else if (su.enrichSummary?.profilesProvided === false) {
          reason = 'profilesProvided=false · 旧缺画像快照';
        } else if (warnings.some((w) => String(w).includes('profiles_empty'))) {
          reason = 'profiles_empty';
        }
        return {
          suIdx,
          unitIndex: Number(su.unitIndex) || suIdx,
          unitId: su.id,
          reason,
        };
      });
  }, [phase, structureForUnit]);

  const onFlushAllProfiles = useCallback(async () => {
    if (!batchId || flushProfilesBusy || reextractAllBusy) return;
    if (batch && isFactoryBatchOwnerAccepted(batch)) {
      showToast('已验收，不可再改富化。', 2200);
      return;
    }
    const ids = enrichProfileGapUnits.map((row) => row.unitId).filter(Boolean);
    if (!ids.length) return;
    if (!window.confirm(`重刷全部缺画像？将保留已提取题干，对 ${ids.length} 页重跑 RFE/PAP。`)) {
      return;
    }
    setFlushProfilesBusy(true);
    showToast(`开始重刷 ${ids.length} 页画像…`, 2200);
    const queue = [...ids];
    let okN = 0;
    let failN = 0;
    const runWorker = async () => {
      while (queue.length) {
        const unitId = queue.shift();
        if (!unitId) break;
        setReenricingUnitIds((prev) => new Set(prev).add(unitId));
        try {
          const result = await factoryService.reenrichStructuralUnit(batchId, unitId);
          if (result.ok) okN += 1;
          else failN += 1;
          if (result.unit) {
            setStructuralUnits((prev) =>
              prev.map((u) =>
                (u.id === unitId
                  ? {
                      ...u,
                      ...result.unit,
                      extractQa: u.extractQa?.length ? u.extractQa : result.unit?.extractQa,
                      view: 'full',
                    }
                  : u)),
            );
          }
        } catch {
          failN += 1;
        } finally {
          setReenricingUnitIds((prev) => {
            const next = new Set(prev);
            next.delete(unitId);
            return next;
          });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(2, ids.length) }, () => runWorker()));
    try {
      const structs = await factoryService.listStructuralUnits(batchId, { view: 'preview' });
      setStructuralUnits(structs);
    } catch {
      /* keep in-place patches */
    }
    showToast(
      failN > 0 ? `画像重刷完成：成功 ${okN}，失败 ${failN}` : `已重刷 ${okN} 页画像`,
      failN > 0 ? 4500 : 2800,
    );
    setFlushProfilesBusy(false);
  }, [
    batch,
    batchId,
    enrichProfileGapUnits,
    flushProfilesBusy,
    reextractAllBusy,
    showToast,
  ]);

  const extractWarnUnitCount = extractFailedUnits.length + enrichProfileGapUnits.length;

  /** Systemic enrich gap: Runtime ADMIN token missing → profiles_empty on RFE. */
  const enrichRuntimeAdminGap = useMemo(() => {
    if (phase !== 'extract') return null as null | 'token' | 'profiles';
    for (const su of structureForUnit) {
      const warnings = Array.isArray(su.enrichSummary?.contextWarnings)
        ? su.enrichSummary!.contextWarnings!
        : [];
      if (warnings.some((w) => String(w).includes('runtime_admin_token_missing'))) {
        return 'token';
      }
      if (su.enrichSummary?.profilesProvided === false) {
        return 'profiles';
      }
    }
    return null;
  }, [phase, structureForUnit]);

  const [enrichSettingsHasToken, setEnrichSettingsHasToken] = useState<boolean | null>(null);
  useEffect(() => {
    if (phase !== 'extract') return;
    let cancelled = false;
    factoryService
      .getEnrichSettings()
      .then((res) => {
        if (!cancelled) setEnrichSettingsHasToken(Boolean(res.hasAdminToken));
      })
      .catch(() => {
        if (!cancelled) setEnrichSettingsHasToken(null);
      });
    return () => {
      cancelled = true;
    };
  }, [phase, batchId]);

  const showRuntimeAdminBanner =
    phase === 'extract'
    && (enrichSettingsHasToken === false
      || (enrichSettingsHasToken !== true
        && (enrichRuntimeAdminGap === 'token' || enrichRuntimeAdminGap === 'profiles'))
      || (enrichSettingsHasToken === true && enrichRuntimeAdminGap != null));

  useEffect(() => {
    if (!unitMoreMenuId) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.(`[data-unit-more="${unitMoreMenuId}"]`)) return;
      setUnitMoreMenuId(null);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [unitMoreMenuId]);

  useEffect(() => {
    if (phase !== 'extract') setUnitMoreMenuId(null);
  }, [phase]);

  useEffect(() => {
    if (!extractFailMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      const root = extractFailMenuRef.current;
      if (root && !root.contains(e.target as Node)) {
        setExtractFailMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [extractFailMenuOpen]);

  useEffect(() => {
    if (!extractFailMenuOpen) return;
    const root = extractFailMenuRef.current;
    const active = root?.querySelector('.compare-extract-fail-item.is-active');
    if (active instanceof HTMLElement) {
      active.scrollIntoView({ block: 'nearest' });
    }
  }, [extractFailMenuOpen, selectedItem]);

  useEffect(() => {
    if (phase !== 'extract') setExtractFailMenuOpen(false);
  }, [phase]);

  useEffect(() => {
    if (!warnExtract || phase !== 'extract') return;
    // Wait until extract units have a chance to load.
    if (loading) return;
    if (extractWarnUnitCount > 0) setExtractFailMenuOpen(true);
    const sp = new URLSearchParams(searchParams);
    if (!sp.has('warn')) return;
    sp.delete('warn');
    setSearchParams(sp, { replace: true });
  }, [
    warnExtract,
    phase,
    loading,
    extractWarnUnitCount,
    searchParams,
    setSearchParams,
  ]);

  const structureLenRef = useRef(structureForUnit.length);
  structureLenRef.current = structureForUnit.length;

  const loadMoreExtractPages = useCallback(() => {
    setExtractVisibleCount((n) => {
      const total = structureLenRef.current;
      if (n >= total) return n;
      return Math.min(total, n + EXTRACT_PAGE_SIZE);
    });
  }, []);

  /** Extract infinite scroll: IO + scroll fallback + chain-load while sentinel stays in view. */
  useEffect(() => {
    if (phase !== 'extract') return;
    const root = itemScrollRef.current;
    if (!root) return;
    if (extractVisibleCount >= structureForUnit.length) return;

    const sentinelNearViewport = () => {
      const el = extractLoadMoreRef.current;
      if (!el) return false;
      const rootRect = root.getBoundingClientRect();
      const elRect = el.getBoundingClientRect();
      return elRect.top <= rootRect.bottom + 160;
    };

    const tryLoad = () => {
      if (!sentinelNearViewport()) return;
      loadMoreExtractPages();
    };

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        loadMoreExtractPages();
      },
      { root, rootMargin: '160px 0px', threshold: 0 },
    );

    const el = extractLoadMoreRef.current;
    if (el) obs.observe(el);

    root.addEventListener('scroll', tryLoad, { passive: true });
    const raf = window.requestAnimationFrame(tryLoad);
    const t = window.setTimeout(tryLoad, 120);

    return () => {
      obs.disconnect();
      root.removeEventListener('scroll', tryLoad);
      window.cancelAnimationFrame(raf);
      window.clearTimeout(t);
    };
  }, [phase, structureForUnit.length, extractVisibleCount, loadMoreExtractPages]);

  const ensureStructuralUnitDetail = useCallback(
    async (unitId: string) => {
      const cur = structuralUnits.find((u) => u.id === unitId);
      if (cur?.view === 'full') return cur;
      if (!batchId || enrichLoadingRef.current.has(unitId)) return cur || null;
      enrichLoadingRef.current.add(unitId);
      setEnrichLoadingIds(new Set(enrichLoadingRef.current));
      try {
        const full = await factoryService.getStructuralUnit(batchId, unitId);
        setStructuralUnits((prev) =>
          prev.map((u) =>
            (u.id === unitId
              ? {
                  ...u,
                  ...full,
                  // Prefer full extractQa (includes figure_box); fall back to preview list.
                  extractQa: full.extractQa?.length ? full.extractQa : u.extractQa,
                  view: 'full',
                }
              : u)),
        );
        return full;
      } catch {
        return cur || null;
      } finally {
        enrichLoadingRef.current.delete(unitId);
        setEnrichLoadingIds(new Set(enrichLoadingRef.current));
      }
    },
    [batchId, structuralUnits],
  );

  const figureBearingPreviews = useMemo((): FigureCropPreview[] => {
    const out: FigureCropPreview[] = [];
    for (const su of structureForUnit) {
      if (su.extractIgnored || su.extractStatus === 'ignored') continue;
      const qaList = structuralUnitExtractQa(su);
      qaList.forEach((qa, qi) => {
        if (!isConfirmedPrintedFigureQa(qa)) return;
        out.push({
          unitId: su.id,
          qaIndex: qi,
          questionPreview: qa.question || '',
          figureKind: qa.figureKind,
          figureNote: qa.figureNote,
          figureBox: qa.figureBox,
        });
      });
    }
    return out;
  }, [structureForUnit]);

  const openFigureCropPreview = useCallback(
    async (seed: FigureCropPreview) => {
      let box = seed.figureBox;
      let note = seed.figureNote;
      let kind = seed.figureKind;
      if (!box || !(box.w > 0 || box.h > 0)) {
        const full = await ensureStructuralUnitDetail(seed.unitId);
        if (full) {
          const rows = structuralUnitExtractQa(full);
          const row = rows[seed.qaIndex];
          if (row?.figureBox) box = row.figureBox;
          if (row?.figureNote) note = row.figureNote;
          if (row?.figureKind) kind = row.figureKind;
        }
      }
      setFigureCropPreview({
        unitId: seed.unitId,
        qaIndex: seed.qaIndex,
        questionPreview: seed.questionPreview,
        figureKind: kind,
        figureNote: note,
        figureBox: box,
      });
    },
    [ensureStructuralUnitDetail],
  );

  const figureCropNavIndex = useMemo(() => {
    if (!figureCropPreview) return -1;
    return figureBearingPreviews.findIndex(
      (item) => item.unitId === figureCropPreview.unitId && item.qaIndex === figureCropPreview.qaIndex,
    );
  }, [figureCropPreview, figureBearingPreviews]);

  const structurePdfItems = useMemo((): PdfContentItem[] => {
    const out: PdfContentItem[] = [];
    structureForUnit.forEach((su, suIdx) => {
      const regions = su.regions?.length
        ? su.regions
        : (su.contentItems || []).map((c) => ({
            page_idx: c.page_idx,
            bbox: c.bbox,
            type: c.type,
            text: c.text,
            _originalIndex: c._originalIndex,
          }));
      const spanPages = Array.isArray(su.pageSpan)
        ? (su.pageSpan as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
        : [];
      if (regions.length) {
        regions.forEach((r, ri) => {
          out.push({
            type: r.type || 'text',
            text: r.text || su.textPreview,
            bbox: r.bbox,
            page_idx: r.page_idx ?? spanPages[0] ?? 0,
            _originalIndex: suIdx * 10000 + ri,
            _globalIndex: suIdx,
          });
        });
      } else {
        // Page-direct extract units have no MinerU regions — still need a page scroll anchor.
        const pageIdx = spanPages[0] ?? 0;
        out.push({
          type: 'structural_unit',
          text: su.textPreview || `单元 #${su.unitIndex + 1}`,
          page_idx: pageIdx,
          _originalIndex: suIdx * 10000,
          _globalIndex: suIdx,
        });
      }
    });
    return out;
  }, [structureForUnit]);

  const selectedUnitPageIdx = useMemo(() => {
    if (!UNIT_RESULT_PHASES.includes(phase) || selectedItem == null) return null;
    const suIdx = Math.floor(selectedItem / 10000);
    const su = structureForUnit[suIdx];
    if (!su) return null;
    const pages = Array.isArray(su.pageSpan)
      ? (su.pageSpan as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
      : [];
    if (pages.length) return pages[0]!;
    const fromItems = structurePdfItems.find((it) => it._globalIndex === suIdx);
    return fromItems?.page_idx != null ? Number(fromItems.page_idx) : null;
  }, [phase, selectedItem, structureForUnit, structurePdfItems]);

  const pdfItems =
    phase === 'layout' || phase === 'semantic_split' ? contentItems : structurePdfItems;

  const pdfSplitLines = useMemo((): PdfSplitLine[] => {
    if (phase !== 'semantic_split') return [];
    const out: PdfSplitLine[] = [];
    for (const page of semanticSplits) {
      for (const line of page.lines || []) {
        out.push({
          page_idx: page.pageIndex,
          y_norm: Number(line.y_norm),
          label: line.reason || undefined,
        });
      }
    }
    return out;
  }, [phase, semanticSplits]);

  const highlightedForUnit = useMemo(() => {
    if (!UNIT_RESULT_PHASES.includes(phase) || selectedItem == null) return undefined;
    const suIdx = Math.floor(selectedItem / 10000);
    return structurePdfItems
      .filter((it) => it._globalIndex === suIdx)
      .map((it) => Number(it._originalIndex));
  }, [phase, selectedItem, structurePdfItems]);

  const pdfUrl = currentLayoutUnit
    ? factoryService.layoutUnitPdfUrl(batchId, currentLayoutUnit.id)
    : fallbackAsset
      ? factoryService.assetPdfUrl(batchId, fallbackAsset.id)
      : '';

  const previewStructuralUnit = useMemo(() => {
    if (!structureForUnit.length) return null as FactoryStructuralUnit | null;
    if (UNIT_RESULT_PHASES.includes(phase) && selectedItem != null) {
      const suIdx = Math.floor(selectedItem / 10000);
      return structureForUnit[suIdx] || structureForUnit[0] || null;
    }
    if (selectedUnitPageIdx != null) {
      const match = structureForUnit.find((su) => {
        const pages = Array.isArray(su.pageSpan)
          ? (su.pageSpan as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
          : [];
        return pages.includes(selectedUnitPageIdx);
      });
      if (match) return match;
    }
    return structureForUnit[0] || null;
  }, [structureForUnit, phase, selectedItem, selectedUnitPageIdx]);

  const pageImageUrl =
    previewStructuralUnit
      ? factoryService.structuralUnitPageImageUrl(batchId, previewStructuralUnit.id)
      : '';

  useEffect(() => {
    setPageImageMeta(null);
    setPageImageInfoOpen(false);
  }, [pageImageUrl]);

  useEffect(() => {
    if (sourceViewMode !== 'image') setPageImageInfoOpen(false);
  }, [sourceViewMode]);

  useEffect(() => {
    if (!pageImageInfoOpen) return;
    const onDown = (e: MouseEvent) => {
      const root = pageImageInfoRef.current;
      if (root && !root.contains(e.target as Node)) {
        setPageImageInfoOpen(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [pageImageInfoOpen]);

  const visibleItems = useMemo(
    () =>
      contentItems.filter(
        (item) => !HIDDEN.includes(String(item.type || '').toLowerCase()),
      ),
    [contentItems],
  );

  useEffect(() => {
    if (selectionSource !== 'bbox' && selectionSource !== 'pdf') return;
    if (selectedItem == null) return;
    const key =
      UNIT_RESULT_PHASES.includes(phase) ? Math.floor(selectedItem / 10000) * 10000 : selectedItem;
    const el = itemRefs.current.get(key) || itemRefs.current.get(selectedItem);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedItem, selectionSource, phase]);

  const requestPdfScrollToSelection = useCallback(() => {
    setPdfScrollRequestId((n) => n + 1);
  }, []);

  const focusExtractUnit = useCallback(
    (suIdx: number, qaIndex: number | null = null) => {
      if (suIdx < 0 || suIdx >= structureForUnit.length) return;
      const su = structureForUnit[suIdx];
      if (!su) return;
      setExtractFailMenuOpen(false);
      pendingFocusUnitRef.current = suIdx;
      const need = suIdx + 1;
      setExtractVisibleCount((n) =>
        Math.max(n, Math.ceil(need / EXTRACT_PAGE_SIZE) * EXTRACT_PAGE_SIZE),
      );
      setSelectionSource('list');
      setSelectedItem(suIdx * 10000);
      let nextQa = qaIndex;
      if (nextQa == null) {
        const qaList = structuralUnitExtractQa(su);
        const figIdx = qaList.findIndex((qa) => isConfirmedPrintedFigureQa(qa));
        nextQa = figIdx >= 0 ? figIdx : null;
      }
      setParticleRoute(su.id, nextQa);
      requestPdfScrollToSelection();
    },
    [structureForUnit, requestPdfScrollToSelection, setParticleRoute],
  );

  /** 路由 unit/qa → 颗粒提取列表定位（与插图提取对照） */
  const lastParticleRouteFocusRef = useRef('');
  useEffect(() => {
    if (phase !== 'extract' || !routeUnitId) return;
    const suIdx = structureForUnit.findIndex((u) => u.id === routeUnitId);
    if (suIdx < 0) return;
    const need = suIdx + 1;
    setExtractVisibleCount((n) =>
      Math.max(n, Math.ceil(need / EXTRACT_PAGE_SIZE) * EXTRACT_PAGE_SIZE),
    );
    setSelectedItem((prev) => {
      const next = suIdx * 10000;
      return prev === next ? prev : next;
    });
    const focusSig = `${routeUnitId}:${routeQaIndex ?? ''}`;
    if (lastParticleRouteFocusRef.current === focusSig) return;
    lastParticleRouteFocusRef.current = focusSig;
    const qaKey = `${routeUnitId}:${routeQaIndex ?? 0}`;
    const t = window.setTimeout(() => {
      const qaEl = qaItemRefs.current.get(qaKey);
      if (qaEl) {
        qaEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        return;
      }
      itemRefs.current.get(suIdx * 10000)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 80);
    return () => window.clearTimeout(t);
  }, [phase, routeUnitId, routeQaIndex, structureForUnit]);

  const onSelectFigureParticle = useCallback(
    (unitId: string, qaIndex: number) => {
      setParticleRoute(unitId, qaIndex);
    },
    [setParticleRoute],
  );

  /** Image mode: click page image → select + scroll right-pane unit (mirrors PDF page sync). */
  const onPageImageActivate = useCallback(() => {
    if (!previewStructuralUnit) return;
    const suIdx = structureForUnit.findIndex((u) => u.id === previewStructuralUnit.id);
    if (suIdx < 0) return;
    const key = suIdx * 10000;
    const scrollRight = () => {
      itemRefs.current.get(key)?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    };
    if (phase === 'extract') {
      focusExtractUnit(suIdx);
      setSelectionSource('pdf');
      window.setTimeout(scrollRight, 80);
      return;
    }
    if (UNIT_RESULT_PHASES.includes(phase)) {
      setSelectionSource('pdf');
      setSelectedItem(key);
      window.setTimeout(scrollRight, 40);
    }
  }, [previewStructuralUnit, structureForUnit, phase, focusExtractUnit]);

  const previewUnitIdx = useMemo(() => {
    if (!previewStructuralUnit) return -1;
    return structureForUnit.findIndex((u) => u.id === previewStructuralUnit.id);
  }, [previewStructuralUnit, structureForUnit]);

  const canPageImagePrev = previewUnitIdx > 0;
  const canPageImageNext =
    previewUnitIdx >= 0 && previewUnitIdx < structureForUnit.length - 1;

  const PAGE_IMAGE_EDGE_CTA = 44;
  const PAGE_IMAGE_EDGE_LATCH = 18;
  const PAGE_IMAGE_EDGE_HIDE_MS = 3000;

  const pageImageScrollRef = useRef<HTMLDivElement | null>(null);
  /** Callback ref so wheel/pointer listeners attach when the scroll node mounts (after authToken). */
  const [pageImageScrollEl, setPageImageScrollEl] = useState<HTMLDivElement | null>(null);
  const bindPageImageScrollEl = useCallback((node: HTMLDivElement | null) => {
    pageImageScrollRef.current = node;
    setPageImageScrollEl((prev) => (prev === node ? prev : node));
  }, []);
  const pageImageEdgeTimerRef = useRef<number | null>(null);
  const pageImageWheelIdleRef = useRef<number | null>(null);
  const pageImageEdgeRef = useRef({
    prevH: 0,
    nextH: 0,
    pulling: false,
    pointerId: -1,
    startY: 0,
    mode: null as 'prev' | 'next' | null,
  });
  const [pageImagePrevH, setPageImagePrevH] = useState(0);
  const [pageImageNextH, setPageImageNextH] = useState(0);
  const [pageImageEdgePulling, setPageImageEdgePulling] = useState(false);
  const [pageImageEdgeLatched, setPageImageEdgeLatched] = useState<'prev' | 'next' | null>(
    null,
  );

  const clearPageImageEdgeTimers = useCallback(() => {
    if (pageImageEdgeTimerRef.current != null) {
      window.clearTimeout(pageImageEdgeTimerRef.current);
      pageImageEdgeTimerRef.current = null;
    }
    if (pageImageWheelIdleRef.current != null) {
      window.clearTimeout(pageImageWheelIdleRef.current);
      pageImageWheelIdleRef.current = null;
    }
  }, []);

  const setPageImageEdgeHeights = useCallback((prevH: number, nextH: number) => {
    pageImageEdgeRef.current.prevH = prevH;
    pageImageEdgeRef.current.nextH = nextH;
    setPageImagePrevH(prevH);
    setPageImageNextH(nextH);
  }, []);

  const dismissPageImageEdge = useCallback(() => {
    clearPageImageEdgeTimers();
    pageImageEdgeRef.current.pulling = false;
    pageImageEdgeRef.current.mode = null;
    setPageImageEdgePulling(false);
    setPageImageEdgeLatched(null);
    setPageImageEdgeHeights(0, 0);
  }, [clearPageImageEdgeTimers, setPageImageEdgeHeights]);

  const latchPageImageEdge = useCallback(
    (dir: 'prev' | 'next') => {
      clearPageImageEdgeTimers();
      pageImageEdgeRef.current.pulling = false;
      setPageImageEdgePulling(false);
      setPageImageEdgeLatched(dir);
      if (dir === 'prev') setPageImageEdgeHeights(PAGE_IMAGE_EDGE_CTA, 0);
      else setPageImageEdgeHeights(0, PAGE_IMAGE_EDGE_CTA);
      pageImageEdgeTimerRef.current = window.setTimeout(() => {
        pageImageEdgeTimerRef.current = null;
        setPageImageEdgeLatched(null);
        setPageImageEdgeHeights(0, 0);
      }, PAGE_IMAGE_EDGE_HIDE_MS);
    },
    [clearPageImageEdgeTimers, setPageImageEdgeHeights],
  );

  const settlePageImageEdge = useCallback(
    (dir: 'prev' | 'next') => {
      const h = dir === 'prev' ? pageImageEdgeRef.current.prevH : pageImageEdgeRef.current.nextH;
      pageImageEdgeRef.current.pulling = false;
      setPageImageEdgePulling(false);
      if (h >= PAGE_IMAGE_EDGE_LATCH) latchPageImageEdge(dir);
      else dismissPageImageEdge();
    },
    [latchPageImageEdge, dismissPageImageEdge],
  );

  const goPageImageAdjacent = useCallback(
    (dir: -1 | 1) => {
      const next = previewUnitIdx + dir;
      if (next < 0 || next >= structureForUnit.length) return;
      dismissPageImageEdge();
      if (phase === 'extract') {
        focusExtractUnit(next);
        setSelectionSource('pdf');
        return;
      }
      if (UNIT_RESULT_PHASES.includes(phase)) {
        const key = next * 10000;
        setSelectionSource('pdf');
        setSelectedItem(key);
        window.setTimeout(() => {
          itemRefs.current.get(key)?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 40);
      }
    },
    [previewUnitIdx, structureForUnit.length, phase, focusExtractUnit, dismissPageImageEdge],
  );

  useEffect(() => {
    dismissPageImageEdge();
  }, [pageImageUrl, dismissPageImageEdge]);

  useEffect(() => () => clearPageImageEdgeTimers(), [clearPageImageEdgeTimers]);

  // Pull-to-reveal at scroll edges: wheel overscroll + pointer drag.
  useEffect(() => {
    const el = pageImageScrollEl;
    if (!el) return;

    const readEdge = () => {
      const { scrollTop, clientHeight, scrollHeight } = el;
      return {
        atTop: scrollTop <= 1,
        atBottom: scrollTop + clientHeight >= scrollHeight - 1,
      };
    };

    const onWheel = (e: WheelEvent) => {
      const { atTop, atBottom } = readEdge();
      const pullingPrev = atTop && e.deltaY < 0;
      const pullingNext = atBottom && e.deltaY > 0;
      if (!pullingPrev && !pullingNext) return;
      e.preventDefault();
      clearPageImageEdgeTimers();
      setPageImageEdgeLatched(null);
      pageImageEdgeRef.current.pulling = true;
      setPageImageEdgePulling(true);
      if (pullingPrev) {
        const next = Math.min(
          PAGE_IMAGE_EDGE_CTA,
          pageImageEdgeRef.current.prevH + Math.min(28, -e.deltaY * 0.45),
        );
        setPageImageEdgeHeights(next, 0);
        pageImageWheelIdleRef.current = window.setTimeout(() => settlePageImageEdge('prev'), 140);
      } else {
        const next = Math.min(
          PAGE_IMAGE_EDGE_CTA,
          pageImageEdgeRef.current.nextH + Math.min(28, e.deltaY * 0.45),
        );
        setPageImageEdgeHeights(0, next);
        pageImageWheelIdleRef.current = window.setTimeout(() => settlePageImageEdge('next'), 140);
      }
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const { atTop, atBottom } = readEdge();
      // Allow starting a pull only from an edge (or fully fitted content).
      if (!atTop && !atBottom) return;
      pageImageEdgeRef.current.pointerId = e.pointerId;
      pageImageEdgeRef.current.startY = e.clientY;
      pageImageEdgeRef.current.mode = null;
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      const st = pageImageEdgeRef.current;
      if (st.pointerId !== e.pointerId) return;
      const dy = e.clientY - st.startY;
      const { atTop, atBottom } = readEdge();
      if (st.mode == null) {
        if (dy > 8 && atTop) st.mode = 'prev';
        else if (dy < -8 && atBottom) st.mode = 'next';
        else return;
        clearPageImageEdgeTimers();
        setPageImageEdgeLatched(null);
        st.pulling = true;
        setPageImageEdgePulling(true);
      }
      e.preventDefault();
      if (st.mode === 'prev') {
        const amount = Math.max(0, Math.min(PAGE_IMAGE_EDGE_CTA, dy));
        setPageImageEdgeHeights(amount, 0);
      } else if (st.mode === 'next') {
        const amount = Math.max(0, Math.min(PAGE_IMAGE_EDGE_CTA, -dy));
        setPageImageEdgeHeights(0, amount);
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      const st = pageImageEdgeRef.current;
      if (st.pointerId !== e.pointerId) return;
      const mode = st.mode;
      st.pointerId = -1;
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (mode) settlePageImageEdge(mode);
      else {
        st.mode = null;
      }
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, [
    pageImageScrollEl,
    pageImageUrl,
    authToken,
    clearPageImageEdgeTimers,
    setPageImageEdgeHeights,
    settlePageImageEdge,
  ]);

  const pageImageShiftY = pageImagePrevH - pageImageNextH;

  useEffect(() => {
    const suIdx = pendingFocusUnitRef.current;
    if (suIdx == null || phase !== 'extract') return;
    if (extractVisibleCount < suIdx + 1) return;
    pendingFocusUnitRef.current = null;
    const key = suIdx * 10000;
    const t = window.setTimeout(() => {
      itemRefs.current.get(key)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 60);
    return () => window.clearTimeout(t);
  }, [phase, selectedItem, extractVisibleCount]);

  const onPdfActivePageChange = useCallback(
    (pageIdx: number) => {
      if (!UNIT_RESULT_PHASES.includes(phase)) return;
      const suIdx = structureForUnit.findIndex((su) => {
        const pages = Array.isArray(su.pageSpan)
          ? (su.pageSpan as unknown[]).map((n) => Number(n)).filter((n) => Number.isFinite(n))
          : [];
        return pages.includes(pageIdx);
      });
      if (suIdx < 0) return;
      const next = suIdx * 10000;
      setSelectionSource('pdf');
      setSelectedItem((prev) => (prev === next ? prev : next));
    },
    [phase, structureForUnit],
  );

  useEffect(() => {
    const el = itemScrollRef.current;
    if (!el) return;
    const snap = () => {
      if (scrollAnchor === 'end') el.scrollTop = el.scrollHeight;
      else el.scrollTop = 0;
    };
    snap();
    const t = window.setTimeout(snap, 80);
    return () => window.clearTimeout(t);
  }, [currentLayoutUnit?.id, scrollAnchor]);

  const handleEdgeNavigate = useCallback(
    (direction: 'prev' | 'next') => {
      if (!layoutUnits.length) {
        showToast('尚无版面分册');
        return;
      }
      if (direction === 'prev') {
        if (activeUnit <= 0) {
          showToast('已到第一分册');
          return;
        }
        setScrollAnchor('end');
        setActiveUnit((i) => Math.max(0, i - 1));
        setSelectedItem(undefined);
        setSelectionSource(null);
        return;
      }
      if (activeUnit >= layoutUnits.length - 1) {
        showToast('已到最后分册');
        return;
      }
      setScrollAnchor('start');
      setActiveUnit((i) => Math.min(layoutUnits.length - 1, i + 1));
      setSelectedItem(undefined);
      setSelectionSource(null);
    },
    [activeUnit, layoutUnits.length, showToast],
  );

  useEdgeWheelNavigate(
    itemScrollRef,
    handleEdgeNavigate,
    [activeUnit, layoutUnits.length, currentLayoutUnit?.id, phase, extractVisibleCount, structureForUnit.length],
    {
      // 提取列表还有未渲染页时，到底继续滚应触发加载更多，而不是翻分册
      allowEdge: (direction) => {
        if (
          direction === 'next'
          && phase === 'extract'
          && extractVisibleCount < structureForUnit.length
        ) {
          return false;
        }
        return true;
      },
      onSuppressedEdge: (direction) => {
        if (direction === 'next') loadMoreExtractPages();
      },
    },
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const idx = visiblePhases.findIndex((p) => p.id === phase);
      if (e.key === 'ArrowLeft' && idx > 0) {
        e.preventDefault();
        setPhase(visiblePhases[idx - 1]!.id);
      }
      if (e.key === 'ArrowRight' && idx >= 0 && idx < visiblePhases.length - 1) {
        e.preventDefault();
        setPhase(visiblePhases[idx + 1]!.id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, setPhase, visiblePhases]);

  const pageDirect = batch?.enableSemanticSplit === false
    || batch?.layoutPhase === 'page_direct';
  const hidePdfPane = phase === 'ttq' || phase === 'ipttq' || phase === 'figure_extract';
  const ownerAccepted = Boolean(batch && isFactoryBatchOwnerAccepted(batch));

  return (
    <div className="compare-page">
      {toast ? (
        <div className="compare-toast" role="status" aria-live="polite">
          {toast}
        </div>
      ) : null}

      <div className="compare-toolbar">
        <div className="compare-toolbar-left">
          <Link to={`/factory/tasks/${batchId}`} className="compare-back">
            ← 任务详情
          </Link>
          <div className="compare-title-block">
            <h1 className="compare-title">{batch?.title || '工序结果'}</h1>
            <p className="compare-hint">
              {phase === 'layout' && pageDirect
                ? '页图准备：按页直通，不做文档块切分；右侧可看各页 · 方向键切换工序'
                : PHASE_HINT[phase]}
            </p>
          </div>
        </div>
        <div className="compare-toolbar-actions">
          <div className="compare-phase-toggle" role="group" aria-label="工序">
            {visiblePhases.map((p) => (
              <button
                key={p.id}
                type="button"
                className={phase === p.id ? 'active' : undefined}
                style={p.wired === false ? { opacity: 0.55 } : undefined}
                title={p.wired === false ? '实验期未接入' : undefined}
                onClick={() => setPhase(p.id)}
              >
                {p.label}
                {p.wired === false ? ' · 未接入' : ''}
              </button>
            ))}
          </div>
          <div className="compare-more" ref={moreMenuRef}>
            <button
              type="button"
              className="compare-more-btn"
              aria-label="更多操作"
              aria-expanded={moreMenuOpen}
              title="更多操作"
              disabled={reextractAllBusy || ttqRerunBusy || ttqMergeOrphansBusy || ipttqRerunBusy}
              onClick={() => setMoreMenuOpen((v) => !v)}
            >
              {reextractAllBusy || ttqRerunBusy || ttqMergeOrphansBusy || ipttqRerunBusy
                ? <Loader2 size={16} className="animate-spin" />
                : <MoreVertical size={16} />}
            </button>
            {moreMenuOpen ? (
              <div className="compare-more-menu" role="menu">
                {ownerAccepted ? (
                  <button
                    type="button"
                    role="menuitem"
                    disabled
                    title="已验收，不可再发起提取或重跑"
                  >
                    <RotateCcw size={14} />
                    已验收 · 不可提取
                  </button>
                ) : null}
                {!ownerAccepted && phase === 'extract' ? (
                  <button
                    type="button"
                    role="menuitem"
                    disabled={reextractAllBusy || reextractingUnitIds.size > 0}
                    title="按当前学科策略重抽全部页（保留页单元，只清旧颗粒）"
                    onClick={() => void onReextractAll()}
                  >
                    <RotateCcw size={14} />
                    全部重提取
                  </button>
                ) : null}
                {!ownerAccepted && phase === 'ttq' ? (
                  <>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={ttqRerunBusy || ttqMergeOrphansBusy || reextractAllBusy || ipttqRerunBusy}
                      title="重新入围聚类并浓缩各组标题；完成后自动品题浓缩（不滴灌）"
                      onClick={() => void onRerunTtqTitles()}
                    >
                      <RotateCcw size={14} />
                      {ttqRerunBusy ? '推荐中…' : '重跑品题推荐'}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={ttqMergeOrphansBusy || ttqRerunBusy || reextractAllBusy || ipttqRerunBusy || ttqMergeQueueDepth > 0}
                      title="将落单的小推荐组按语义并入相近组（不重跑全量推荐、不清空浓缩）"
                      onClick={() => void onMergeTtqOrphanGroups()}
                    >
                      <Merge size={14} />
                      {ttqMergeOrphansBusy ? '融合中…' : '融合孤立推荐组'}
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      disabled={ttqMergeOrphansBusy || ttqRerunBusy || reextractAllBusy || ipttqRerunBusy || ttqMergeQueueDepth > 0}
                      title={ttqManualMergeMode
                        ? '退出拖拽合并模式'
                        : '进入拖拽模式：把源卡拖到目标卡上直接合并，并刷新品题浓缩目标'}
                      onClick={() => {
                        setMoreMenuOpen(false);
                        if (ttqManualMergeMode) exitTtqManualMergeMode();
                        else toggleTtqManualMergeMode();
                      }}
                    >
                      <GripVertical size={14} />
                      {ttqManualMergeMode ? '退出手动合并' : '手动合并'}
                    </button>
                  </>
                ) : null}
                {!ownerAccepted && phase === 'ipttq' ? (
                  <>
                    {showContinueIpttq ? (
                      <button
                        type="button"
                        role="menuitem"
                        disabled={ipttqRerunBusy || ttqRerunBusy || ttqMergeOrphansBusy || reextractAllBusy}
                        title={`品题推荐 ${warehouseTtqaCount} 组，已浓缩 ${condensedIpttqCount} 条；继续补齐缺口（保留已完成）`}
                        onClick={() => void onRerunIpttq('continue')}
                      >
                        <Play size={14} />
                        {ipttqRerunBusy ? '浓缩中…' : '继续品题浓缩'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      role="menuitem"
                      disabled={ipttqRerunBusy || ttqRerunBusy || ttqMergeOrphansBusy || reextractAllBusy}
                      title="重新生成全部 IPTTQ skill + 诱导题库草稿（跳过直觉草图；不滴灌）"
                      onClick={() => void onRerunIpttq('full')}
                    >
                      <RotateCcw size={14} />
                      {ipttqRerunBusy ? '浓缩中…' : '重跑品题浓缩'}
                    </button>
                  </>
                ) : null}
                {!ownerAccepted && phase !== 'extract' && phase !== 'ttq' && phase !== 'ipttq' ? (
                  <button
                    type="button"
                    role="menuitem"
                    disabled
                    title="切换到「颗粒提取」「品题推荐」或「品题浓缩」后可用"
                  >
                    <RotateCcw size={14} />
                    全部重提取
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {error ? <p className="compare-error">{error}</p> : null}
      {loading ? <p className="compare-empty">加载中…</p> : null}

      {!loading && !error ? (
        <>
          {showRuntimeAdminBanner ? (
            <p className="compare-hint-banner compare-hint-banner-warn" role="status">
              {enrichSettingsHasToken === true && enrichRuntimeAdminGap != null
                ? '服务端已有 Runtime ADMIN token，但本任务富化结果仍是旧的「缺画像」快照。'
                : enrichSettingsHasToken === false || enrichRuntimeAdminGap === 'token'
                  ? '缺 Runtime 学科画像：未配置 ADMIN token，RFE/PAP 无法绑定主干。'
                  : 'Runtime 学科画像未加载成功，RFE 处于 pending（profiles_empty）。'}
              {enrichProfileGapUnits.length > 0 ? (
                <>
                  {' '}共 {enrichProfileGapUnits.length} 页缺画像，见右侧警告列表。
                </>
              ) : null}
                              {enrichSettingsHasToken === true ? (
                <>用警告列表里的「重刷全部画像」刷新绑定。</>
              ) : (
                <>
                  <Link to="/factory/settings?tab=enrich" className="compare-inline-link">
                    去设置 · 富化画像
                  </Link>
                  配置后，对缺画像页「重新提取本页」。
                </>
              )}
            </p>
          ) : null}
          {phase === 'semantic_split' && semanticSummary ? (
            <p className="compare-hint-banner">
              {semanticSummary.model || 'deepseek-v4-flash'} · {semanticSummary.pageCount} 页记录 ·{' '}
              {semanticSummary.lineCount} 条接缝线
              {batch?.enableSemanticSplit === false
                ? ' · 本任务未启用语义切分'
                : ''}
            </p>
          ) : null}
          {!layoutUnits.length && !structuralUnits.length ? (
            <p className="compare-hint-banner">
              {pageDirect
                ? '尚无页图单元。请先完成「页图准备」后刷新。'
                : '尚无版面分册。请先完成文档块切分工序后刷新。'}
              <button
                type="button"
                className="compare-inline-link"
                onClick={() => navigate(`/factory/tasks/${batchId}`)}
              >
                返回工序进度
              </button>
            </p>
          ) : hidePdfPane ? null : (
            <div className="compare-pager" aria-label="当前分册">
              <div className="compare-source-toggle" role="group" aria-label="原文显示方式">
                <button
                  type="button"
                  className={sourceViewMode === 'image' ? 'active' : undefined}
                  onClick={() => setSourceViewMode('image')}
                  title="按页加载 JPEG，节省整册 PDF 带宽"
                >
                  <Image size={13} strokeWidth={2.25} aria-hidden />
                  图片
                </button>
                <button
                  type="button"
                  className={sourceViewMode === 'pdf' ? 'active' : undefined}
                  onClick={() => setSourceViewMode('pdf')}
                  title="加载整册 PDF（带宽较大）"
                >
                  <FileText size={13} strokeWidth={2.25} aria-hidden />
                  PDF
                </button>
              </div>
              <div className="compare-pager-nav">
                <button
                  type="button"
                  className="compare-pager-btn"
                  disabled={activeUnit <= 0}
                  onClick={() => handleEdgeNavigate('prev')}
                  aria-label="上一册"
                >
                  ‹
                </button>
                <span className="compare-pager-range">
                  第 {formatPageRange(pageStart, pageEnd) || '—'} 页
                </span>
                <span className="compare-meta">
                  · 分册 {activeUnit + 1}/{layoutUnits.length}
                  {currentLayoutUnit?.mineruStatus
                    ? ` · ${currentLayoutUnit.mineruStatus}`
                    : ''}
                </span>
                <button
                  type="button"
                  className="compare-pager-btn"
                  disabled={activeUnit >= layoutUnits.length - 1}
                  onClick={() => handleEdgeNavigate('next')}
                  aria-label="下一册"
                >
                  ›
                </button>
              </div>
            </div>
          )}

          <div className={`compare-shell${hidePdfPane ? ' compare-shell-single' : ''}`}>
            {!hidePdfPane ? (
            <section className="compare-pane compare-pane-pdf" aria-label="原文预览">
              <div className="compare-pane-head">
                <span className="compare-pane-head-title">
                  {sourceViewMode === 'image' ? '原文图片' : '原文 PDF'}
                  {sourceViewMode === 'image' ? (
                    <span className="compare-page-image-info-wrap" ref={pageImageInfoRef}>
                      <button
                        type="button"
                        className="compare-page-image-info-btn"
                        aria-label="页图详情"
                        aria-expanded={pageImageInfoOpen}
                        title="页图详情"
                        disabled={!pageImageUrl}
                        onClick={() => setPageImageInfoOpen((o) => !o)}
                      >
                        <Info size={14} strokeWidth={2.25} aria-hidden />
                      </button>
                      {pageImageInfoOpen ? (
                        <div
                          className="compare-page-image-info-popup"
                          role="dialog"
                          aria-label="页图详情"
                        >
                          <div className="compare-page-image-info-popup-head">页图参数</div>
                          <dl className="compare-page-image-info-list">
                            <div>
                              <dt>单元</dt>
                              <dd>
                                {previewStructuralUnit
                                  ? `#${previewStructuralUnit.unitIndex + 1}`
                                  : '—'}
                              </dd>
                            </div>
                            <div>
                              <dt>页码</dt>
                              <dd>
                                {(() => {
                                  const span = Array.isArray(previewStructuralUnit?.pageSpan)
                                    ? (previewStructuralUnit!.pageSpan as unknown[])
                                        .map((n) => Number(n))
                                        .filter((n) => Number.isFinite(n))
                                    : [];
                                  if (!span.length) return '—';
                                  const a = Math.min(...span) + 1;
                                  const b = Math.max(...span) + 1;
                                  return a === b ? `第 ${a} 页` : `第 ${a}–${b} 页`;
                                })()}
                              </dd>
                            </div>
                            <div>
                              <dt>像素</dt>
                              <dd>
                                {pageImageMeta && pageImageMeta.width > 0
                                  ? `${pageImageMeta.width} × ${pageImageMeta.height} px`
                                  : pageImageMeta
                                    ? '加载中…'
                                    : '尚未加载'}
                              </dd>
                            </div>
                            <div>
                              <dt>文件大小</dt>
                              <dd>
                                {pageImageMeta
                                  ? formatImageBytes(pageImageMeta.byteSize)
                                  : '尚未加载'}
                              </dd>
                            </div>
                            <div>
                              <dt>格式</dt>
                              <dd>
                                {pageImageMeta?.contentType
                                  ? pageImageMeta.contentType.replace(/^image\//, '').toUpperCase()
                                  : '—'}
                              </dd>
                            </div>
                          </dl>
                        </div>
                      ) : null}
                    </span>
                  ) : null}
                </span>
                <div className="compare-pane-head-actions">
                  {sourceViewMode === 'pdf' ? (
                    <div className="compare-fit-toggle" role="group" aria-label="PDF 适配方式">
                      <button
                        type="button"
                        className={layoutMode === 'fit-width' ? 'active' : undefined}
                        onClick={() => setLayoutMode('fit-width')}
                      >
                        适配宽度
                      </button>
                      <button
                        type="button"
                        className={layoutMode === 'fit-height' ? 'active' : undefined}
                        onClick={() => setLayoutMode('fit-height')}
                      >
                        适配高度
                      </button>
                    </div>
                  ) : (
                    <span className="compare-meta">
                      {previewStructuralUnit
                        ? `单元 #${previewStructuralUnit.unitIndex + 1}`
                        : '随右侧选中单元切换'}
                    </span>
                  )}
                  <span className="compare-meta">
                    {currentLayoutUnit
                      ? formatPageRange(pageStart, pageEnd)
                        ? `p.${formatPageRange(pageStart, pageEnd)}`
                        : ''
                      : fallbackAsset
                        ? '源文件'
                        : ''}
                  </span>
                </div>
              </div>
              <div className="compare-pane-body">
                {sourceViewMode === 'image' ? (
                  pageImageUrl && authToken ? (
                    <div
                      className={`compare-page-image-stage${
                        pageImageEdgePulling ? ' is-pulling' : ''
                      }${pageImageEdgeLatched ? ' is-latched' : ''}`}
                    >
                      <button
                        type="button"
                        className={`compare-page-edge-nav is-prev${
                          pageImagePrevH > 6 ? ' is-open' : ''
                        }${pageImageEdgeLatched === 'prev' ? ' is-latched' : ''}`}
                        style={{ height: pageImagePrevH }}
                        disabled={!canPageImagePrev}
                        tabIndex={pageImagePrevH > 6 ? 0 : -1}
                        aria-hidden={pageImagePrevH <= 6}
                        onClick={() => {
                          if (!canPageImagePrev) return;
                          goPageImageAdjacent(-1);
                        }}
                        title={canPageImagePrev ? '上一单元' : '已是第一单元'}
                      >
                        <span className="compare-page-edge-nav-label">
                          {canPageImagePrev ? '上一页' : '已是第一页'}
                        </span>
                      </button>
                      <button
                        type="button"
                        className={`compare-page-edge-nav is-next${
                          pageImageNextH > 6 ? ' is-open' : ''
                        }${pageImageEdgeLatched === 'next' ? ' is-latched' : ''}`}
                        style={{ height: pageImageNextH }}
                        disabled={!canPageImageNext}
                        tabIndex={pageImageNextH > 6 ? 0 : -1}
                        aria-hidden={pageImageNextH <= 6}
                        onClick={() => {
                          if (!canPageImageNext) return;
                          goPageImageAdjacent(1);
                        }}
                        title={canPageImageNext ? '下一单元' : '已是最后一单元'}
                      >
                        <span className="compare-page-edge-nav-label">
                          {canPageImageNext ? '下一页' : '已是最后一页'}
                        </span>
                      </button>
                      <div ref={bindPageImageScrollEl} className="compare-page-image-scroll">
                        <div
                          className="compare-page-image-column"
                          style={{
                            transform:
                              pageImageShiftY !== 0
                                ? `translateY(${pageImageShiftY}px)`
                                : undefined,
                          }}
                        >
                          <div
                            className="compare-page-image-wrap is-clickable"
                            role="button"
                            tabIndex={0}
                            title={
                              previewStructuralUnit
                                ? `触顶下拉 / 触底上拉翻页 · 轻点定位右侧单元 #${previewStructuralUnit.unitIndex + 1}`
                                : '触顶下拉 / 触底上拉翻页 · 轻点定位右侧对应单元'
                            }
                            onClick={() => {
                              if (pageImageEdgePulling || pageImageEdgeLatched) return;
                              onPageImageActivate();
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onPageImageActivate();
                              }
                              if (e.key === 'ArrowUp' && canPageImagePrev) {
                                e.preventDefault();
                                goPageImageAdjacent(-1);
                              }
                              if (e.key === 'ArrowDown' && canPageImageNext) {
                                e.preventDefault();
                                goPageImageAdjacent(1);
                              }
                            }}
                          >
                            <AuthImage
                              key={pageImageUrl}
                              url={pageImageUrl}
                              alt={
                                previewStructuralUnit
                                  ? `单元 #${previewStructuralUnit.unitIndex + 1} 页图`
                                  : '页图'
                              }
                              className="compare-page-image"
                              onLoadedMeta={setPageImageMeta}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="compare-empty" style={{ padding: 24 }}>
                      {!authToken
                        ? '等待登录令牌…'
                        : !previewStructuralUnit
                          ? '请在右侧选择单元以加载页图'
                          : '暂无页图。可切换到 PDF，或确认页图准备已完成。'}
                      {previewStructuralUnit ? (
                        <button
                          type="button"
                          className="compare-inline-link"
                          onClick={() => setSourceViewMode('pdf')}
                        >
                          改用 PDF
                        </button>
                      ) : null}
                    </p>
                  )
                ) : pdfUrl && authToken ? (
                  <PDFViewer
                    key={pdfUrl}
                    url={pdfUrl}
                    authToken={authToken}
                    items={pdfItems}
                    splitLines={pdfSplitLines}
                    selectedItemIndex={
                      phase === 'layout' || phase === 'semantic_split'
                        ? selectedItem
                        : highlightedForUnit?.[0]
                    }
                    scrollToPageIdx={
                      UNIT_RESULT_PHASES.includes(phase) ? selectedUnitPageIdx : null
                    }
                    scrollRequestId={pdfScrollRequestId}
                    highlightedItemIndices={
                      UNIT_RESULT_PHASES.includes(phase) ? highlightedForUnit : undefined
                    }
                    autoScrollOnSelect
                    layoutMode={layoutMode}
                    className="compare-pdf"
                    scrollAnchor={scrollAnchor}
                    onEdgeNavigate={layoutUnits.length ? handleEdgeNavigate : undefined}
                    onActivePageChange={
                      UNIT_RESULT_PHASES.includes(phase) ? onPdfActivePageChange : undefined
                    }
                    onItemClick={(index) => {
                      setSelectionSource('bbox');
                      setSelectedItem(index);
                    }}
                  />
                ) : (
                  <p className="compare-empty" style={{ padding: 24 }}>
                    {!authToken
                      ? '等待登录令牌…'
                      : '暂无 PDF 可预览。请确认批次已上传源文件并完成版面工序。'}
                  </p>
                )}
              </div>
            </section>
            ) : null}

            <section
              className="compare-pane compare-pane-items"
              aria-label={
                phase === 'layout'
                  ? '版面条目'
                  : phase === 'semantic_split'
                    ? '接缝切分线'
                    : phase === 'structure'
                      ? '结构单元'
                      : phase === 'extract'
                        ? '知识颗粒'
                        : phase === 'figure_extract'
                          ? '插图提取'
                          : phase === 'ttq'
                            ? '品题推荐'
                            : phase === 'ipttq'
                              ? '品题浓缩'
                              : '品题结果'
              }
            >
              <div className="compare-pane-head">
                <span className="compare-pane-head-title">
                  {phase === 'layout'
                    ? '版面'
                    : phase === 'semantic_split'
                      ? '接缝线'
                      : phase === 'structure'
                        ? '提取前体'
                        : phase === 'extract'
                          ? '知识颗粒'
                          : phase === 'figure_extract'
                            ? '插图提取'
                            : phase === 'ttq'
                              ? '品题推荐'
                              : phase === 'ipttq'
                                ? '品题浓缩'
                                : '品题'}
                  {phase === 'extract' && extractWarnUnitCount > 0 ? (
                    <span className="compare-extract-fail-wrap" ref={extractFailMenuRef}>
                      <button
                        type="button"
                        className="compare-extract-fail-btn"
                        aria-label={[
                          extractFailedUnits.length
                            ? `提取失败 ${extractFailedUnits.length}`
                            : null,
                          enrichProfileGapUnits.length
                            ? `缺画像 ${enrichProfileGapUnits.length}`
                            : null,
                        ].filter(Boolean).join(' · ') || '警告'}
                        aria-expanded={extractFailMenuOpen}
                        title={[
                          extractFailedUnits.length
                            ? `提取失败 ${extractFailedUnits.length} 个单元`
                            : null,
                          enrichProfileGapUnits.length
                            ? `缺画像 ${enrichProfileGapUnits.length} 个单元`
                            : null,
                        ].filter(Boolean).join(' · ')}
                        onClick={() => setExtractFailMenuOpen((o) => !o)}
                      >
                        <TriangleAlert size={14} strokeWidth={2.25} aria-hidden />
                        <span className="compare-extract-fail-count">{extractWarnUnitCount}</span>
                      </button>
                      {extractFailMenuOpen ? (
                        <div className="compare-extract-fail-popup" role="listbox" aria-label="提取与富化警告">
                          {extractFailedUnits.length > 0 ? (
                            <>
                              <div className="compare-extract-fail-popup-head">
                                提取失败 · {extractFailedUnits.length} · 点击定位
                              </div>
                              <ul className="compare-extract-fail-list">
                                {extractFailedUnits.map((row) => {
                                  const isActive =
                                    selectedItem != null
                                    && Math.floor(Number(selectedItem) / 10000) === row.suIdx;
                                  return (
                                    <li key={`fail-${row.suIdx}`}>
                                      <button
                                        type="button"
                                        className={`compare-extract-fail-item${isActive ? ' is-active' : ''}`}
                                        aria-current={isActive ? 'true' : undefined}
                                        onClick={() => focusExtractUnit(row.suIdx)}
                                      >
                                        <span className="compare-extract-fail-unit">
                                          单元 #{row.unitIndex + 1}
                                        </span>
                                        <span className="compare-extract-fail-reason" title={row.reason}>
                                          {row.reason}
                                        </span>
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            </>
                          ) : null}
                          {enrichProfileGapUnits.length > 0 ? (
                            <>
                              <div className="compare-extract-fail-popup-head is-profile">
                                <span>缺画像 · {enrichProfileGapUnits.length}</span>
                                <button
                                  type="button"
                                  className="compare-extract-fail-flush"
                                  disabled={
                                    flushProfilesBusy
                                    || reextractAllBusy
                                    || Boolean(batch && isFactoryBatchOwnerAccepted(batch))
                                    || enrichSettingsHasToken === false
                                  }
                                  title={
                                    enrichSettingsHasToken === false
                                      ? '先配置 Runtime ADMIN token 再重刷'
                                      : '保留题干，对缺画像页重跑 RFE/PAP'
                                  }
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void onFlushAllProfiles();
                                  }}
                                >
                                  {flushProfilesBusy ? (
                                    <Loader2 size={12} className="animate-spin" aria-hidden />
                                  ) : null}
                                  {flushProfilesBusy ? '重刷中…' : '重刷全部画像'}
                                </button>
                              </div>
                              <ul className="compare-extract-fail-list">
                                {enrichProfileGapUnits.map((row) => {
                                  const isActive =
                                    selectedItem != null
                                    && Math.floor(Number(selectedItem) / 10000) === row.suIdx;
                                  return (
                                    <li key={`profile-gap-${row.suIdx}`}>
                                      <button
                                        type="button"
                                        className={`compare-extract-fail-item is-profile${isActive ? ' is-active' : ''}`}
                                        aria-current={isActive ? 'true' : undefined}
                                        onClick={() => focusExtractUnit(row.suIdx)}
                                      >
                                        <span className="compare-extract-fail-unit">
                                          单元 #{row.unitIndex + 1}
                                        </span>
                                        <span className="compare-extract-fail-reason is-profile" title={row.reason}>
                                          {row.reason}
                                        </span>
                                      </button>
                                    </li>
                                  );
                                })}
                              </ul>
                            </>
                          ) : null}
                        </div>
                      ) : null}
                    </span>
                  ) : null}
                </span>
                <span className="compare-meta">
                  {(phase === 'ipttq' && (ipttqJobProgress || ipttqRerunBusy))
                    || (phase === 'ttq'
                      && (ttqJobProgress || ttqRerunBusy || ttqProductResolveBusy)) ? (
                    <Loader2 size={12} className="animate-spin shrink-0" aria-hidden />
                  ) : null}
                  {phase === 'layout'
                    ? layoutMissing
                      ? '暂无条目'
                      : `${visibleItems.length} 条${selectedItem != null ? ' · 已选' : ''}`
                    : phase === 'semantic_split'
                      ? `${pdfSplitLines.length} 线`
                      : phase === 'structure'
                        ? `${structureForUnit.length} 单元 · 提取前体${
                            selectedItem != null ? ' · 已选' : ''
                          }`
                        : phase === 'extract'
                          ? `${Math.min(extractVisibleCount, structureForUnit.length)}/${structureForUnit.length} 单元${
                              selectedItem != null ? ' · 已选' : ''
                            }`
                          : phase === 'figure_extract'
                            ? '试验台 · 多家 edit 对照'
                            : phase === 'ttq'
                            ? (ttqJobProgress
                              || (ttqAdmission?.warehouseTtqaCount
                                ? `${ttqAdmission.warehouseTtqaCount} 组`
                                : '仓内'))
                              : phase === 'ipttq'
                                ? (ipttqJobProgress
                                  || (ipttqRerunBusy
                                    ? '浓缩中…'
                                    : ipttqBatch?.records?.length
                                      ? `${ipttqBatch.records.length} 条`
                                      : '仓内'))
                                : '待接入'}
                </span>
              </div>
              <div
                className={`compare-item-scroll${phase === 'figure_extract' ? ' compare-item-scroll-fill' : ''}`}
                ref={itemScrollRef}
              >
                {layoutError ? <p className="compare-error">{layoutError}</p> : null}

                {phase === 'figure_extract' ? (
                  <div className="flex min-h-0 flex-1 flex-col p-3 pt-1">
                    <FigureExtractPhasePanel
                      batchId={batchId}
                      structuralUnits={structureForUnit}
                      selectedUnitId={routeUnitId}
                      selectedQaIndex={routeQaIndex}
                      onSelectParticle={onSelectFigureParticle}
                    />
                  </div>
                ) : null}

                {phase === 'semantic_split' ? (
                  pdfSplitLines.length === 0 ? (
                    <p className="compare-empty" style={{ padding: 16 }}>
                      无跨页接缝线（未启用语义切分，或接缝判定为不连续）
                    </p>
                  ) : (
                    pdfSplitLines.map((line, li) => (
                      <div key={`${line.page_idx}-${li}`} className="compare-item" style={{ cursor: 'default' }}>
                        <div className="compare-item-meta">
                          <span className="compare-type">seam</span>
                          <span>p.{line.page_idx + 1}</span>
                          <span>y {(Number(line.y_norm) * 100).toFixed(1)}%</span>
                        </div>
                        {line.label ? (
                          <div className="compare-item-text text-sm">{line.label}</div>
                        ) : null}
                      </div>
                    ))
                  )
                ) : null}

                {phase === 'layout'
                  ? visibleItems.map((item) => {
                      const idx = Number(item._originalIndex ?? 0);
                      const active = selectedItem === idx;
                      const bookPage = pageStart + Number(item.page_idx ?? 0) + 1;
                      const itemType = String(item.type || 'text').toLowerCase();
                      const imgPath = item.img_path ? String(item.img_path) : undefined;
                      const isImageLike =
                        itemType === 'image' || itemType === 'figure' || Boolean(imgPath);
                      const plainText = stripImageDescriptions(String(item.text || ''));
                      return (
                        <button
                          key={idx}
                          type="button"
                          ref={(el) => {
                            if (el) itemRefs.current.set(idx, el);
                            else itemRefs.current.delete(idx);
                          }}
                          className={`compare-item${active ? ' active' : ''}${
                            isImageLike ? ' has-image' : ''
                          }`}
                          onClick={() => {
                            setSelectionSource('list');
                            setSelectedItem(idx);
                            requestPdfScrollToSelection();
                          }}
                        >
                          <div className="compare-item-meta">
                            <span className="compare-type">{String(item.type || 'text')}</span>
                            <span>p.{bookPage}</span>
                          </div>
                          {isImageLike && currentLayoutUnit ? (
                            <ItemPreviewImage
                              batchId={batchId}
                              unitId={currentLayoutUnit.id}
                              imgPath={imgPath}
                              pageIdx={Number(item.page_idx ?? 0)}
                              bbox={item.bbox as [number, number, number, number] | undefined}
                              alt={plainText || `p.${bookPage} 图`}
                            />
                          ) : null}
                          {plainText ? (
                            <div className="compare-item-text">
                              <MathTextBlock text={plainText} className="text-sm" />
                            </div>
                          ) : !isImageLike ? (
                            <div className="compare-item-text">（无文本）</div>
                          ) : null}
                        </button>
                      );
                    })
                  : null}

                {phase === 'structure'
                  ? structureForUnit.map((su, suIdx) => {
                      const selected =
                        selectedItem != null && Math.floor(selectedItem / 10000) === suIdx;
                      const pages = Array.isArray(su.pageSpan) ? (su.pageSpan as number[]) : [];
                      const previewLines = structuralUnitPreviewLines(su);
                      const precursor = su.extractPrecursor;
                      const blockCount =
                        precursor?.blocks?.length ?? su.itemCount ?? previewLines.length;
                      const imageSlots = precursor?.images?.length ?? pages.length;
                      const target = structuralUnitExtractRouteLabel(su, {
                        primaryDiscipline: batch?.primaryDiscipline,
                      });
                      return (
                        <div
                          key={su.id}
                          role="button"
                          tabIndex={0}
                          ref={(el) => {
                            const key = suIdx * 10000;
                            if (el) itemRefs.current.set(key, el);
                            else itemRefs.current.delete(key);
                          }}
                          className={`compare-item${selected ? ' active' : ''}`}
                          onClick={() => {
                            setSelectionSource('list');
                            setSelectedItem(suIdx * 10000);
                            requestPdfScrollToSelection();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setSelectionSource('list');
                              setSelectedItem(suIdx * 10000);
                              requestPdfScrollToSelection();
                            }
                          }}
                        >
                          <div className="compare-item-meta">
                            <span className="compare-type">单元 #{su.unitIndex + 1}</span>
                            <span>
                              {pages.length
                                ? `p.${pages.map((p) => p + 1).join(',')}`
                                : su.itemCount
                                  ? `${su.itemCount} 块`
                                  : ''}
                            </span>
                          </div>
                          <div className="compare-precursor-meta">
                            <span className="compare-precursor-chip">{target}</span>
                            <span>
                              {blockCount} blocks · {imageSlots} 图槽
                              {precursor?.input_kind ? ` · ${precursor.input_kind}` : ''}
                            </span>
                          </div>
                          <div className="compare-item-text space-y-1">
                            {previewLines.length ? (
                              previewLines.map((line, lineIdx) => (
                                <MathTextBlock
                                  key={`${su.id}-${lineIdx}`}
                                  text={line}
                                  className="text-sm"
                                />
                              ))
                            ) : (
                              '（空单元）'
                            )}
                          </div>
                          {precursor ? (
                            <details
                              className="compare-precursor-details"
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                            >
                              <summary>前体接口形状</summary>
                              <pre className="compare-precursor-json">
                                {JSON.stringify(
                                  {
                                    schema: precursor.schema,
                                    target_fc: precursor.target_fc,
                                    input_kind: precursor.input_kind,
                                    unit_id: precursor.unit_id || su.id,
                                    page_span: precursor.page_span ?? pages,
                                    mode: precursor.mode,
                                    blocks: (precursor.blocks || []).map((b) => ({
                                      id: b.id,
                                      type: b.type,
                                      page: b.page,
                                      bbox: b.bbox,
                                      text:
                                        String(b.text || '').length > 120
                                          ? `${String(b.text).slice(0, 120)}…`
                                          : b.text,
                                      img_url: b.img_url,
                                    })),
                                    images: (precursor.images || []).map((img) => ({
                                      id: img.id,
                                      page: img.page,
                                      role: img.role,
                                      image_url: img.image_url,
                                      resolve: img.resolve,
                                    })),
                                  },
                                  null,
                                  2,
                                )}
                              </pre>
                            </details>
                          ) : (
                            <p className="compare-precursor-legacy">
                              旧聚合结果（无前体形状）。请在任务详情「重新启动全部工序」后重跑结构聚合。
                            </p>
                          )}
                        </div>
                      );
                    })
                  : null}

                {phase === 'extract'
                  ? extractUnitsWindow.map((su, suIdx) => {
                      const selected =
                        selectedItem != null && Math.floor(selectedItem / 10000) === suIdx;
                      const pages = Array.isArray(su.pageSpan) ? (su.pageSpan as number[]) : [];
                      const qaList = structuralUnitExtractQa(su);
                      const title = structuralUnitExtractTitle(su);
                      const tokensLabel = structuralUnitExtractTokensLabel(su);
                      const busy = reextractingUnitIds.has(su.id);
                      const ignored =
                        Boolean(su.extractIgnored) || su.extractStatus === 'ignored';
                      const extractFailed = structuralUnitLooksExtractFailed(su);
                      const ignoreBusy = ignoreExtractBusyIds.has(su.id);

                      const handleUnitClick = () => {
                        setSelectionSource('list');
                        setSelectedItem(suIdx * 10000);
                        const figIdx = qaList.findIndex((qa) => isConfirmedPrintedFigureQa(qa));
                        setParticleRoute(su.id, figIdx >= 0 ? figIdx : null);
                        requestPdfScrollToSelection();
                      };

                      return (
                        <div
                          key={`extract-${su.id}`}
                          role="button"
                          tabIndex={0}
                          ref={(el) => {
                            const key = suIdx * 10000;
                            if (el) itemRefs.current.set(key, el);
                            else itemRefs.current.delete(key);
                          }}
                          className={`compare-item${selected ? ' active' : ''}${
                            ignored ? ' is-extract-ignored' : ''
                          }`}
                          onClick={handleUnitClick}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleUnitClick();
                            }
                          }}
                        >
                          <div className="compare-item-meta">
                            <span className="compare-type">单元 #{su.unitIndex + 1}</span>
                            <span
                              className={
                                extractFailed
                                  ? 'compare-extract-status is-failed'
                                  : ignored
                                    ? 'compare-extract-status is-ignored'
                                    : undefined
                              }
                            >
                              {pages.length ? `p.${pages.map((p) => p + 1).join(',')}` : ''}
                              {extractFailed
                                ? ' · 提取失败'
                                : ignored
                                  ? ' · 已忽略提取'
                                  : su.extractStatus && su.extractStatus !== 'ignored'
                                    ? ` · ${su.extractStatus}`
                                    : ''}
                            </span>
                          </div>
                          <div className="compare-precursor-meta">
                            <span className="compare-precursor-chip">
                              {structuralUnitExtractRouteLabel(su, {
                                primaryDiscipline: batch?.primaryDiscipline,
                              })}
                            </span>
                            <span>
                              {qaList.length
                                ? `${qaList.length} QA`
                                : su.extractSummary?.qaCount != null
                                  ? `${su.extractSummary.qaCount} QA`
                                  : ignored
                                    ? '已跳过'
                                    : '尚无颗粒'}
                              {title ? ` · ${title}` : ''}
                            </span>
                            {tokensLabel ? (
                              <span className="compare-precursor-chip compare-token-chip" title={
                                su.extractSummary?.model
                                  ? `${su.extractSummary.model}`
                                  : '本页提取 token'
                              }>
                                {tokensLabel}
                              </span>
                            ) : null}
                            {structuralUnitEnrichLabel(su) ? (
                              <span
                                className={`compare-precursor-chip compare-enrich-chip${
                                  su.enrichStatus === 'failed' ? ' is-failed' : ''
                                }${su.enrichStatus === 'succeeded' ? ' is-ok' : ''}`}
                                title={su.enrichError || '本页 RFE + PAP'}
                              >
                                {structuralUnitEnrichLabel(su)}
                              </span>
                            ) : su.extractStatus === 'succeeded' ? (
                              <span className="compare-precursor-chip compare-enrich-chip" title="尚未写入 enrich">
                                enrich · 无
                              </span>
                            ) : null}
                            <div className="compare-item-extract-actions">
                              <button
                                type="button"
                                className="compare-item-reextract"
                                disabled={
                                  ownerAccepted
                                  || busy
                                  || reextractAllBusy
                                  || su.extractStatus === 'running'
                                  || ignoreBusy
                                }
                                title={ownerAccepted ? '已验收，不可再提取' : undefined}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void onReextractUnit(su.id);
                                }}
                              >
                                {ownerAccepted
                                  ? '已验收'
                                  : busy
                                    ? '重新提取中…'
                                    : '重新提取本页'}
                              </button>
                              <span
                                className="compare-unit-more-wrap"
                                data-unit-more={su.id}
                              >
                                <button
                                  type="button"
                                  className="compare-unit-more-btn"
                                  aria-label="更多操作"
                                  aria-expanded={unitMoreMenuId === su.id}
                                  disabled={ownerAccepted || ignoreBusy || busy}
                                  title={ownerAccepted ? '已验收，不可修改' : '更多'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setUnitMoreMenuId((cur) => (cur === su.id ? null : su.id));
                                  }}
                                >
                                  <MoreVertical size={14} strokeWidth={2.25} aria-hidden />
                                </button>
                                {unitMoreMenuId === su.id ? (
                                  <div className="compare-more-menu compare-unit-more-menu" role="menu">
                                    <button
                                      type="button"
                                      role="menuitem"
                                      disabled={ignoreBusy}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void onToggleIgnoreExtract(su.id, !ignored);
                                      }}
                                    >
                                      {ignored ? '取消忽略提取' : '忽略此页提取'}
                                    </button>
                                  </div>
                                ) : null}
                              </span>
                            </div>
                          </div>
                          {extractFailed ? (
                            <p className="compare-precursor-legacy compare-unit-fc-error">
                              提取失败：{formatUnitFcError(su.extractError) || '未返回失败原因'}
                            </p>
                          ) : null}
                          {ignored ? (
                            <p className="compare-precursor-legacy compare-unit-ignored-note">
                              已忽略本页内容提取，不再计入失败。可从右侧菜单取消忽略，或「重新提取本页」。
                            </p>
                          ) : null}
                          {su.enrichStatus === 'failed' || su.enrichError ? (
                            <p className="compare-precursor-legacy compare-unit-fc-error">
                              RFE/PAP 失败：{formatUnitFcError(su.enrichError) || '未知错误'}
                              {!ownerAccepted ? ' · 可用「重新提取本页」重试' : ''}
                            </p>
                          ) : null}
                          {qaList.length
                          || (!ignored
                            && (su.extractStatus === 'succeeded'
                              || su.coachOverlay
                              || su.learnerEvidence
                              || su.coachOverlayStatus
                              || su.learnerEvidenceStatus)) ? (
                            <div className="compare-qa-list">
                              {!ignored
                              && (su.extractStatus === 'succeeded'
                                || su.coachOverlay
                                || su.learnerEvidence
                                || su.coachOverlayStatus
                                || su.learnerEvidenceStatus) ? (
                                <>
                                  {(() => {
                                    const coachPreview = buildCoachOverlayPreview(
                                      su.coachOverlay || null,
                                    );
                                    return (
                                      <div className="compare-qa-item compare-unit-layer">
                                        <details
                                          className={`compare-qa-enrich-details${
                                            coachPreview?.hasContent ? '' : ' is-empty'
                                          }`}
                                          onToggle={(e) => {
                                            if ((e.target as HTMLDetailsElement).open) {
                                              void ensureStructuralUnitDetail(su.id);
                                            }
                                          }}
                                        >
                                          <summary>
                                            <span className="compare-qa-enrich-summary-text">
                                              {enrichLoadingIds.has(su.id) && !su.coachOverlay
                                                ? '教练层 · 加载中…'
                                                : coachPreview?.summaryLabel
                                                  || (su.coachOverlayStatus
                                                    ? `教练层 · ${overlayJobStatusLabelZh(su.coachOverlayStatus)}`
                                                    : '教练层 · 展开加载详情')}
                                            </span>
                                          </summary>
                                          {su.coachOverlayError ? (
                                            <p className="compare-qa-overlay-error">
                                              {formatUnitFcError(su.coachOverlayError)}
                                            </p>
                                          ) : null}
                                          {coachPreview ? (
                                            <CoachOverlayEnrichBody preview={coachPreview} />
                                          ) : (
                                            <p>
                                              {enrichLoadingIds.has(su.id)
                                                ? '正在拉取本页教练层…'
                                                : '展开后加载完整教练层（页级）'}
                                            </p>
                                          )}
                                        </details>
                                      </div>
                                    );
                                  })()}
                                  {(() => {
                                    const learnerPreview = buildLearnerOverlayPreview(
                                      su.learnerEvidence || null,
                                    );
                                    return (
                                      <div className="compare-qa-item compare-unit-layer">
                                        <details
                                          className={`compare-qa-enrich-details${
                                            learnerPreview?.hasContent ? '' : ' is-empty'
                                          }`}
                                          onToggle={(e) => {
                                            if ((e.target as HTMLDetailsElement).open) {
                                              void ensureStructuralUnitDetail(su.id);
                                            }
                                          }}
                                        >
                                          <summary>
                                            <span className="compare-qa-enrich-summary-text">
                                              {enrichLoadingIds.has(su.id) && !su.learnerEvidence
                                                ? '学习层 · 加载中…'
                                                : learnerPreview?.summaryLabel
                                                  || (su.learnerEvidenceStatus
                                                    ? `学习层 · ${overlayJobStatusLabelZh(su.learnerEvidenceStatus)}`
                                                    : '学习层 · 展开加载详情')}
                                            </span>
                                          </summary>
                                          {su.learnerEvidenceError ? (
                                            <p className="compare-qa-overlay-error">
                                              {formatUnitFcError(su.learnerEvidenceError)}
                                            </p>
                                          ) : null}
                                          {learnerPreview ? (
                                            <LearnerOverlayEnrichBody preview={learnerPreview} />
                                          ) : (
                                            <p>
                                              {enrichLoadingIds.has(su.id)
                                                ? '正在拉取本页学习层…'
                                                : '展开后加载完整学习层（页级）'}
                                            </p>
                                          )}
                                        </details>
                                      </div>
                                    );
                                  })()}
                                </>
                              ) : null}
                              {qaList.length ? (
                                <div className="compare-qa-particles-group">
                                  <div
                                    className="compare-qa-item compare-unit-layer compare-qa-section-sticky"
                                    role="heading"
                                    aria-level={3}
                                  >
                                    <span className="compare-qa-enrich-summary-text">
                                      {(() => {
                                        const withFig = qaList.filter((q) => q.hasProblemFigure === true).length;
                                        const bits = [
                                          `知识颗粒 · ${qaList.length} 题`,
                                          withFig > 0 ? `有配图 ${withFig}` : '',
                                        ].filter(Boolean);
                                        return bits.join(' · ');
                                      })()}
                                    </span>
                                    <button
                                      type="button"
                                      className="compare-qa-particles-reextract"
                                      disabled={
                                        ownerAccepted
                                        || busy
                                        || reextractAllBusy
                                        || reextractingUnitIds.has(su.id)
                                        || su.extractStatus === 'running'
                                        || ignoreBusy
                                      }
                                      title={
                                        ownerAccepted
                                          ? '已验收，不可再提取'
                                          : '只重提本页知识颗粒（不动富化 / 教练层 / 学习层）'
                                      }
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        void onReextractUnit(su.id, { particlesOnly: true });
                                      }}
                                    >
                                      {reextractingUnitIds.has(su.id) ? '重提中…' : '重提颗粒'}
                                    </button>
                                  </div>
                                  {qaList.map((qa, qi) => {
                                    const qaRouteActive =
                                      routeUnitId === su.id && routeQaIndex === qi;
                                    return (
                                <div
                                  key={`${su.id}-qa-${qi}`}
                                  ref={(el) => {
                                    const key = `${su.id}:${qi}`;
                                    if (el) qaItemRefs.current.set(key, el);
                                    else qaItemRefs.current.delete(key);
                                  }}
                                  role="button"
                                  tabIndex={0}
                                  className={`compare-qa-item${qaRouteActive ? ' is-route-active' : ''}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectionSource('list');
                                    setSelectedItem(suIdx * 10000);
                                    setParticleRoute(su.id, qi);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key !== 'Enter' && e.key !== ' ') return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectionSource('list');
                                    setSelectedItem(suIdx * 10000);
                                    setParticleRoute(su.id, qi);
                                  }}
                                >
                                  <div className="compare-item-text space-y-1">
                                    <div className="compare-qa-question">
                                      <span className="compare-qa-label">
                                        Q{qi + 1}
                                        {qa.confidence != null
                                          ? ` · ${Math.round(qa.confidence * 100)}%`
                                          : ''}
                                      </span>
                                      <div className="compare-qa-main-question">
                                        <MathTextBlock text={qa.question} className="text-sm" />
                                      </div>
                                      {(() => {
                                        const mark = qaFigureMarkLabel(qa);
                                        const canCrop = qa.hasProblemFigure === true;
                                        return (
                                          <div
                                            className={`compare-qa-figure-mark is-${mark.tone}${
                                              canCrop ? ' is-clickable' : ''
                                            }`}
                                            title={
                                              canCrop
                                                ? `${mark.title || ''}${mark.title ? ' · ' : ''}点击预览切块`
                                                : mark.title
                                            }
                                            role={canCrop ? 'button' : undefined}
                                            tabIndex={canCrop ? 0 : undefined}
                                            onClick={(e) => {
                                              if (!canCrop) return;
                                              e.stopPropagation();
                                              void openFigureCropPreview({
                                                unitId: su.id,
                                                qaIndex: qi,
                                                questionPreview: qa.question,
                                                figureKind: qa.figureKind,
                                                figureNote: qa.figureNote,
                                                figureBox: qa.figureBox,
                                              });
                                            }}
                                            onKeyDown={(e) => {
                                              if (!canCrop) return;
                                              if (e.key !== 'Enter' && e.key !== ' ') return;
                                              e.preventDefault();
                                              e.stopPropagation();
                                              void openFigureCropPreview({
                                                unitId: su.id,
                                                qaIndex: qi,
                                                questionPreview: qa.question,
                                                figureKind: qa.figureKind,
                                                figureNote: qa.figureNote,
                                                figureBox: qa.figureBox,
                                              });
                                            }}
                                          >
                                            <Image size={13} strokeWidth={2.25} aria-hidden />
                                            <span>{mark.label}</span>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    {qa.subQuestions && qa.subQuestions.length > 0 ? (
                                      <div className="compare-qa-sub-items">
                                        {qa.subQuestions.map((subQ, sqi) => (
                                          <div key={`${su.id}-qa-${qi}-subq-${sqi}`} className="compare-qa-sub-item">
                                            <span className="compare-qa-sub-label">({sqi + 1})</span>
                                            <MathTextBlock text={subQ} className="text-sm" />
                                          </div>
                                        ))}
                                      </div>
                                    ) : null}
                                    <div className="compare-qa-answer">
                                      <span className="compare-qa-label">A</span>
                                      <div className="compare-qa-main-answer">
                                        <MathTextBlock text={qa.answer} className="text-sm" />
                                      </div>
                                    </div>
                                    {qa.subAnswers && qa.subAnswers.length > 0 ? (
                                      <div className="compare-qa-sub-items compare-qa-sub-answers">
                                        {qa.subAnswers.map((subA, sai) => (
                                          <div key={`${su.id}-qa-${qi}-suba-${sai}`} className="compare-qa-sub-item">
                                            <span className="compare-qa-sub-label">({sai + 1})</span>
                                            <MathTextBlock text={subA} className="text-sm" />
                                          </div>
                                        ))}
                                      </div>
                                    ) : null}
                                    {(() => {
                                      const enrich = structuralUnitEnrichQaPreview(su, qi);
                                      const enrichBusy = enrichLoadingIds.has(su.id);
                                      if (!enrich) {
                                        if (su.enrichStatus === 'succeeded' || su.enrichStatus === 'failed') {
                                          return (
                                            <details
                                              className="compare-qa-enrich-details is-empty"
                                              onToggle={(e) => {
                                                if ((e.target as HTMLDetailsElement).open) {
                                                  void ensureStructuralUnitDetail(su.id);
                                                }
                                              }}
                                            >
                                              <summary>
                                                {enrichBusy
                                                  ? '内容富化、隐性能力和考点流 · 加载中…'
                                                  : '内容富化、隐性能力和考点流 · 展开加载详情'}
                                              </summary>
                                              <p>
                                                {enrichBusy
                                                  ? '正在拉取本页富化详情…'
                                                  : su.enrichResult
                                                    ? `本颗粒无富化行（索引 ${qi}）`
                                                    : '展开后加载完整富化详情'}
                                              </p>
                                            </details>
                                          );
                                        }
                                        return null;
                                      }
                                      const rfeTone =
                                        enrich.rfeStatus === 'bound'
                                          ? 'is-ok'
                                          : enrich.rfeStatus === 'pending'
                                            ? 'is-warn'
                                            : '';
                                      const papTone =
                                        enrich.papStatus === 'ready'
                                          ? 'is-ok'
                                          : enrich.papStatus === 'partial'
                                            ? 'is-warn'
                                            : '';
                                      return (
                                        <details className="compare-qa-enrich-details">
                                          <summary>
                                            <span className="compare-qa-enrich-summary-text">{enrich.summaryLabel}</span>
                                          </summary>
                                          <div className="compare-qa-enrich-body">
                                            {enrich.gradeBandLabel ? (
                                              <div className="compare-qa-enrich-row">
                                                <span className="compare-qa-enrich-k">学段</span>
                                                <span className="compare-qa-enrich-v">{enrich.gradeBandLabel}</span>
                                              </div>
                                            ) : null}
                                            <div className={`compare-qa-enrich-row ${rfeTone}`}>
                                              <span className="compare-qa-enrich-v">
                                                {enrich.rfeStatus === 'pending' && enrich.rfeReason === 'profiles_empty'
                                                  ? (
                                                    <>
                                                      {enrichSettingsHasToken === true
                                                        ? '缺 Runtime 画像（旧快照，可只刷新画像）'
                                                        : '缺 Runtime 画像（需配置 ADMIN token）'}
                                                      {enrichSettingsHasToken !== true ? (
                                                        <Link
                                                          to="/factory/settings?tab=enrich"
                                                          className="compare-inline-link"
                                                          onClick={(e) => e.stopPropagation()}
                                                        >
                                                          去设置
                                                        </Link>
                                                      ) : null}
                                                      <button
                                                        type="button"
                                                        className="compare-inline-link"
                                                        disabled={
                                                          reenricingUnitIds.has(su.id)
                                                          || reextractingUnitIds.has(su.id)
                                                          || reextractAllBusy
                                                          || ownerAccepted
                                                        }
                                                        onClick={(e) => {
                                                          e.preventDefault();
                                                          e.stopPropagation();
                                                          void onReenrichUnit(su.id);
                                                        }}
                                                      >
                                                        {reenricingUnitIds.has(su.id) ? '刷新中…' : '刷新画像'}
                                                      </button>
                                                    </>
                                                  )
                                                  : enrich.rfeReason === 'grade_band_unresolved'
                                                    ? (
                                                      <span className="compare-qa-enrich-reason">
                                                        学段未决（需自然年龄或内容学段 hint）
                                                      </span>
                                                    )
                                                  : (
                                                    <>
                                                      {enrich.spineTitle || enrich.moduleKey || enrich.rfeStatus}
                                                      {enrich.rfeReason ? (
                                                        <span className="compare-qa-enrich-reason">
                                                          {enrich.rfeReason}
                                                        </span>
                                                      ) : null}
                                                    </>
                                                  )}
                                              </span>
                                            </div>
                                            {(enrich.problemType
                                              || (enrich.papStatus !== 'ready' && enrich.papStatus !== 'partial' && enrich.papStatus)) ? (
                                              <div className={`compare-qa-enrich-row ${papTone}`}>
                                                <span className="compare-qa-enrich-v">
                                                  {enrich.problemType
                                                    || enrich.papStatus}
                                                </span>
                                              </div>
                                            ) : null}
                                            {enrich.papDetail.hasContent ? (
                                              <div className="compare-qa-pap-rich">
                                                {enrich.papDetail.flowSummary ? (
                                                  <div className="compare-qa-pap-block">
                                                    <h4>能力流概览</h4>
                                                    <p className="compare-qa-pap-flow">
                                                      <MathTextBlock text={enrich.papDetail.flowSummary} className="text-sm" />
                                                    </p>
                                                  </div>
                                                ) : null}
                                                {enrich.papDetail.implicitSteps.length > 0 ? (
                                                  <div className="compare-qa-pap-block">
                                                    <h4>隐性能力步骤</h4>
                                                    <ol className="compare-qa-pap-steps">
                                                      {enrich.papDetail.implicitSteps.map((step, index) => (
                                                        <li key={`${step.implicitAbility}-${index}`}>
                                                          <div className="compare-qa-pap-step-head">
                                                            <span className="compare-qa-pap-step-n">
                                                              {step.stepOrder ?? index + 1}
                                                            </span>
                                                            <span className="compare-qa-pap-step-title">
                                                              <MathTextBlock text={step.implicitAbility} inline />
                                                              {step.implicitAbilityIsNew ? (
                                                                <em className="compare-qa-pap-new">新</em>
                                                              ) : null}
                                                            </span>
                                                          </div>
                                                          {step.actionObject ? (
                                                            <p className="compare-qa-pap-step-obj">
                                                              作用对象：
                                                              <MathTextBlock text={step.actionObject} inline />
                                                            </p>
                                                          ) : null}
                                                          {step.reasoning ? (
                                                            <div className="compare-qa-pap-step-reason">
                                                              <MathTextBlock text={step.reasoning} className="text-sm" />
                                                            </div>
                                                          ) : null}
                                                        </li>
                                                      ))}
                                                    </ol>
                                                  </div>
                                                ) : null}
                                                {enrich.papDetail.matchedTags.length > 0 ? (
                                                  <div className="compare-qa-pap-block">
                                                    <h4>匹配能力标签</h4>
                                                    <ul className="compare-qa-enrich-tags">
                                                      {enrich.papDetail.matchedTags.map((tag, index) => (
                                                        <li key={`${tag.label}-${index}`} title={tag.reason || undefined}>
                                                          {tag.label}
                                                        </li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                ) : null}
                                                {enrich.papDetail.examPointSteps.length > 0 ? (
                                                  <div className="compare-qa-pap-block">
                                                    <h4>考点流</h4>
                                                    <ol className="compare-qa-pap-steps">
                                                      {enrich.papDetail.examPointSteps.map((step, index) => (
                                                        <li key={`${step.examPointLabel}-${index}`}>
                                                          <div className="compare-qa-pap-step-head">
                                                            <span className="compare-qa-pap-step-n is-muted">
                                                              {step.stepOrder ?? index + 1}
                                                            </span>
                                                            <span className="compare-qa-pap-step-title">
                                                              <MathTextBlock text={step.examPointLabel} inline />
                                                            </span>
                                                          </div>
                                                          {step.reasoning ? (
                                                            <div className="compare-qa-pap-step-reason">
                                                              <MathTextBlock text={step.reasoning} className="text-sm" />
                                                            </div>
                                                          ) : null}
                                                        </li>
                                                      ))}
                                                    </ol>
                                                  </div>
                                                ) : null}
                                              </div>
                                            ) : (
                                              <div className={`compare-qa-enrich-row ${papTone}`}>
                                                <span className="compare-qa-enrich-k" />
                                                <span className="compare-qa-enrich-v compare-qa-enrich-reason">
                                                  无隐性能力步骤 / 标签
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </details>
                                      );
                                    })()}
                                  </div>
                                </div>
                                    );
                                  })}
                                </div>
                              ) : null}
                            </div>
                          ) : ignored ? null : !(su.extractError || su.extractStatus === 'failed') ? (
                            <div className="compare-item-text">
                              {busy || su.extractStatus === 'running'
                                ? '本页提取中…'
                                : su.extractStatus === 'succeeded'
                                  ? '本页提取完成，但没有可用知识颗粒（0 QA）'
                                  : '等待提取；完成后知识颗粒会显示在此'}
                            </div>
                          ) : null}

                        </div>
                      );
                    }).concat(
                      extractVisibleCount < structureForUnit.length
                        ? [
                            <div
                              key="extract-load-more"
                              ref={extractLoadMoreRef}
                              className="compare-item"
                              role="button"
                              tabIndex={0}
                              style={{ cursor: 'pointer', opacity: 0.85 }}
                              onClick={() => loadMoreExtractPages()}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  loadMoreExtractPages();
                                }
                              }}
                            >
                              <div className="compare-item-text text-sm text-muted">
                                滚动或点击加载更多 · 已显示 {extractVisibleCount}/{structureForUnit.length} 页
                              </div>
                            </div>,
                          ]
                        : structureForUnit.length > EXTRACT_PAGE_SIZE
                          ? [
                              <div
                                key="extract-end"
                                className="compare-item"
                                style={{ cursor: 'default', opacity: 0.7 }}
                              >
                                <div className="compare-item-text text-sm text-muted">
                                  已全部加载 · {structureForUnit.length} 页
                                </div>
                              </div>,
                            ]
                          : [],
                    )
                  : null}

                {phase === 'ttq' ? (
                  ttqAdmission && ttqAdmission.warehouseTtqas?.length ? (
                    <div className="ttq-admission-list" style={{ padding: '0 0 8px', color: '#E8ECF1' }}>
                      <div className="ttq-admission-summary" aria-label="入围汇总">
                        <div className="ttq-admission-summary-kicker">本任务入围汇总</div>
                        <div className="ttq-admission-summary-row">
                          入围仓 {ttqAdmission.warehouseTtqaCount} 组
                          <span className="ttq-admission-summary-dot">·</span>
                          颗粒 {ttqAdmission.particleCount}
                          {ttqAdmission.extractParticleCount
                            ? `/${ttqAdmission.extractParticleCount}`
                            : ''}
                          {ttqAdmission.recycleParticleCount
                            ? ` · recycle ${ttqAdmission.recycleParticleCount}`
                            : ''}
                          {ttqAdmission.reuseSeatCount
                            ? ` · 复用 +${ttqAdmission.reuseSeatCount}`
                            : ''}
                          <span className="ttq-admission-summary-dot">·</span>
                          推荐 {ttqAdmission.recommendationCount}
                          {ttqAdmission.builtAt ? (
                            <>
                              <span className="ttq-admission-summary-dot">·</span>
                              {new Date(ttqAdmission.builtAt).toLocaleString()}
                            </>
                          ) : null}
                        </div>
                        <div className="ttq-admission-summary-product">
                          <span className="ttq-admission-summary-product-label">成品智能体</span>
                          <span className="text-muted">贡献入仓 / 人审见顶栏「成品智能体」</span>
                          <Link
                            to={`/factory/goods?batchId=${encodeURIComponent(batchId)}`}
                            className="ttq-admission-summary-link"
                          >
                            打开成品智能体贡献 →
                          </Link>
                        </div>
                        {(ttqMergeOrphansBusy || (ttqRerunBusy && ttqJobProgress)) ? (
                          <div className="ttq-admission-summary-banner is-running" role="status">
                            <Loader2 size={14} className="animate-spin shrink-0" aria-hidden />
                            <div className="ttq-admission-summary-banner-body">
                              <strong>
                                {ttqMergeOrphansBusy ? '正在相似再入围（融合孤立组）' : '正在重跑品题入围'}
                              </strong>
                              <span>
                                {ttqMergeOrphansBusy
                                  ? '仅对小组（≤5 题）逐个 LLM 判断能否并入更大组；高亮的是当前在判的卡片。'
                                  : ttqJobProgress || '排队 / 聚类 / 标题 / 相似再入围进行中…'}
                              </span>
                            </div>
                          </div>
                        ) : ttqManualMergeMode ? (
                          <div className="ttq-admission-summary-banner is-running is-manual-merge" role="status">
                            {ttqMergeQueueDepth > 0
                              ? <Loader2 size={14} className="animate-spin shrink-0" aria-hidden />
                              : <GripVertical size={14} className="shrink-0" aria-hidden />}
                            <div className="ttq-admission-summary-banner-body">
                              <strong>
                                {ttqMergeQueueDepth > 0
                                  ? `手动合并 · 后台同步 ${ttqMergeQueueDepth}`
                                  : '手动合并模式'}
                              </strong>
                              <span>
                                拖动可继续合并，操作会排队后台同步，不冻结界面。拖错了可点「撤销」。Esc 或「退出」结束；点卡片仍可查看题目。
                              </span>
                            </div>
                            <div className="ttq-admission-summary-banner-actions">
                              <button
                                type="button"
                                className="ttq-admission-summary-banner-undo"
                                disabled={!ttqManualMergeCanUndo}
                                title={ttqManualMergeCanUndo ? '撤销上一次拖拽合并' : '暂无可撤销的合并'}
                                onClick={() => void onUndoManualMerge()}
                              >
                                <Undo2 size={13} aria-hidden />
                                撤销上一步合并
                              </button>
                              <button
                                type="button"
                                className="ttq-admission-summary-banner-exit"
                                onClick={exitTtqManualMergeMode}
                              >
                                退出手动合并
                              </button>
                            </div>
                          </div>
                        ) : ttqReadmitReport ? (
                          <div
                            className={`ttq-admission-summary-banner ${ttqReadmitReport.merged > 0 ? 'is-ok' : 'is-muted'}`}
                            role="status"
                          >
                            <div className="ttq-admission-summary-banner-body">
                              <strong>
                                {ttqReadmitReport.merged > 0
                                  ? `相似再入围完成 · 合并 ${ttqReadmitReport.merged} 组`
                                  : '相似再入围完成 · 未合并'}
                              </strong>
                              <span>
                                {ttqReadmitReport.skippedReason
                                  ? (
                                    String(ttqReadmitReport.skippedReason).startsWith('no_small_groups')
                                      ? '本批没有足够小的候选组（默认 ≤5 题），所以没有进入 LLM 再判。'
                                      : `跳过原因：${ttqReadmitReport.skippedReason}`
                                  )
                                  : `审视 ${ttqReadmitReport.considered} 个小组候选`
                                    + (ttqReadmitReport.merged > 0
                                      ? `，成功并入 ${ttqReadmitReport.merged} 组。`
                                      : '，模型均未判定可合并。')}
                              </span>
                              {ttqReadmitReport.mergedPairs?.length ? (
                                <ul className="ttq-admission-summary-banner-list">
                                  {ttqReadmitReport.mergedPairs.slice(0, 4).map((p) => (
                                    <li key={`${p.fromId}-${p.intoId}`}>
                                      「{p.fromTitle}」→「{p.intoTitle}」
                                      {p.reason ? `（${p.reason}）` : ''}
                                    </li>
                                  ))}
                                </ul>
                              ) : null}
                              {!ttqReadmitReport.merged && ttqReadmitReport.sampleRejectReasons?.length ? (
                                <ul className="ttq-admission-summary-banner-list">
                                  {ttqReadmitReport.sampleRejectReasons.slice(0, 4).map((line) => (
                                    <li key={line}>{line}</li>
                                  ))}
                                </ul>
                              ) : null}
                              {ttqReadmitReport.finishedAt ? (
                                <span className="ttq-admission-summary-banner-time">
                                  {new Date(ttqReadmitReport.finishedAt).toLocaleString()}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>
                      <div className="ttq-admission-groups ttq-admission-card-grid">
                      {(() => {
                        const simMatch = /^ttq_similarity_readmit:(\d+)\/(\d+)(?::([^·]+))?/i
                          .exec(ttqJobProgressRaw);
                        const judgingShorts = String(simMatch?.[3] || '')
                          .split(',')
                          .map((s) => s.trim().toLowerCase())
                          .filter(Boolean);
                        const inSimilarityPhase = Boolean(
                          ttqMergeOrphansBusy
                          || (ttqRerunBusy && ttqJobProgressRaw.includes('similarity')),
                        );
                        const rows = [...(ttqAdmission.warehouseTtqas || [])].sort((a, b) => {
                          const an = a.particles?.length || 0;
                          const bn = b.particles?.length || 0;
                          const aHot = judgingShorts.some((s) => String(a.id).toLowerCase().startsWith(s)) ? 0 : 1;
                          const bHot = judgingShorts.some((s) => String(b.id).toLowerCase().startsWith(s)) ? 0 : 1;
                          if (aHot !== bHot) return aHot - bHot;
                          if (inSimilarityPhase) {
                            const aOrphan = an > 0 && an <= 5 ? 0 : 1;
                            const bOrphan = bn > 0 && bn <= 5 ? 0 : 1;
                            if (aOrphan !== bOrphan) return aOrphan - bOrphan;
                          }
                          return bn - an || String(a.id).localeCompare(String(b.id));
                        });
                        return rows.map((row: FactoryWarehouseTtqa) => {
                        const rec = ttqAdmission.recommendations.find(
                          (r) => r.targetTtqaId === row.id
                            || r.targetTtqaId === row.factoryMeta?.productId
                            || r.id === row.factoryMeta?.admissionRecommendationId,
                        );
                        const n = row.particles?.length || 0;
                        const rawTitle = String(row.title || '').trim();
                        const titleLooksLikeStem =
                          !rawTitle
                          || /^【\d+】/.test(rawTitle)
                          || (/=/.test(rawTitle) && /[÷×+\-−*/\d]/.test(rawTitle));
                        const displayTitle = titleLooksLikeStem
                          ? `品题组 · ${n || 1} 题`
                          : rawTitle;
                        const isOrphanCandidate = n > 0 && n <= 5;
                        const isJudgingThis = judgingShorts.some((s) => (
                          String(row.id).toLowerCase().startsWith(s)
                        ));
                        // Only the currently in-flight cards spin — never light the whole board.
                        const showSpin = isJudgingThis;
                        const isDropTarget = Boolean(ttqDragSourceId || ttqDragSourceIdRef.current)
                          && ttqDropTargetId === row.id
                          && (ttqDragSourceId || ttqDragSourceIdRef.current) !== row.id;
                        const isDragSource = (
                          ttqDragSourceId === row.id || ttqDragSourceIdRef.current === row.id
                        );
                        return (
                          <div
                            key={row.id}
                            role="button"
                            tabIndex={0}
                            // Always draggable when merge is allowed — first drag enters merge mode.
                            // Use <div> not <button>: Chromium DnD on button is flaky.
                            draggable={!ownerAccepted && !ttqRerunBusy && !ttqMergeOrphansBusy}
                            className={[
                              'ttq-admission-card ttq-admission-vcard',
                              isOrphanCandidate && inSimilarityPhase ? 'is-orphan' : '',
                              showSpin ? 'is-judging' : '',
                              n >= 65 ? 'is-capped' : '',
                              ttqManualMergeMode ? 'is-manual-merge' : '',
                              isDragSource ? 'is-drag-source' : '',
                              isDropTarget ? 'is-drop-target' : '',
                            ].filter(Boolean).join(' ')}
                            onMouseDown={() => {
                              ttqDidDragRef.current = false;
                            }}
                            onClick={() => {
                              if (ttqDidDragRef.current) {
                                ttqDidDragRef.current = false;
                                return;
                              }
                              void openTtqGroupDrawer(row.id);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                void openTtqGroupDrawer(row.id);
                              }
                            }}
                            onDragStart={(e) => {
                              if (ownerAccepted || ttqRerunBusy || ttqMergeOrphansBusy) {
                                e.preventDefault();
                                return;
                              }
                              ttqDidDragRef.current = true;
                              if (!ttqManualMergeMode) {
                                setTtqManualMergeMode(true);
                                showToast('已进入手动合并：拖到目标卡上合并；点 Banner「退出」或 Esc 结束。', 3200);
                              }
                              // text/plain is required — custom MIME types are often empty on drop.
                              e.dataTransfer.setData('text/plain', row.id);
                              e.dataTransfer.setData('text/ttq-source-id', row.id);
                              e.dataTransfer.effectAllowed = 'move';
                              ttqDragSourceIdRef.current = row.id;
                              setTtqDragSourceId(row.id);
                            }}
                            onDragEnd={() => {
                              // Drop runs before dragEnd; keep ref until next frame for safety.
                              window.requestAnimationFrame(() => {
                                ttqDragSourceIdRef.current = null;
                                setTtqDragSourceId(null);
                                setTtqDropTargetId(null);
                              });
                            }}
                            onDragOver={(e) => {
                              // Use ref — setState from dragStart may not have flushed yet,
                              // and without preventDefault the browser never fires drop.
                              const src = ttqDragSourceIdRef.current || ttqDragSourceId;
                              if (!src || src === row.id) return;
                              e.preventDefault();
                              e.stopPropagation();
                              e.dataTransfer.dropEffect = 'move';
                              if (ttqDropTargetId !== row.id) setTtqDropTargetId(row.id);
                            }}
                            onDragEnter={(e) => {
                              const src = ttqDragSourceIdRef.current || ttqDragSourceId;
                              if (!src || src === row.id) return;
                              e.preventDefault();
                              setTtqDropTargetId(row.id);
                            }}
                            onDragLeave={(e) => {
                              // Ignore leave into children.
                              const related = e.relatedTarget as Node | null;
                              if (related && e.currentTarget.contains(related)) return;
                              if (ttqDropTargetId === row.id) setTtqDropTargetId(null);
                            }}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const sourceId = (
                                e.dataTransfer.getData('text/plain')
                                || e.dataTransfer.getData('text/ttq-source-id')
                                || ttqDragSourceIdRef.current
                                || ttqDragSourceId
                                || ''
                              ).trim();
                              setTtqDropTargetId(null);
                              ttqDragSourceIdRef.current = null;
                              setTtqDragSourceId(null);
                              if (sourceId && sourceId !== row.id) {
                                if (!ttqManualMergeMode) setTtqManualMergeMode(true);
                                void onManualMergeDrop(sourceId, row.id);
                              }
                            }}
                          >
                            <span className="ttq-admission-vcard-badge">
                              {showSpin || (ttqMergeQueueDepth > 0 && isDropTarget) ? (
                                <Loader2 size={12} className="animate-spin" aria-hidden />
                              ) : ttqManualMergeMode ? (
                                <GripVertical size={12} aria-hidden />
                              ) : null}
                              {row.factoryMeta?.learningAdmitted ? '已滴灌' : '仓内'}
                              {isOrphanCandidate && inSimilarityPhase ? ' · 候选' : ''}
                              {isDropTarget ? ' · 放到这里合并' : ''}
                            </span>
                            <strong className="ttq-admission-vcard-title">
                              <MathTextBlock text={displayTitle} className="text-sm" />
                            </strong>
                            <span className="ttq-admission-vcard-meta">
                              {n} 题
                              {rec?.scores?.total != null
                                ? ` · 分 ${rec.scores.total.toFixed(2)}`
                                : ''}
                              {n >= 65 ? ' · 已达上限' : ''}
                            </span>
                            <span className="ttq-admission-vcard-foot">
                              {showSpin
                                ? '正在判断能否融合…'
                                : ttqManualMergeMode
                                  ? (isDragSource ? '拖到目标卡…' : '拖合并 · 点查看题目')
                                  : '点击查看 · 拖动可合并'}
                            </span>
                          </div>
                        );
                        });
                      })()}
                      </div>
                    </div>
                  ) : (
                    <p className="compare-empty" style={{ padding: 16, color: '#E8ECF1' }}>
                      {artifactPhaseLoading
                        ? '加载入围成果…'
                        : ttqMetaHint
                          ? `${ttqMetaHint}。明细正在从成品智能体仓同步…`
                          : '尚无入围成品智能体。完成颗粒提取（含 RFE/PAP）后，工序「品题推荐」会写入与学习侧同形的仓内 TTQA。'}
                      {!artifactPhaseLoading && ttqMetaHint ? (
                        <>
                          {' '}
                          <Link
                            to={`/factory/goods?batchId=${encodeURIComponent(batchId)}`}
                            className="ttq-admission-summary-link"
                          >
                            打开成品智能体 →
                          </Link>
                        </>
                      ) : null}
                    </p>
                  )
                ) : null}

                {phase === 'ipttq' ? (
                  ipttqBatch && ipttqBatch.records?.length ? (
                    <div className="ttq-admission-list" style={{ color: '#FFFFFF' }}>
                      {batch?.meta?.ipttqBankBackpressureExhausted === true ? (
                        <div
                          className="text-xs"
                          style={{
                            padding: '8px 12px',
                            color: '#fecaca',
                            borderBottom: '1px solid rgba(232,236,241,0.12)',
                          }}
                        >
                          诱导题库未达标（工序失败），以下为已落库的 skill / 诱导草稿，可继续补库或手动再试。
                        </div>
                      ) : null}
                      <div
                        className="text-xs"
                        style={{
                          padding: '8px 12px',
                          color: '#E8ECF1',
                          borderBottom: '1px solid rgba(232,236,241,0.12)',
                        }}
                      >
                        浓缩 {admissionScopedIpttqRecords.length || ipttqBatch.ipttqCount} 条
                        {(ttqAdmission?.warehouseTtqaCount || ipttqBatch.warehouseTtqaCount)
                          ? ` / ${ttqAdmission?.warehouseTtqaCount || ipttqBatch.warehouseTtqaCount} 组`
                          : ''}
                        {' · 诱导草稿 '}
                        {ipttqBatch.bankDraftCount}
                        {' · 本批最高目标 '}
                        {ipttqBatch.targetBank || 5} 题
                        {(ipttqBatch.warnings || []).some((w) => String(w).startsWith('partial:'))
                          || ipttqJobProgress
                          ? ` · ${ipttqJobProgress || '生成中…'}`
                          : ''}
                        {ipttqBatch.builtAt
                          ? ` · ${new Date(ipttqBatch.builtAt).toLocaleString()}`
                          : ''}
                      </div>
                      <div className="ipttq-card-grid">
                      {sortedIpttqRecords.map((row) => {
                        const bankN = row.effectiveBankCount
                          ?? row.bankParticleDrafts?.length
                          ?? 0;
                        const target = resolveIpttqRowTarget(row, ipttqBatch?.targetBank || 5);
                        const shortfall = Math.max(0, target - bankN);
                        const complete = shortfall === 0 && row.factoryMeta?.bankOk !== false;
                        const title = String(row.skill?.mainTitle || '').trim() || '品题浓缩结果';
                        const group = String(row.factoryMeta?.warehouseTtqaTitle || '').trim();
                        const productId = resolveIpttqProductId(row);
                        const pct = target > 0 ? Math.min(100, Math.round((bankN / target) * 100)) : 0;
                        const linkedWarehouse = (ttqAdmission?.warehouseTtqas || []).find((w) => (
                          w.id === row.ttqaId
                          || String(w.factoryMeta?.productId || '') === String(row.ttqaId || '')
                          || String(w.factoryMeta?.productId || '') === productId
                        ));
                        const sourceN = Number(row.factoryMeta?.nativeMemberCount)
                          || Number(row.factoryMeta?.totalMemberCount)
                          || linkedWarehouse?.particles?.length
                          || row.referenceParticleIds?.length
                          || 0;
                        return (
                          <button
                            key={row.id}
                            type="button"
                            className={`ipttq-card${complete ? ' is-complete' : ' is-shortfall'}`}
                            onClick={() => setIpttqDetail(row)}
                          >
                            <div className="ipttq-card-head">
                              <span className={`ipttq-card-badge${complete ? ' is-ok' : ''}`}>
                                {complete ? '已满' : `欠 ${shortfall}`}
                              </span>
                              <div className="ipttq-card-head-body">
                                <span className="ipttq-card-title">
                                  <MathTextBlock text={title} className="text-sm font-semibold" />
                                </span>
                                <span className={`ipttq-card-subtitle${group && group !== title ? '' : ' is-placeholder'}`}>
                                  {group && group !== title ? (
                                    <MathTextBlock text={group} className="text-xs" />
                                  ) : (
                                    '\u00a0'
                                  )}
                                </span>
                              </div>
                              <span className="ipttq-card-meta">
                                {sourceN > 0 ? `入围 ${sourceN} 题 · ` : ''}
                                诱导 {bankN}/{target}
                                {row.factoryMeta?.skillOk === false ? ' · skill 未齐' : ''}
                              </span>
                            </div>
                            <span className="ipttq-card-progress" aria-hidden>
                              <span
                                className="ipttq-card-progress-fill"
                                style={{ width: `${pct}%` }}
                              />
                            </span>
                            {row.skill?.thinkingLineage ? (
                              <span className="ipttq-card-lineage">
                                {truncateMathAwarePreview(row.skill.thinkingLineage, 64)}
                              </span>
                            ) : (
                              <span className="ipttq-card-lineage is-empty">暂无思考脉络</span>
                            )}
                            {productId ? (
                              <span
                                className="ipttq-card-foot"
                                role="presentation"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/factory/goods?productId=${encodeURIComponent(productId)}`);
                                }}
                              >
                                成品智能体 →
                              </span>
                            ) : null}
                          </button>
                        );
                      })}
                      </div>
                    </div>
                  ) : (
                    <p className="compare-empty" style={{ padding: 16, color: '#E8ECF1' }}>
                      {artifactPhaseLoading || ipttqJobProgress || ipttqRerunBusy
                        ? `品题浓缩进行中${ipttqJobProgress ? ` · ${ipttqJobProgress}` : ''}（每组 skill + 诱导题，轮询 AppFactory 进度）…`
                        : ipttqMetaHint
                          ? `${ipttqMetaHint}${batch?.meta?.ipttqBankBackpressureExhausted ? ' · 工序未达标，以下为已落库部分' : ''}。`
                          : '尚无品题浓缩。完成「品题推荐」后会自动生成；也可点 ⋮「重跑品题浓缩」。'}
                      {!artifactPhaseLoading && ipttqMetaHint ? (
                        <>
                          {' '}
                          <Link
                            to={`/factory/goods?batchId=${encodeURIComponent(batchId)}`}
                            className="ttq-admission-summary-link"
                          >
                            在成品智能体查看 skill / 诱导草稿 →
                          </Link>
                        </>
                      ) : null}
                    </p>
                  )
                ) : null}

                {phase === 'layout' && !visibleItems.length ? (
                  <p className="compare-empty" style={{ padding: 16 }}>
                    {layoutMissing || !layoutUnits.length
                      ? '本页段尚无版面条目（解析未完成或未抽出）'
                      : '暂无条目'}
                  </p>
                ) : null}
                {phase === 'structure' && !structureForUnit.length ? (
                  <p className="compare-empty" style={{ padding: 16 }}>
                    暂无提取前体。完成「结构聚合」或页图准备后，此处展示将交给提取前体接口形状。
                  </p>
                ) : null}
                {phase === 'extract' && !structureForUnit.length ? (
                  <p className="compare-empty" style={{ padding: 16 }}>
                    {pageDirect
                      ? '暂无按页单元，无法展示颗粒。请先完成页图准备。'
                      : '暂无结构单元，无法展示颗粒。请先完成结构聚合。'}
                  </p>
                ) : null}
                {phase === 'layout' && pageDirect && structuralUnits.length ? (
                  <div className="compare-item" style={{ cursor: 'default' }}>
                    <div className="compare-item-meta">
                      <span className="compare-type">单页直通</span>
                      <span>{structuralUnits.length} 页单元已就绪</span>
                    </div>
                    <div className="compare-item-text text-sm text-muted">
                      不做文档块切分。切换到「颗粒提取」可查看已完成页的知识颗粒。
                    </div>
                  </div>
                ) : null}
              </div>
            </section>
          </div>
        </>
      ) : null}

      {ttqGroupDrawerId ? (() => {
        const summaryRow = (ttqAdmission?.warehouseTtqas || []).find((w) => w.id === ttqGroupDrawerId);
        const row = ttqGroupDrawerRow || summaryRow || null;
        const n = row?.particles?.length || 0;
        const rawTitle = String(row?.title || summaryRow?.title || '').trim();
        const titleLooksLikeStem =
          !rawTitle
          || /^【\d+】/.test(rawTitle)
          || (/=/.test(rawTitle) && /[÷×+\-−*/\d]/.test(rawTitle));
        const displayTitle = titleLooksLikeStem ? `品题组 · ${n || 1} 题` : rawTitle;
        const particles = ttqGroupDrawerRow?.particles || [];
        const bright = { color: '#FFFFFF' as const };
        return (
          <div
            className="fixed inset-0 z-50 flex justify-end bg-black/55"
            role="presentation"
            onClick={() => closeTtqGroupDrawer()}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="入围组题目列表"
              className="ttq-admission-drawer"
              style={bright}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="ttq-admission-drawer-head">
                <div className="min-w-0 flex-1">
                  <p className="text-xs" style={{ color: '#8b94a3' }}>入围推荐组</p>
                  <h3 className="mt-1 text-base font-semibold" style={bright}>
                    <MathTextBlock text={displayTitle} className="text-base" />
                  </h3>
                  <p className="mt-1 text-xs" style={{ color: '#8b94a3' }}>
                    {n} 题 · 点条目可看题干与答案
                  </p>
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary"
                  style={{ color: '#FFFFFF' }}
                  onClick={() => closeTtqGroupDrawer()}
                >
                  关闭
                </button>
              </div>
              <div className="ttq-admission-drawer-body">
                {ttqGroupDrawerLoading ? (
                  <div className="flex items-center gap-2 px-4 py-8 text-sm" style={{ color: '#8b94a3' }}>
                    <Loader2 size={16} className="animate-spin" aria-hidden />
                    加载题目列表…
                  </div>
                ) : particles.length === 0 ? (
                  <p className="px-4 py-8 text-sm" style={{ color: '#8b94a3' }}>本组暂无题目明细</p>
                ) : (
                  particles.map((p) => {
                    const fromUnit = lookupExtractQaForParticle(p.particleId, structuralUnits);
                    const qa = resolveAdmissionParticleQa({
                      particleQuestions: p.particleQuestions,
                      particleAnswers: p.particleAnswers,
                      particleTitle: p.particleTitle,
                      subQuestions: p.subQuestions,
                      subAnswers: p.subAnswers,
                      extractQuestion: fromUnit?.question,
                      extractAnswer: fromUnit?.answer,
                      extractSubQuestions: fromUnit?.subQuestions,
                      extractSubAnswers: fromUnit?.subAnswers,
                      pap: p.pap,
                    });
                    const label = truncateMathAwarePreview(
                      admissionParticleListLabel(qa, true) || p.particleId || '',
                      140,
                    );
                    return (
                      <button
                        key={p.id || p.particleId}
                        type="button"
                        className="ttq-admission-drawer-item"
                        onClick={() => setTtqParticleDetail({
                          groupTitle: displayTitle,
                          particle: p,
                        })}
                      >
                        <MathTextBlock text={label} className="text-sm" />
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        );
      })() : null}

      {ttqParticleDetail ? (() => {
        const p = ttqParticleDetail.particle;
        const papDetail = buildPapDetail(
          (p.pap || null) as PrivateActivityPackage | null,
        );
        const fromUnit = lookupExtractQaForParticle(p.particleId, structuralUnits);
        const qa = resolveAdmissionParticleQa({
          particleQuestions: p.particleQuestions,
          particleAnswers: p.particleAnswers,
          particleTitle: p.particleTitle,
          subQuestions: p.subQuestions,
          subAnswers: p.subAnswers,
          extractQuestion: fromUnit?.question,
          extractAnswer: fromUnit?.answer,
          extractSubQuestions: fromUnit?.subQuestions,
          extractSubAnswers: fromUnit?.subAnswers,
          pap: p.pap,
        });
        const question = qa.question;
        const answer = qa.answer;
        const subQuestions = qa.subQuestions;
        const subAnswers = qa.subAnswers;
        const bright = { color: '#FFFFFF' as const };
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
            role="presentation"
            onClick={() => setTtqParticleDetail(null)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="知识颗粒详情"
              className="ttq-particle-dialog max-h-[85dvh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-surface p-5 shadow-xl"
              style={bright}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs" style={{ color: '#E8ECF1' }}>{ttqParticleDetail.groupTitle}</p>
                  <h3 className="mt-1 text-base font-semibold" style={bright}>知识颗粒</h3>
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary"
                  style={{ color: '#FFFFFF' }}
                  onClick={() => setTtqParticleDetail(null)}
                >
                  关闭
                </button>
              </div>

              <section className="mt-4">
                <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>
                  题干
                  {fromUnit ? (
                    <span className="ml-2 font-normal opacity-70">引用颗粒提取</span>
                  ) : null}
                </h4>
                <div className="mt-2 text-sm" style={bright}>
                  {question
                    ? <MathTextBlock text={question} className="text-sm" />
                    : <span style={{ color: '#E8ECF1' }}>暂无题干</span>}
                </div>
                {subQuestions.length > 0 ? (
                  <div className="mt-3 rounded-lg border border-border px-3 py-2">
                    <div className="text-sm font-medium" style={bright}>
                      子问题（{subQuestions.length}）
                    </div>
                    <div className="mt-2 space-y-2">
                      {subQuestions.map((subQ, sqi) => (
                        <div key={`subq-${sqi}`} className="flex gap-2 text-sm" style={bright}>
                          <span className="shrink-0 tabular-nums opacity-80">({sqi + 1})</span>
                          <MathTextBlock text={subQ} className="text-sm" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>

              <section className="mt-4 border-t border-border pt-4">
                <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>答案</h4>
                <div className="mt-2 text-sm" style={bright}>
                  {answer
                    ? <MathTextBlock text={answer} className="text-sm" />
                    : <span style={{ color: '#E8ECF1' }}>暂无答案</span>}
                </div>
                {subAnswers.length > 0 ? (
                  <div className="mt-3 rounded-lg border border-border px-3 py-2">
                    <div className="text-sm font-medium" style={bright}>
                      子答案（{subAnswers.length}）
                    </div>
                    <div className="mt-2 space-y-2">
                      {subAnswers.map((subA, sai) => (
                        <div key={`suba-${sai}`} className="flex gap-2 text-sm" style={bright}>
                          <span className="shrink-0 tabular-nums opacity-80">({sai + 1})</span>
                          <MathTextBlock text={subA} className="text-sm" />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </section>

              <section className="mt-4 border-t border-border pt-4">
                <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>隐性能力</h4>
                {papDetail.implicitSteps.length > 0 ? (
                  <ol className="mt-2 space-y-3 text-sm" style={bright}>
                    {papDetail.implicitSteps.map((step, index) => (
                      <li
                        key={`${step.implicitAbility}-${index}`}
                        className="border-b border-border/60 pb-3 last:border-0"
                      >
                        <div className="font-medium" style={bright}>
                          {step.stepOrder ?? index + 1}.{' '}
                          <MathTextBlock text={step.implicitAbility} inline />
                          {step.implicitAbilityIsNew ? '（新）' : ''}
                        </div>
                        {step.actionObject ? (
                          <p className="mt-1 text-sm" style={{ color: '#E8ECF1' }}>
                            作用对象：
                            <MathTextBlock text={step.actionObject} inline />
                          </p>
                        ) : null}
                        {step.reasoning ? (
                          <div className="mt-1 text-sm leading-relaxed" style={{ color: '#E8ECF1' }}>
                            <MathTextBlock text={step.reasoning} className="text-sm" />
                          </div>
                        ) : null}
                      </li>
                    ))}
                  </ol>
                ) : null}
                {papDetail.matchedTags.length > 0 ? (
                  <ul className="mt-3 space-y-2 text-sm" style={bright}>
                    {papDetail.matchedTags.map((tag, index) => (
                      <li
                        key={`${tag.label}-${index}`}
                        className="border-b border-border/60 pb-2 last:border-0"
                      >
                        <div className="font-medium" style={bright}>{tag.label}</div>
                        {tag.category ? (
                          <p className="mt-0.5 text-xs" style={{ color: '#E8ECF1' }}>
                            分类：{tag.category}
                          </p>
                        ) : null}
                        {tag.reason ? (
                          <p className="mt-1 text-sm leading-relaxed" style={{ color: '#E8ECF1' }}>
                            {tag.reason}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {!papDetail.implicitSteps.length && !papDetail.matchedTags.length ? (
                  <p className="mt-2 text-sm" style={{ color: '#E8ECF1' }}>暂无隐性能力</p>
                ) : null}
              </section>
            </div>
          </div>
        );
      })() : null}

      {ipttqDetail ? (() => {
        const row = ipttqDetail;
        const productId = resolveIpttqProductId(row);
        const bright = { color: '#FFFFFF' as const };
        const bank = row.bankParticleDrafts || [];
        const bankSorted = sortBankDraftsByCreatedAt(bank);
        const sessionBankDrafts = bankDraftsThisSession(bankSorted, ipttqBankGen?.startedAt);
        const caps = row.skill?.implicitCapabilities || [];
        const memberRatio = readInductionBankMemberRatio(ipttqProductMeta);
        const ratioLabel = formatInductionRatioLabel(memberRatio);
        const memberRefLookup = buildMemberReferenceLookup(row, ttqAdmission?.warehouseTtqas);
        const { poolSize, referenceSampleSize } = readInductionPoolMeta(
          ipttqProductMeta,
          row.factoryMeta?.nativeMemberCount,
          memberRatio,
        );
        const closeIpttqDetail = () => {
          setIpttqDetail(null);
        };
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-4"
            onClick={closeIpttqDetail}
            onKeyDown={(e) => {
              if (e.key === 'Escape') closeIpttqDetail();
            }}
            role="presentation"
          >
            <div
              ref={ipttqDialogRef}
              className="ttq-particle-dialog max-h-[85dvh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-surface p-5 shadow-xl"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs" style={{ color: '#E8ECF1' }}>
                    {row.factoryMeta?.warehouseTtqaTitle || '仓内品题组'}
                  </p>
                  <h3 className="mt-1 text-base font-semibold" style={bright}>
                    <MathTextBlock text={row.skill?.mainTitle || '品题浓缩结果'} className="text-base" />
                  </h3>
                </div>
                <button
                  type="button"
                  className="shrink-0 rounded-lg border border-border px-2.5 py-1 text-xs hover:border-primary"
                  style={{ color: '#FFFFFF' }}
                  onClick={closeIpttqDetail}
                >
                  关闭
                </button>
              </div>

              {row.skill?.subtitle ? (
                <p className="mt-2 text-sm" style={{ color: '#E8ECF1' }}>{row.skill.subtitle}</p>
              ) : null}

              <section className="mt-4">
                <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>思考脉络</h4>
                <div className="mt-2 text-sm" style={bright}>
                  {row.skill?.thinkingLineage
                    ? <MathTextBlock text={row.skill.thinkingLineage} className="text-sm" />
                    : <span style={{ color: '#E8ECF1' }}>暂无</span>}
                </div>
              </section>

              <section className="mt-4 border-t border-border pt-4">
                <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>隐性能力</h4>
                {caps.length ? (
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-sm" style={bright}>
                    {caps.map((c) => <li key={c}>{c}</li>)}
                  </ul>
                ) : (
                  <p className="mt-2 text-sm" style={{ color: '#E8ECF1' }}>暂无</p>
                )}
              </section>

              <section className="mt-4 border-t border-border pt-4">
                <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>
                  诱导题库草稿（{bank.length}/{resolveIpttqRowTarget(row)}）
                </h4>
                {row.factoryMeta?.nativeMemberCount != null ? (
                  <p className="mt-1 text-xs" style={{ color: '#8b94a3' }}>
                    原生入围 {row.factoryMeta.nativeMemberCount} 题
                    {row.factoryMeta.totalMemberCount != null
                      && row.factoryMeta.totalMemberCount > row.factoryMeta.nativeMemberCount
                      ? ` · 成品总成员 ${row.factoryMeta.totalMemberCount}（复用位不计入目标）`
                      : ''}
                    {' · 目标 '}{ratioLabel}
                    {' · 压缩率 1/'}{memberRatio}
                  </p>
                ) : null}
                <p className="mt-1 text-xs" style={{ color: '#E8ECF1' }}>
                  仓内预备 · 未滴灌 · 非学习侧正式 KP
                </p>
                {poolSize > 0 ? (
                  <p className="mt-2 text-xs" style={{ color: '#8b94a3' }}>
                    抽样诱导 · 入围池 {poolSize} 题
                    {referenceSampleSize > 0
                      ? ` · 每道诱导题随机参考 ${referenceSampleSize} 道原题（轻量综合，含 PAP 摘要）`
                      : ''}
                  </p>
                ) : null}
                {bankSorted.length ? (
                  <div className="mt-3 space-y-4">
                    {bankSorted.map((d, i) => {
                      const arrivalAt = formatLocalTimeFromIso(d.metadata?.createdAt);
                      const generateMs = readBankDraftGenerateMs(d, ipttqBankGen?.startedAt);
                      const referenceLabels = formatDraftReferenceLabels(d, memberRefLookup);
                      return (
                      <div
                        key={d.id}
                        className="rounded-lg border border-border px-3 py-2"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="text-sm font-medium min-w-0" style={bright}>
                            <span className="mr-1 tabular-nums">({i + 1})</span>
                            <MathTextBlock
                              inline
                              text={d.title || d.metadata?.displayTitle || '诱导题'}
                              className="text-sm font-medium"
                            />
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
                            {arrivalAt ? (
                              <span className="tabular-nums text-[11px]" style={{ color: '#8b94a3' }}>
                                {arrivalAt}
                                {generateMs != null ? ` · FC ${formatElapsedMs(generateMs)}` : ''}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        {referenceLabels ? (
                          <p className="mt-1 text-[11px] leading-relaxed" style={{ color: '#8b94a3' }}>
                            参考原题：{referenceLabels}
                          </p>
                        ) : null}
                        <div className="mt-2 text-sm" style={bright}>
                          <MathTextBlock text={d.questions?.main || ''} className="text-sm" />
                        </div>
                        {d.answers?.main ? (
                          <div className="mt-2 text-sm" style={{ color: '#E8ECF1' }}>
                            答：<MathTextBlock text={d.answers.main} className="text-sm" />
                          </div>
                        ) : null}
                      </div>
                    );
                    })}
                  </div>
                ) : (
                  <p className="mt-2 text-sm" style={{ color: '#E8ECF1' }}>尚无诱导题草稿</p>
                )}
              </section>

              {row.factoryMeta?.warnings?.length ? (
                <section className="mt-4 border-t border-border pt-4">
                  <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>警告</h4>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs" style={{ color: '#E8ECF1' }}>
                    {row.factoryMeta.warnings.map((w) => <li key={w}>{w}</li>)}
                  </ul>
                </section>
              ) : null}

              {productId ? (
                <div className="mt-4 border-t border-border pt-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h4 className="text-xs font-medium tracking-wide" style={{ color: '#E8ECF1' }}>
                      就地生成诱导题库
                    </h4>
                    {ipttqBankGen?.status === 'running' ? (
                      <span className="inline-flex items-center gap-1 text-xs" style={{ color: '#2ec4b6' }}>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                        {bankStockPhaseLabel(ipttqBankGen.phase)}
                      </span>
                    ) : null}
                  </div>
                  <p className="mt-1 text-xs" style={{ color: '#8b94a3' }}>
                    随机抽取少量原题 + PAP 摘要综合出题；在此组上下文直接补库，便于逐组验题。
                  </p>

                  {ipttqBankGen && ipttqBankGen.status !== 'idle' ? (
                    <div
                      className="mt-3 rounded-lg border border-border px-3 py-2.5 text-xs"
                      style={{ color: '#E8ECF1' }}
                    >
                      <div className="flex flex-wrap gap-x-3 gap-y-1">
                        <span>
                          诱导已回 {ipttqBankGen.bankDone}/{ipttqBankGen.bankTarget || '—'}
                        </span>
                        {ipttqBankGen.inFlight > 0 ? (
                          <span>FC 在途 {ipttqBankGen.inFlight}</span>
                        ) : null}
                        <span>用时 {formatElapsedMs(ipttqBankGen.elapsedMs)}</span>
                        {ipttqBankGen.usageTotal > 0 ? (
                          <span>Token {formatTokenCompact(ipttqBankGen.usageTotal)}</span>
                        ) : null}
                        {ipttqBankGen.timeouts > 0 ? (
                          <span style={{ color: '#fecaca' }}>超时 {ipttqBankGen.timeouts}</span>
                        ) : null}
                        {ipttqBankGen.skips > 0 ? (
                          <span style={{ color: '#fcd34d' }}>跳过 {ipttqBankGen.skips}</span>
                        ) : null}
                      </div>
                      {ipttqBankGen.bankTarget > 0 ? (
                        <div
                          className="mt-2 h-1.5 overflow-hidden rounded-full"
                          style={{ background: 'rgba(232,236,241,0.12)' }}
                          aria-hidden
                        >
                          <div
                            className="h-full rounded-full transition-all duration-300"
                            style={{
                              width: `${Math.min(100, Math.round((ipttqBankGen.bankDone / ipttqBankGen.bankTarget) * 100))}%`,
                              background: ipttqBankGen.status === 'running'
                                ? 'linear-gradient(90deg, rgba(251,191,36,0.9), rgba(251,191,36,0.5))'
                                : 'linear-gradient(90deg, rgba(46,196,182,0.85), rgba(46,196,182,0.55))',
                            }}
                          />
                        </div>
                      ) : null}
                      {sessionBankDrafts.length > 0
                      && (ipttqBankGen.status === 'running' || ipttqBankGen.status === 'paused' || ipttqBankGen.status === 'done') ? (
                        <div className="mt-2 space-y-1 border-t border-border/60 pt-2">
                          <p style={{ color: '#8b94a3' }}>本轮回题</p>
                          <ul className="space-y-0.5">
                            {sessionBankDrafts.map((d) => (
                              <li
                                key={`arrival-${d.id}`}
                                className="tabular-nums"
                                style={{ color: '#cbd5e1' }}
                                title={formatBankDraftArrivalLine(d, memberRefLookup, ipttqBankGen.startedAt)}
                              >
                                {formatBankDraftArrivalLine(d, memberRefLookup, ipttqBankGen.startedAt)}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : null}
                      {ipttqBankGen.status === 'running' && ipttqBankGen.inFlight > 0 && ipttqBankGen.elapsedMs >= 180_000 ? (
                        <p className="mt-2" style={{ color: '#8b94a3' }}>
                          原题抽样 + PAP + 模型推理进行中；在途下降即表示陆续回写。
                        </p>
                      ) : null}
                      {ipttqBankGen.status === 'done' ? (
                        <p className="mt-2" style={{ color: '#2ec4b6' }}>
                          完成 · 本轮新增 {ipttqBankGen.added} 题
                        </p>
                      ) : null}
                      {ipttqBankGen.status === 'paused' ? (
                        <p className="mt-2" style={{ color: '#fcd34d' }}>
                          已暂停 · 已保存 {ipttqBankGen.bankDone} 题，可继续补库
                        </p>
                      ) : null}
                      {ipttqBankGen.status === 'failed' || ipttqBankGen.status === 'locked' ? (
                        <p className="mt-2" style={{ color: '#fecaca' }}>
                          {ipttqBankGen.error || (ipttqBankGen.status === 'locked' ? '补库被占用' : '生成失败')}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-xs font-medium hover:bg-primary/15 disabled:opacity-50"
                      style={{ color: '#2ec4b6' }}
                      disabled={ipttqBankGenBusy || ipttqBankGen?.status === 'running'}
                      onClick={() => void startIpttqBankGen(productId)}
                    >
                      {ipttqBankGenBusy || ipttqBankGen?.status === 'running' ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                      ) : (
                        <Play className="h-3.5 w-3.5" aria-hidden />
                      )}
                      {ipttqBankGen?.status === 'running'
                        ? '生成中…'
                        : bank.length > 0
                          ? '继续补库'
                          : '生成诱导题库'}
                    </button>
                    {ipttqBankGen?.status === 'running' ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-medium hover:bg-amber-500/15 disabled:opacity-50"
                        style={{ color: '#fcd34d' }}
                        disabled={ipttqBankPauseBusy}
                        onClick={() => void pauseIpttqBankGen(productId)}
                      >
                        {ipttqBankPauseBusy ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                        ) : (
                          <Pause className="h-3.5 w-3.5" aria-hidden />
                        )}
                        {ipttqBankPauseBusy ? '暂停中…' : '暂停'}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="text-xs hover:underline"
                      style={{ color: '#8b94a3' }}
                      onClick={() => {
                        navigate(`/factory/goods?productId=${encodeURIComponent(productId)}`);
                      }}
                    >
                      成品智能体详情 →
                    </button>
                  </div>
                </div>
              ) : (
                <div className="mt-4 border-t border-border pt-4">
                  <p className="text-xs" style={{ color: '#fecaca' }}>
                    未关联成品智能体，无法就地生成诱导题库。
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      })() : null}
      {figureCropPreview && batchId ? (
        <FigureCropPreviewDialog
          batchId={batchId}
          preview={figureCropPreview}
          onClose={() => setFigureCropPreview(null)}
          navIndex={figureCropNavIndex >= 0 ? figureCropNavIndex : undefined}
          navTotal={figureBearingPreviews.length > 0 ? figureBearingPreviews.length : undefined}
          onPrevFigure={
            figureCropNavIndex > 0
              ? () => {
                  void openFigureCropPreview(figureBearingPreviews[figureCropNavIndex - 1]!);
                }
              : undefined
          }
          onNextFigure={
            figureCropNavIndex >= 0 && figureCropNavIndex < figureBearingPreviews.length - 1
              ? () => {
                  void openFigureCropPreview(figureBearingPreviews[figureCropNavIndex + 1]!);
                }
              : undefined
          }
        />
      ) : null}
    </div>
  );
}
